import type { MediaAccessDecision } from "@topzle/api-client";
import type { BattleshipPublicState } from "@topzle/game-battleship";
import type { ConnectFourState } from "@topzle/game-connect-four";
import {
  analyzeLudoMove,
  chooseLudoBotAction,
  getLudoBotThrowProfile,
  LUDO_DICE_PRESENTATION_MS,
  LUDO_FAST_DICE_PRESENTATION_MS,
  type LudoColor,
  type LudoEvent,
  type LudoMoveBlockReason,
  type LudoState,
  ludoBoardPath,
  ludoColors,
  ludoHomeLanes,
  ludoSafeSquares,
  ludoStarts,
  ludoTokenCoordinate,
} from "@topzle/game-ludo";
import type { MafiaPublicState } from "@topzle/game-mafia";
import { gameCatalog } from "@topzle/game-registry";
import type { RockPaperScissorsPublicState } from "@topzle/game-rock-paper-scissors";
import type { SnakesLaddersState } from "@topzle/game-snakes-ladders";
import { formatLocalTime } from "@topzle/locale";
import {
  CloudflareRoomCall,
  type PeerMediaSignal,
  PeerToPeerRoomCall,
  RoomConnection,
  type RoomConnectionState,
} from "@topzle/realtime";
import type { ActiveRoomSnapshot, GameResult, RoomGameResults, RoomMessage } from "@topzle/types";
import { Chess, type Square } from "chess.js";
import {
  ArrowLeft,
  BookOpen,
  Bot,
  BrainCircuit,
  Bug,
  Camera,
  CameraOff,
  Check,
  CheckCheck,
  Clock3,
  Copy,
  Eye,
  EyeOff,
  Gamepad2,
  LoaderCircle,
  Mail,
  Maximize2,
  MessageCircle,
  MessageSquareText,
  Mic,
  MicOff,
  Minimize2,
  MoonStar,
  MoreHorizontal,
  PhoneOff,
  RotateCcw,
  Scale,
  Send,
  Settings2,
  Share2,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Swords,
  Target,
  Trophy,
  UserMinus,
  UserPlus,
  Users,
  Volume2,
  VolumeX,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { BattleshipBoard } from "../components/BattleshipBoard";
import { BrandMark } from "../components/BrandMark";
import { CallAccessDialog } from "../components/CallAccessDialog";
import { ConnectFourBoard } from "../components/ConnectFourBoard";
import { DrawGuessBoard, type DrawGuessPublicState } from "../components/DrawGuessBoard";
import { MafiaBoard } from "../components/MafiaBoard";
import { RockPaperScissorsBoard } from "../components/RockPaperScissorsBoard";
import { SnakesLaddersBoard } from "../components/SnakesLaddersBoard";
import { SupportDialog } from "../components/SupportDialog";
import type { SupportRequestKind } from "../components/SupportRequestForm";
import {
  calculateLudoThrowPhysics,
  clampLudoPhysics,
  constrainLudoThrowToBoard,
  defaultLudoThrowPhysics,
  hasLudoDiceTouchedEdge,
  type LudoThrowPhysics,
  ludoDiceRollDurationMs,
} from "../ludoDicePhysics";
import { SupabasePeerSignalBus } from "../realtime/SupabasePeerSignalBus";
import {
  type PlayableRoomGameId,
  playableRoomGameIdSet,
  roomGameCatalog,
} from "../roomGameCatalog";
import { roomTurnParticipantId, shouldNotifyRoomParticipant } from "../roomNotifications";
import { useAppStore } from "../state";
import { useAuthSession } from "../useAuthSession";

function flagForCountry(countryCode?: string): string | null {
  if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) return null;
  return String.fromCodePoint(...[...countryCode].map((letter) => 127397 + letter.charCodeAt(0)));
}

interface ChatItem {
  id: string;
  senderId?: string;
  text: string;
  at: number;
  kind?: "message" | "result";
  result?: GameResult;
  roomResultSummary?: string;
  delivery?: "sending" | "sent";
  receipts?: Record<string, "delivered" | "read">;
}
interface PresenceToast {
  id: string;
  participantId: string;
  displayName: string;
  state: "inactive" | "removed";
}
interface TicTacToeRoomState {
  board: Array<"X" | "O" | null>;
  players: { X: string; O: string };
  turn: "X" | "O";
  status: string;
  winner?: "X" | "O" | null;
  winningLine?: number[] | null;
  finishReason?: string | null;
  clocks?: { X: number; O: number } | null;
  initialClockSeconds?: number | null;
  lastMoveAt?: number;
}
interface ChessRoomState {
  fen: string;
  players: { white: string; black: string };
  status: string;
  winner: "white" | "black" | null;
  reason: string | null;
  clocks: { white: number; black: number } | null;
  initialClockSeconds?: number | null;
  lastMoveAt: number;
  history: Array<{ san: string; from: string; to: string; promotion?: string }>;
}
type LudoRoomState = LudoState;

type TimeControl = { seconds: number | null; label: string };
type BotPolicyOption = {
  id: string;
  label: string;
  description: string;
  difficulty: "easy" | "medium" | "hard";
  icon: typeof Bot;
};
type SavedGameSetup = {
  botPolicy?: string;
  clockSeconds?: number | null;
  rounds?: number;
};

const gameStyleLoaders: Partial<Record<PlayableRoomGameId, () => Promise<unknown>>> = {
  "tic-tac-toe": () => import("../styles/games/tic-tac-toe.css"),
  chess: () => import("../styles/games/chess.css"),
  ludo: () => import("../styles/games/ludo.css"),
  "connect-four": () => import("../styles/games/connect-four.css"),
  battleship: () => import("../styles/games/battleship.css"),
  "draw-guess": () => import("../styles/games/draw-guess.css"),
  "rock-paper-scissors": () => import("../styles/games/rock-paper-scissors.css"),
  "snakes-ladders": () => import("../styles/games/snakes-ladders.css"),
  mafia: () => import("../styles/games/mafia.css"),
};
const loadedGameStyles = new Set<string>();
const pendingGameStyles = new Map<string, Promise<unknown>>();
const aiOpponentGameIds = new Set<string>([
  "tic-tac-toe",
  "chess",
  "ludo",
  "connect-four",
  "battleship",
  "draw-guess",
  "rock-paper-scissors",
  "snakes-ladders",
]);
const teenQuickChat = [
  "Hi!",
  "Ready?",
  "Good game!",
  "Nice move!",
  "Your turn",
  "One more round?",
  "Well played!",
  "Thanks for playing!",
  "Oops!",
  "Close one!",
] as const;
const floatingChatDurationMs = 5_000;
const showFloatingChatStorageKey = "topzle.chat.show-new-messages";
const roomAudioNotificationsStorageKey = "topzle.room.audio-notifications";
const roomSetupDefaultsStorageKey = "topzle.game-setup-defaults:v1";

const botPolicies: Record<string, BotPolicyOption[]> = {
  "tic-tac-toe": [
    {
      id: "easy",
      label: "Easy",
      description: "Predictable practice",
      difficulty: "easy",
      icon: Sparkles,
    },
    {
      id: "medium",
      label: "Tactical",
      description: "Wins and blocks threats",
      difficulty: "medium",
      icon: Target,
    },
    {
      id: "hard",
      label: "Perfect",
      description: "Full minimax search",
      difficulty: "hard",
      icon: BrainCircuit,
    },
    {
      id: "random",
      label: "Random",
      description: "Any legal square",
      difficulty: "easy",
      icon: Bot,
    },
  ],
  chess: [
    {
      id: "aggressive",
      label: "Aggressive",
      description: "Checks, captures and pressure",
      difficulty: "hard",
      icon: Swords,
    },
    {
      id: "defensive",
      label: "Defensive",
      description: "King safety and restriction",
      difficulty: "hard",
      icon: Shield,
    },
    {
      id: "strategic",
      label: "Strategic",
      description: "Development and centre control",
      difficulty: "hard",
      icon: BrainCircuit,
    },
    {
      id: "random",
      label: "Random",
      description: "A legal move without evaluation",
      difficulty: "easy",
      icon: Bot,
    },
  ],
  ludo: [
    {
      id: "balanced",
      label: "Balanced",
      description: "Progress, safety and captures",
      difficulty: "hard",
      icon: Scale,
    },
    {
      id: "aggressive",
      label: "Aggressive",
      description: "Chases captures and pressure",
      difficulty: "hard",
      icon: Swords,
    },
    {
      id: "defensive",
      label: "Defensive",
      description: "Safe squares and blockades",
      difficulty: "hard",
      icon: Shield,
    },
    {
      id: "random",
      label: "Random",
      description: "Any eligible goti",
      difficulty: "easy",
      icon: Bot,
    },
  ],
  "connect-four": [
    {
      id: "easy",
      label: "Easy",
      description: "Relaxed legal drops",
      difficulty: "easy",
      icon: Sparkles,
    },
    {
      id: "medium",
      label: "Tactical",
      description: "Blocks and builds threats",
      difficulty: "medium",
      icon: Target,
    },
    {
      id: "hard",
      label: "Strategic",
      description: "Deep search and fork play",
      difficulty: "hard",
      icon: BrainCircuit,
    },
    {
      id: "random",
      label: "Random",
      description: "Any open column",
      difficulty: "easy",
      icon: Bot,
    },
  ],
  battleship: [
    {
      id: "probability",
      label: "Probability",
      description: "Fleet-density targeting",
      difficulty: "hard",
      icon: BrainCircuit,
    },
    {
      id: "hunt",
      label: "Hunt",
      description: "Finishes unresolved contacts",
      difficulty: "medium",
      icon: Target,
    },
    {
      id: "probability-defensive",
      label: "Defensive fleet",
      description: "Tactical placement and probability fire",
      difficulty: "hard",
      icon: Shield,
    },
    {
      id: "random",
      label: "Recon",
      description: "Tests untried water",
      difficulty: "easy",
      icon: Bot,
    },
  ],
  "draw-guess": [
    {
      id: "easy",
      label: "Relaxed",
      description: "Slower local guesses",
      difficulty: "easy",
      icon: Sparkles,
    },
    {
      id: "medium",
      label: "Balanced",
      description: "Normal local classifier pace",
      difficulty: "medium",
      icon: Scale,
    },
    {
      id: "hard",
      label: "Rapid",
      description: "Frequent local guesses",
      difficulty: "hard",
      icon: BrainCircuit,
    },
  ],
  "rock-paper-scissors": [
    {
      id: "uniform",
      label: "Uniform",
      description: "Mixes all gestures equally",
      difficulty: "easy",
      icon: Scale,
    },
    {
      id: "transition",
      label: "Transition",
      description: "Answers your latest pattern",
      difficulty: "medium",
      icon: Target,
    },
    {
      id: "frequency",
      label: "Frequency",
      description: "Counters your most common gesture",
      difficulty: "hard",
      icon: BrainCircuit,
    },
  ],
  "snakes-ladders": [
    {
      id: "fair",
      label: "Fair roller",
      description: "Standard six-sided die",
      difficulty: "medium",
      icon: Bot,
    },
  ],
};

function defaultBotPolicy(gameId: string): BotPolicyOption {
  return (
    botPolicies[gameId]?.[0] ?? {
      id: "medium",
      label: "Balanced",
      description: "Balanced play",
      difficulty: "medium",
      icon: Bot,
    }
  );
}

function readGameSetupDefaults(): Record<string, SavedGameSetup> {
  try {
    return JSON.parse(window.localStorage.getItem(roomSetupDefaultsStorageKey) ?? "{}") as Record<
      string,
      SavedGameSetup
    >;
  } catch {
    return {};
  }
}

async function loadGameStyles(gameId: string): Promise<void> {
  if (loadedGameStyles.has(gameId)) return;
  const loader = gameStyleLoaders[gameId as PlayableRoomGameId];
  if (!loader) return;
  let pending = pendingGameStyles.get(gameId);
  if (!pending) {
    pending = loader();
    pendingGameStyles.set(gameId, pending);
  }
  try {
    await pending;
    loadedGameStyles.add(gameId);
  } finally {
    pendingGameStyles.delete(gameId);
  }
}

const gameTimeControls: Partial<Record<PlayableRoomGameId, TimeControl[]>> = {
  "tic-tac-toe": [
    { seconds: null, label: "No timer" },
    { seconds: 30, label: "30 seconds per player" },
    { seconds: 60, label: "1 minute per player" },
    { seconds: 120, label: "2 minutes per player" },
  ],
  chess: [
    { seconds: null, label: "No clock" },
    { seconds: 60, label: "Bullet · 1 minute" },
    { seconds: 180, label: "Blitz · 3 minutes" },
    { seconds: 300, label: "Blitz · 5 minutes" },
    { seconds: 600, label: "Rapid · 10 minutes" },
    { seconds: 900, label: "Rapid · 15 minutes" },
  ],
  "draw-guess": [
    { seconds: 45, label: "Quick sketch · 45 seconds" },
    { seconds: 60, label: "Classic · 1 minute" },
    { seconds: 90, label: "Relaxed · 90 seconds" },
    { seconds: 120, label: "Detailed · 2 minutes" },
  ],
};
const celebrationParticles = Array.from({ length: 36 }, (_, position) => ({
  id: `firework-${position + 1}`,
  position,
}));
const ludoYardTokenCoordinates: Record<LudoColor, Array<{ row: number; column: number }>> = {
  red: [
    { row: 1, column: 1 },
    { row: 1, column: 4 },
    { row: 4, column: 1 },
    { row: 4, column: 4 },
  ],
  green: [
    { row: 1, column: 10 },
    { row: 1, column: 13 },
    { row: 4, column: 10 },
    { row: 4, column: 13 },
  ],
  yellow: [
    { row: 10, column: 10 },
    { row: 10, column: 13 },
    { row: 13, column: 10 },
    { row: 13, column: 13 },
  ],
  blue: [
    { row: 10, column: 1 },
    { row: 10, column: 4 },
    { row: 13, column: 1 },
    { row: 13, column: 4 },
  ],
};
const ludoPathIndexByCoordinate = new Map<string, number>(
  ludoBoardPath.map(
    (coordinate, index) => [`${coordinate.row}:${coordinate.column}`, index] as const,
  ),
);
const ludoHomeColorByCoordinate = new Map<string, LudoColor>(
  ludoColors.flatMap((color) =>
    ludoHomeLanes[color].map(
      (coordinate) => [`${coordinate.row}:${coordinate.column}`, color] as const,
    ),
  ),
);
const ludoStartColorBySquare = new Map(
  ludoColors.map((color) => [ludoStarts[color], color] as const),
);
const ludoYardSlots = new Set(
  Object.values(ludoYardTokenCoordinates)
    .flat()
    .map(({ row, column }) => `${row}:${column}`),
);
const ludoColorHex: Record<LudoColor, string> = {
  red: "#e83f53",
  green: "#19a96b",
  yellow: "#e3a50d",
  blue: "#347fda",
};
const ludoDicePips: Record<number, Array<{ left: number; top: number }>> = {
  1: [{ left: 50, top: 50 }],
  2: [
    { left: 27, top: 27 },
    { left: 73, top: 73 },
  ],
  3: [
    { left: 27, top: 27 },
    { left: 50, top: 50 },
    { left: 73, top: 73 },
  ],
  4: [
    { left: 27, top: 27 },
    { left: 73, top: 27 },
    { left: 27, top: 73 },
    { left: 73, top: 73 },
  ],
  5: [
    { left: 27, top: 27 },
    { left: 73, top: 27 },
    { left: 50, top: 50 },
    { left: 27, top: 73 },
    { left: 73, top: 73 },
  ],
  6: [
    { left: 27, top: 23 },
    { left: 73, top: 23 },
    { left: 27, top: 50 },
    { left: 73, top: 50 },
    { left: 27, top: 77 },
    { left: 73, top: 77 },
  ],
};
const ludoCaptureParticles = Array.from({ length: 10 }, (_, index) => index);
const ludoCenterCapturePolygons: Record<LudoColor, string> = {
  red: "0,0 50,50 0,100",
  green: "0,0 100,0 50,50",
  yellow: "100,0 100,100 50,50",
  blue: "0,100 100,100 50,50",
};

function ludoCaptureMarks(playerId: string, count: number) {
  const marks: Array<{ id: string; number: number }> = [];
  for (let cutNumber = 1; cutNumber <= count; cutNumber += 1) {
    marks.push({ id: `${playerId}:cut:${cutNumber}`, number: cutNumber });
  }
  return marks;
}

function ludoDiceHomeOrigin(color: LudoColor, dieWidth: number, dieHeight: number) {
  return {
    x: (color === "red" || color === "blue" ? -1 : 1) * dieWidth * 1.82,
    y: (color === "red" || color === "green" ? -1 : 1) * dieHeight * 1.82,
  };
}

function ludoYardColor(row: number, column: number): LudoColor | null {
  if (row <= 5 && column <= 5) return "red";
  if (row <= 5 && column >= 9) return "green";
  if (row >= 9 && column >= 9) return "yellow";
  if (row >= 9 && column <= 5) return "blue";
  return null;
}

function isLudoYardInterior(row: number, column: number): boolean {
  const interiorRow = (row >= 1 && row <= 4) || (row >= 10 && row <= 13);
  const interiorColumn = (column >= 1 && column <= 4) || (column >= 10 && column <= 13);
  return interiorRow && interiorColumn;
}

function ludoMoveBlockMessage(reason: LudoMoveBlockReason | null): string {
  switch (reason) {
    case "no_roll":
      return "Roll the dice before choosing a goti.";
    case "finished":
      return "That goti is already home.";
    case "needs_six":
      return "That goti needs a 6 to leave the yard.";
    case "overshoot":
      return "That goti needs an exact roll to reach the centre.";
    case "capture_required":
      return "Cut an opponent once before this goti can enter its home lane.";
    case "blockade":
      return "An opponent’s two-goti blockade is blocking this route.";
    default:
      return "That goti cannot use this roll.";
  }
}

function participantName(
  participantId: string,
  snapshot: ActiveRoomSnapshot | null,
  viewerId: string,
) {
  if (participantId === viewerId) return "You";
  if (participantId.startsWith("ai:")) return "Topzle Bot";
  return (
    snapshot?.participants.find((participant) => participant.id === participantId)?.displayName ??
    "Player"
  );
}

function describeGameResult(
  result: GameResult,
  snapshot: ActiveRoomSnapshot | null,
  viewerId: string,
) {
  const reason = result.reason.replaceAll("_", " ");
  if (result.status === "draw" || result.winnerIds.length === 0)
    return reason === "stalemate"
      ? "Stalemate — the game ended in a draw."
      : `Game tied${reason ? ` · ${reason}` : ""}.`;
  const winners = result.winnerIds
    .map((winnerId) => participantName(winnerId, snapshot, viewerId))
    .join(", ");
  const ranking = result.placements?.length
    ? ` Final order: ${result.placements
        .map((playerId, index) => `${index + 1}. ${participantName(playerId, snapshot, viewerId)}`)
        .join(", ")}.`
    : "";
  return `${winners} ${result.winnerIds.length > 1 ? "win" : "wins"}${reason ? ` · ${reason}` : ""}.${ranking}`;
}

function addResultToRoomSummary(
  current: RoomGameResults | undefined,
  result: GameResult,
  snapshot: ActiveRoomSnapshot | null,
): RoomGameResults {
  const next: RoomGameResults = {
    totalGames: (current?.totalGames ?? 0) + 1,
    draws:
      (current?.draws ?? 0) +
      (result.status === "draw" || (result.status === "completed" && result.winnerIds.length === 0)
        ? 1
        : 0),
    abandoned: (current?.abandoned ?? 0) + (result.status === "abandoned" ? 1 : 0),
    plays: { ...(current?.plays ?? {}) },
    wins: { ...(current?.wins ?? {}) },
    playerNames: { ...(current?.playerNames ?? {}) },
  };
  for (const playerId of new Set([...Object.keys(result.scores), ...result.winnerIds])) {
    next.plays[playerId] = (next.plays[playerId] ?? 0) + 1;
    next.playerNames[playerId] =
      snapshot?.participants.find((participant) => participant.id === playerId)?.displayName ??
      next.playerNames[playerId] ??
      (playerId.startsWith("ai:") ? "Topzle Bot" : "Player");
  }
  for (const winnerId of new Set(result.winnerIds))
    next.wins[winnerId] = (next.wins[winnerId] ?? 0) + 1;
  return next;
}

export function describeRoomGameResults(
  results: RoomGameResults,
  snapshot: ActiveRoomSnapshot | null,
  viewerId: string,
): string {
  const parts = [`${results.totalGames} ${results.totalGames === 1 ? "game" : "games"}`];
  const wins = Object.entries(results.plays)
    .filter(([, games]) => games > 0)
    .sort(([leftId], [rightId]) => {
      const leftWins = results.wins[leftId] ?? 0;
      const rightWins = results.wins[rightId] ?? 0;
      if (rightWins !== leftWins) return rightWins - leftWins;
      return leftId.localeCompare(rightId);
    })
    .map(([playerId]) => {
      const count = results.wins[playerId] ?? 0;
      const fallbackName = results.playerNames[playerId];
      const currentName = participantName(playerId, snapshot, viewerId);
      const name = currentName === "Player" ? (fallbackName ?? "Player") : currentName;
      return `${name}: ${count} ${count === 1 ? "win" : "wins"}`;
    });
  parts.push(...wins);
  parts.push(`${results.draws} ${results.draws === 1 ? "draw" : "draws"}`);
  if (results.abandoned > 0)
    parts.push(`${results.abandoned} abandoned ${results.abandoned === 1 ? "game" : "games"}`);
  return parts.join(" · ");
}

export function RoomPage() {
  const { roomId } = useParams();
  const hiddenAvatarStorageKey = `topzle.room-hidden-avatars:${roomId ?? "unknown"}`;
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuthSession();
  const launchState = location.state as {
    openGamePicker?: boolean;
    clockSeconds?: number | null;
    aiDifficulty?: "easy" | "medium" | "hard";
    aiPolicy?: string;
    gameId?: string | null;
    inviteNotice?: string;
  } | null;
  const stored = useAppStore((state) => state.room);
  const microphone = useAppStore((state) => state.microphone);
  const camera = useAppStore((state) => state.camera);
  const toggleMicrophone = useAppStore((state) => state.toggleMicrophone);
  const toggleCamera = useAppStore((state) => state.toggleCamera);
  const updateRoomSnapshot = useAppStore((state) => state.updateRoomSnapshot);
  const recordGameStarted = useAppStore((state) => state.recordGameStarted);
  const recordGameResult = useAppStore((state) => state.recordGameResult);
  const [connectionState, setConnectionState] = useState<RoomConnectionState>({
    status: "idle",
    snapshot: stored?.snapshot ?? null,
    lastSequence: stored?.snapshot.sequence ?? 0,
    error: null,
  });
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [showNewMessagesOnScreen, setShowNewMessagesOnScreen] = useState(
    () => window.localStorage.getItem(showFloatingChatStorageKey) !== "false",
  );
  const [audioNotificationsEnabled, setAudioNotificationsEnabled] = useState(
    () => window.localStorage.getItem(roomAudioNotificationsStorageKey) !== "false",
  );
  const [floatingChatMessages, setFloatingChatMessages] = useState<ChatItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(Boolean(launchState?.openGamePicker));
  const [pickerMode, setPickerMode] = useState<"catalog" | "setup">(
    launchState?.gameId ? "setup" : "catalog",
  );
  const [playWithBot, setPlayWithBot] = useState(false);
  const [inviteFriendsOpen, setInviteFriendsOpen] = useState(false);
  const [inviteShareStarted, setInviteShareStarted] = useState(false);
  const [mediaPending, setMediaPending] = useState<"microphone" | "camera" | null>(null);
  const [mediaAccessDecision, setMediaAccessDecision] = useState<MediaAccessDecision | null>(null);
  const [callAccessOpen, setCallAccessOpen] = useState(false);
  const [hiddenAvatarIds, setHiddenAvatarIds] = useState<ReadonlySet<string>>(() => {
    try {
      const saved = window.localStorage.getItem(hiddenAvatarStorageKey);
      return new Set(saved ? (JSON.parse(saved) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const [gameActionPending, setGameActionPending] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);
  const [gameStartPending, setGameStartPending] = useState<string | null>(null);
  const [selectedGameId, setSelectedGameId] = useState<string>(
    launchState?.gameId ?? stored?.snapshot.currentGameId ?? "tic-tac-toe",
  );
  const [gameClockSeconds, setGameClockSeconds] = useState<
    Record<"tic-tac-toe" | "chess", number | null>
  >({
    "tic-tac-toe":
      launchState?.gameId === "tic-tac-toe" ? (launchState.clockSeconds ?? null) : null,
    chess: launchState?.gameId === "chess" ? (launchState.clockSeconds ?? null) : null,
  });
  const [drawGuessRoundSeconds, setDrawGuessRoundSeconds] = useState(60);
  const [drawGuessRounds, setDrawGuessRounds] = useState(3);
  const [pickerBotPolicy, setPickerBotPolicy] = useState(
    launchState?.aiPolicy ??
      stored?.snapshot.settings.aiPolicy ??
      defaultBotPolicy(launchState?.gameId ?? stored?.snapshot.currentGameId ?? "tic-tac-toe").id,
  );
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [dismissedChallengerIds, setDismissedChallengerIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [headerPanel, setHeaderPanel] = useState<"participants" | "settings" | "more" | null>(null);
  const [participantsPulse, setParticipantsPulse] = useState(false);
  const [supportKind, setSupportKind] = useState<Exclude<SupportRequestKind, "support"> | null>(
    null,
  );
  const [copyPending, setCopyPending] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [celebration, setCelebration] = useState<string | null>(null);
  const [roundComplete, setRoundComplete] = useState<{
    id: string;
    resultText: string;
    roomSummary: string;
    outcome: "lost" | "draw" | "abandoned";
  } | null>(null);
  const [resultRevealPending, setResultRevealPending] = useState(false);
  const [localAudioMediaStream, setLocalAudioMediaStream] = useState<MediaStream | null>(null);
  const [localVideoStream, setLocalVideoStream] = useState<MediaStream | null>(null);
  const [remoteMediaStreams, setRemoteMediaStreams] = useState<Map<string, MediaStream>>(
    () => new Map(),
  );
  const [chat, setChat] = useState<ChatItem[]>([
    { id: "welcome", text: "Room created. Say hello 👋", at: Date.now() },
    ...(launchState?.inviteNotice
      ? [
          {
            id: "invite-notice",
            text: launchState.inviteNotice,
            at: Date.now(),
          },
        ]
      : []),
  ]);
  const [draft, setDraft] = useState("");
  const [typingParticipantIds, setTypingParticipantIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [presenceToasts, setPresenceToasts] = useState<PresenceToast[]>([]);
  const [inactiveRemovalPending, setInactiveRemovalPending] = useState<string | null>(null);
  const connection = useRef<RoomConnection | null>(null);
  const roomCall = useRef<CloudflareRoomCall | PeerToPeerRoomCall | null>(null);
  const peerSignalBus = useRef<SupabasePeerSignalBus | null>(null);
  const pendingPeerSignalAcks = useRef(
    new Map<string, { timer: number; resolve: (delivered: boolean) => void }>(),
  );
  const pendingPeerSignals = useRef<PeerMediaSignal[]>([]);
  const roomTransportStatus = useRef<RoomConnectionState["status"]>("idle");
  const roomTransportConnectedOnce = useRef(false);
  const chatOpenRef = useRef(chatOpen);
  const showNewMessagesOnScreenRef = useRef(showNewMessagesOnScreen);
  const floatingChatTimers = useRef(new Map<string, number>());
  const receivedChatMessageIds = useRef(new Set<string>());
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const readReceiptsSent = useRef(new Set<string>());
  const typingExpiryTimers = useRef(new Map<string, number>());
  const typingStopTimer = useRef<number | null>(null);
  const typingPublished = useRef(false);
  const publishedMedia = useRef<{
    mediaSessionId?: string;
    audioTrackId?: string;
    videoTrackId?: string;
  }>({});
  const mediaAnnouncementTimer = useRef<number | null>(null);
  const latestSnapshot = useRef<ActiveRoomSnapshot | null>(stored?.snapshot ?? null);
  const audioNotificationsEnabledRef = useRef(audioNotificationsEnabled);
  const notificationAudioContext = useRef<AudioContext | null>(null);
  const baseDocumentTitle = useRef(document.title);
  const titleAlertInterval = useRef<number | null>(null);
  const lastTurnParticipantId = useRef<string | null>(
    roomTurnParticipantId(stored?.snapshot.currentGameId ?? null, stored?.snapshot.gameState),
  );
  const resultMessages = useRef(new Set<string>());
  const participantsPulseTimer = useRef<number | null>(null);
  const resultRevealTimer = useRef<number | null>(null);
  const analyticsGameStartIds = useRef(new Set<string>());
  const pendingSnakesResultAnnouncement = useRef<{
    moveNonce: number;
    announce: () => void;
  } | null>(null);
  const pendingStart = useRef<{ gameId: string; versusAi: boolean } | null>(null);
  const startTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localAudioStream = useRef<MediaStream | null>(null);
  const localVideoStreamRef = useRef<MediaStream | null>(null);
  const hasStartedVideo = useRef(false);
  const storedRoomId = stored?.roomId;
  const storedParticipantId = stored?.participantId;
  const storedHostId = stored?.snapshot.hostId;
  const storedReconnectToken = stored?.reconnectToken;
  const storedWebsocketUrl = stored?.websocketUrl;
  const storedRealtimeSignalToken = stored?.realtimeSignalToken;
  const mediaActorKey = session?.user.id ?? storedParticipantId ?? null;
  const localMediaAllowed = mediaAccessDecision?.allowed === true;
  const peerSignalFallbackNeeded =
    mediaAccessDecision?.provider === "p2p" &&
    Boolean(
      localAudioMediaStream ||
        localVideoStream ||
        connectionState.snapshot?.participants.some(
          (participant) => participant.microphone || participant.camera,
        ),
    );
  const deliverPeerSignal = useCallback((signal: PeerMediaSignal) => {
    const call = roomCall.current;
    if (call instanceof PeerToPeerRoomCall) void call.handleSignal(signal);
    else pendingPeerSignals.current = [...pendingPeerSignals.current.slice(-63), signal];
  }, []);
  const sendPeerSignal = useCallback(
    async (
      targetId: string,
      signal: Omit<PeerMediaSignal, "fromId" | "targetId">,
    ): Promise<boolean> => {
      const signalId = signal.signalId ?? crypto.randomUUID();
      const identifiedSignal = { ...signal, signalId };
      const directMessageId =
        roomTransportStatus.current === "connected"
          ? connection.current?.sendTracked("media.signal", {
              targetId,
              ...identifiedSignal,
            })
          : null;
      if (!directMessageId)
        return (await peerSignalBus.current?.send(targetId, identifiedSignal)) ?? false;
      return new Promise<boolean>((resolve) => {
        const timer = window.setTimeout(() => {
          pendingPeerSignalAcks.current.delete(signalId);
          const fallbackBus = peerSignalBus.current;
          if (!fallbackBus) {
            resolve(false);
            return;
          }
          void fallbackBus
            .send(targetId, identifiedSignal)
            .then(resolve)
            .catch(() => resolve(false));
        }, 1_000);
        pendingPeerSignalAcks.current.set(signalId, { timer, resolve });
      });
    },
    [],
  );
  const refreshMediaAccess = useCallback(async () => {
    try {
      const decision = await api.mediaAccess();
      setMediaAccessDecision(decision);
      if (decision.allowed) setCallAccessOpen(false);
    } catch (error) {
      setMediaAccessDecision({
        allowed: false,
        provider: "p2p",
        reason: "calls_paused",
        action: null,
        message: error instanceof Error ? error.message : "Call access could not be checked.",
      });
    }
  }, []);
  const announcePublishedMedia = useCallback(
    (media = publishedMedia.current, retryWhenDisconnected = true) => {
      publishedMedia.current = media;
      if (mediaAnnouncementTimer.current !== null) {
        window.clearTimeout(mediaAnnouncementTimer.current);
        mediaAnnouncementTimer.current = null;
      }
      const microphone = Boolean(
        media.audioTrackId &&
          localAudioStream.current
            ?.getAudioTracks()
            .some((track) => track.enabled && track.readyState === "live"),
      );
      const camera = Boolean(
        media.videoTrackId &&
          localVideoStreamRef.current
            ?.getVideoTracks()
            .some((track) => track.enabled && track.readyState === "live"),
      );
      const sent = Boolean(
        connection.current?.send("media.state", {
          microphone,
          camera,
          ...media,
        }),
      );
      if (!sent && retryWhenDisconnected)
        mediaAnnouncementTimer.current = window.setTimeout(
          () => announcePublishedMedia(publishedMedia.current),
          750,
        );
      return sent;
    },
    [],
  );
  const applyMediaPreferences = useCallback(
    (preferences: { activeSpeakerIds: string[]; hiddenVideoIds: string[] }) =>
      roomCall.current?.setMediaPreferences(preferences),
    [],
  );
  useEffect(() => {
    const saved = readGameSetupDefaults()[selectedGameId];
    const availablePolicies = botPolicies[selectedGameId] ?? [];
    const requestedPolicy =
      saved?.botPolicy ??
      (stored?.snapshot.currentGameId === selectedGameId
        ? stored.snapshot.settings.aiPolicy
        : undefined);
    const policy =
      availablePolicies.find((candidate) => candidate.id === requestedPolicy) ??
      defaultBotPolicy(selectedGameId);
    setPickerBotPolicy(policy.id);
    // Playing a bot is always an explicit choice for this round.
    setPlayWithBot(false);
    if (
      (selectedGameId === "tic-tac-toe" || selectedGameId === "chess") &&
      saved?.clockSeconds !== undefined
    )
      setGameClockSeconds((value) => ({ ...value, [selectedGameId]: saved.clockSeconds ?? null }));
    if (selectedGameId === "draw-guess") {
      if (typeof saved?.clockSeconds === "number") setDrawGuessRoundSeconds(saved.clockSeconds);
      if (typeof saved?.rounds === "number") setDrawGuessRounds(saved.rounds);
    }
  }, [selectedGameId, stored?.snapshot.currentGameId, stored?.snapshot.settings.aiPolicy]);
  useEffect(() => {
    if (!mediaActorKey || !storedRoomId || storedRoomId !== roomId) return;
    void refreshMediaAccess();
    const refresh = () => void refreshMediaAccess();
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [mediaActorKey, refreshMediaAccess, roomId, storedRoomId]);
  const clearFloatingChatMessages = useCallback(() => {
    for (const timer of floatingChatTimers.current.values()) window.clearTimeout(timer);
    floatingChatTimers.current.clear();
    setFloatingChatMessages([]);
  }, []);
  const floatChatMessage = useCallback((item: ChatItem) => {
    if (!showNewMessagesOnScreenRef.current || chatOpenRef.current) return;
    setFloatingChatMessages((items) => [
      ...items.filter((candidate) => candidate.id !== item.id),
      item,
    ]);
    const previousTimer = floatingChatTimers.current.get(item.id);
    if (previousTimer) window.clearTimeout(previousTimer);
    floatingChatTimers.current.set(
      item.id,
      window.setTimeout(() => {
        floatingChatTimers.current.delete(item.id);
        setFloatingChatMessages((items) => items.filter((candidate) => candidate.id !== item.id));
      }, floatingChatDurationMs),
    );
  }, []);
  const ensureNotificationAudio = useCallback((): AudioContext | null => {
    if (notificationAudioContext.current) return notificationAudioContext.current;
    const AudioContextConstructor =
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
      window.AudioContext;
    if (!AudioContextConstructor) return null;
    notificationAudioContext.current = new AudioContextConstructor();
    return notificationAudioContext.current;
  }, []);
  const playRoomNotification = useCallback(
    (kind: "turn" | "join" | "chat") => {
      if (!audioNotificationsEnabledRef.current) return;
      try {
        const context = ensureNotificationAudio();
        if (!context) return;
        if (context.state === "suspended") void context.resume();
        const now = context.currentTime + 0.02;
        const notes = kind === "turn" ? [659, 880] : kind === "join" ? [523, 659] : [784];
        notes.forEach((frequency, index) => {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          const startsAt = now + index * 0.13;
          oscillator.type = kind === "turn" ? "sine" : kind === "join" ? "triangle" : "sine";
          oscillator.frequency.setValueAtTime(frequency, startsAt);
          gain.gain.setValueAtTime(0.0001, startsAt);
          gain.gain.exponentialRampToValueAtTime(0.12, startsAt + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + 0.11);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(startsAt);
          oscillator.stop(startsAt + 0.12);
        });
      } catch {
        // Browsers can block audio until a gesture. The next room interaction unlocks it.
      }
    },
    [ensureNotificationAudio],
  );
  const clearTabActivity = useCallback(() => {
    if (titleAlertInterval.current !== null) {
      window.clearInterval(titleAlertInterval.current);
      titleAlertInterval.current = null;
    }
    document.title = baseDocumentTitle.current;
  }, []);
  const showTabActivity = useCallback((label: string) => {
    if (document.visibilityState !== "hidden") return;
    if (titleAlertInterval.current !== null) window.clearInterval(titleAlertInterval.current);
    let showAlert = true;
    const render = () => {
      document.title = showAlert ? `🔔 ${label} · Topzle` : baseDocumentTitle.current;
      showAlert = !showAlert;
    };
    render();
    titleAlertInterval.current = window.setInterval(render, 900);
  }, []);
  useEffect(() => {
    audioNotificationsEnabledRef.current = audioNotificationsEnabled;
    window.localStorage.setItem(
      roomAudioNotificationsStorageKey,
      String(audioNotificationsEnabled),
    );
  }, [audioNotificationsEnabled]);
  useEffect(() => {
    const unlockAudio = () => {
      if (!audioNotificationsEnabledRef.current) return;
      const context = ensureNotificationAudio();
      if (context?.state === "suspended") void context.resume();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") clearTabActivity();
    };
    document.addEventListener("pointerdown", unlockAudio, { passive: true });
    document.addEventListener("keydown", unlockAudio);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearTabActivity();
      const context = notificationAudioContext.current;
      notificationAudioContext.current = null;
      if (context) void context.close();
    };
  }, [clearTabActivity, ensureNotificationAudio]);
  useEffect(() => {
    chatOpenRef.current = chatOpen;
    if (chatOpen) {
      setUnreadChatCount(0);
      clearFloatingChatMessages();
    }
  }, [chatOpen, clearFloatingChatMessages]);
  useEffect(() => {
    if (!chatOpen) return;
    const viewerId = storedParticipantId || storedHostId || "";
    for (const item of chat) {
      if (!item.senderId || item.senderId === viewerId || readReceiptsSent.current.has(item.id))
        continue;
      if (connection.current?.send("chat.receipt", { messageId: item.id, status: "read" }))
        readReceiptsSent.current.add(item.id);
    }
    const frame = window.requestAnimationFrame(() => {
      const element = chatScrollRef.current;
      element?.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [chat, chatOpen, storedHostId, storedParticipantId]);
  useEffect(() => {
    showNewMessagesOnScreenRef.current = showNewMessagesOnScreen;
    window.localStorage.setItem(showFloatingChatStorageKey, String(showNewMessagesOnScreen));
    if (!showNewMessagesOnScreen) clearFloatingChatMessages();
  }, [clearFloatingChatMessages, showNewMessagesOnScreen]);
  useEffect(() => {
    window.localStorage.setItem(hiddenAvatarStorageKey, JSON.stringify([...hiddenAvatarIds]));
  }, [hiddenAvatarIds, hiddenAvatarStorageKey]);
  useEffect(() => {
    if (!celebration) return;
    const timer = window.setTimeout(() => setCelebration(null), 4_500);
    return () => window.clearTimeout(timer);
  }, [celebration]);
  useEffect(
    () => () => {
      localAudioStream.current?.getTracks().forEach((track) => {
        track.stop();
      });
      localVideoStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
      roomCall.current?.stop();
      for (const timer of floatingChatTimers.current.values()) window.clearTimeout(timer);
      floatingChatTimers.current.clear();
      for (const timer of typingExpiryTimers.current.values()) window.clearTimeout(timer);
      typingExpiryTimers.current.clear();
      if (typingStopTimer.current) window.clearTimeout(typingStopTimer.current);
      if (mediaAnnouncementTimer.current !== null)
        window.clearTimeout(mediaAnnouncementTimer.current);
      if (participantsPulseTimer.current !== null)
        window.clearTimeout(participantsPulseTimer.current);
      if (resultRevealTimer.current !== null) window.clearTimeout(resultRevealTimer.current);
    },
    [],
  );
  useEffect(() => {
    const leaveRoom = (event: PageTransitionEvent) => {
      if (!event.persisted) connection.current?.close();
    };
    window.addEventListener("pagehide", leaveRoom);
    return () => window.removeEventListener("pagehide", leaveRoom);
  }, []);
  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 760px)");
    const closeChatOnMobile = (event: MediaQueryListEvent) => {
      if (event.matches) setChatOpen(false);
    };
    mobile.addEventListener("change", closeChatOnMobile);
    return () => mobile.removeEventListener("change", closeChatOnMobile);
  }, []);
  useEffect(() => {
    if (
      !storedRoomId ||
      storedRoomId !== roomId ||
      !storedWebsocketUrl ||
      !storedReconnectToken ||
      (!storedParticipantId && !storedHostId)
    )
      return;
    roomTransportStatus.current = "idle";
    roomTransportConnectedOnce.current = false;
    const roomConnection = new RoomConnection({
      url: storedWebsocketUrl,
      roomId: storedRoomId,
      participantId: storedParticipantId || storedHostId || "",
      reconnectToken: storedReconnectToken,
      onState: (state) => {
        const priorTransportStatus = roomTransportStatus.current;
        const recoveredTransport =
          state.status === "connected" &&
          roomTransportConnectedOnce.current &&
          priorTransportStatus !== "connected";
        roomTransportStatus.current = state.status;
        if (state.snapshot?.gameState !== latestSnapshot.current?.gameState)
          setGameActionPending(false);
        if (state.snapshot) {
          latestSnapshot.current = state.snapshot;
          const connectedParticipantIds = new Set(
            state.snapshot.participants
              .filter((participant) => participant.connected)
              .map((participant) => participant.id),
          );
          if (connectedParticipantIds.size > 0)
            setPresenceToasts((items) => {
              const nextItems = items.filter(
                (item) =>
                  item.state !== "inactive" || !connectedParticipantIds.has(item.participantId),
              );
              return nextItems.length === items.length ? items : nextItems;
            });
        }
        setConnectionState(state);
        if (state.status === "connected") {
          if (recoveredTransport && roomCall.current instanceof PeerToPeerRoomCall)
            roomCall.current.restartConnections();
          roomTransportConnectedOnce.current = true;
          roomConnection.send("room.presence", {
            activeInRoom: document.visibilityState === "visible" && document.hasFocus(),
          });
        }
      },
      onMessage: (message: RoomMessage) => {
        if (message.type === "media.signal_ack") {
          const payload = message.payload as { signalId?: unknown };
          if (typeof payload.signalId === "string") {
            const pending = pendingPeerSignalAcks.current.get(payload.signalId);
            if (pending) {
              window.clearTimeout(pending.timer);
              pendingPeerSignalAcks.current.delete(payload.signalId);
              pending.resolve(true);
            }
          }
          return;
        }
        if (message.type === "media.signal") {
          deliverPeerSignal(message.payload as PeerMediaSignal);
          return;
        }
        if (message.type === "chat.message") {
          const payload = message.payload as { text?: string; history?: boolean };
          const isHistory = payload.history === true;
          if (payload.text && !receivedChatMessageIds.current.has(message.id)) {
            receivedChatMessageIds.current.add(message.id);
            const viewerId = storedParticipantId || storedHostId || "";
            const item: ChatItem = {
              id: message.id,
              ...(message.senderId ? { senderId: message.senderId } : {}),
              text: payload.text,
              at: message.serverTimestamp ?? Date.now(),
              ...(message.senderId === viewerId ? { delivery: "sent" as const } : {}),
            };
            setChat((items) => {
              const existing = items.findIndex((candidate) => candidate.id === item.id);
              if (existing < 0) return [...items, item];
              return items.map((candidate, index) =>
                index === existing ? { ...candidate, delivery: "sent" as const } : candidate,
              );
            });
            if (!isHistory && message.senderId && message.senderId !== viewerId) {
              const status = chatOpenRef.current ? "read" : "delivered";
              if (
                connection.current?.send("chat.receipt", {
                  messageId: message.id,
                  status,
                }) &&
                status === "read"
              )
                readReceiptsSent.current.add(message.id);
            }
            if (
              !isHistory &&
              message.senderId !== viewerId &&
              shouldNotifyRoomParticipant(latestSnapshot.current, viewerId)
            ) {
              playRoomNotification("chat");
              showTabActivity("New chat message");
              if (!chatOpenRef.current) {
                setUnreadChatCount((count) => Math.min(count + 1, 99));
                floatChatMessage(item);
              }
            }
          }
        }
        if (message.type === "chat.receipt") {
          const payload = message.payload as {
            messageId?: string;
            participantId?: string;
            status?: "delivered" | "read";
          };
          const { messageId, participantId, status } = payload;
          if (messageId && participantId && status)
            setChat((items) =>
              items.map((item) =>
                item.id === messageId
                  ? {
                      ...item,
                      receipts: {
                        ...item.receipts,
                        [participantId]: status,
                      },
                    }
                  : item,
              ),
            );
        }
        if (message.type === "chat.typing" && message.senderId) {
          const payload = message.payload as { isTyping?: boolean };
          const participantId = message.senderId;
          const previousTimer = typingExpiryTimers.current.get(participantId);
          if (previousTimer) window.clearTimeout(previousTimer);
          if (payload.isTyping) {
            setTypingParticipantIds((current) => new Set(current).add(participantId));
            typingExpiryTimers.current.set(
              participantId,
              window.setTimeout(() => {
                typingExpiryTimers.current.delete(participantId);
                setTypingParticipantIds((current) => {
                  const next = new Set(current);
                  next.delete(participantId);
                  return next;
                });
              }, 2_000),
            );
          } else {
            typingExpiryTimers.current.delete(participantId);
            setTypingParticipantIds((current) => {
              const next = new Set(current);
              next.delete(participantId);
              return next;
            });
          }
        }
        if (message.type === "room.leave" && message.senderId) {
          const departed = latestSnapshot.current?.participants.find(
            (participant) => participant.id === message.senderId,
          );
          if (departed && !departed.id.startsWith("ai:")) {
            setPresenceToasts((items) =>
              [
                ...items.filter((item) => item.participantId !== departed.id),
                {
                  id: message.id,
                  participantId: departed.id,
                  displayName: departed.displayName,
                  state: "inactive" as const,
                },
              ].slice(-4),
            );
            setChat((items) =>
              items.some((item) => item.id === `presence:${message.id}`)
                ? items
                : [
                    ...items,
                    {
                      id: `presence:${message.id}`,
                      text: `${departed.displayName} went inactive.`,
                      at: message.serverTimestamp ?? Date.now(),
                    },
                  ],
            );
          }
        }
        if (message.type === "room.join") {
          const joined = message.payload as { id?: string; displayName?: string; role?: string };
          if (joined.id) {
            setPresenceToasts((items) => items.filter((item) => item.participantId !== joined.id));
            const viewerId = storedParticipantId || storedHostId || "";
            if (joined.id !== viewerId) {
              if (participantsPulseTimer.current !== null)
                window.clearTimeout(participantsPulseTimer.current);
              setParticipantsPulse(false);
              window.requestAnimationFrame(() => setParticipantsPulse(true));
              participantsPulseTimer.current = window.setTimeout(() => {
                setParticipantsPulse(false);
                participantsPulseTimer.current = null;
              }, 2_400);
            }
            if (
              joined.id !== viewerId &&
              joined.role !== "ai" &&
              !joined.id.startsWith("ai:") &&
              shouldNotifyRoomParticipant(latestSnapshot.current, viewerId)
            ) {
              playRoomNotification("join");
              showTabActivity(`${joined.displayName ?? "A player"} joined`);
            }
          }
        }
        if (message.type === "room.member_updated") {
          const update = message.payload as {
            participantId?: string;
            displayName?: string;
            removed?: boolean;
            replacementPlayerId?: string;
          };
          if (update.removed && update.participantId) {
            setInactiveRemovalPending(null);
            setPresenceToasts((items) =>
              [
                ...items.filter((item) => item.participantId !== update.participantId),
                {
                  id: message.id,
                  participantId: update.participantId ?? "",
                  displayName: update.displayName ?? "Inactive player",
                  state: "removed" as const,
                },
              ].slice(-4),
            );
            setChat((items) => [
              ...items,
              {
                id: `presence:${message.id}`,
                text: update.replacementPlayerId?.startsWith("ai:")
                  ? `${update.displayName ?? "The inactive player"} was removed. Topzle AI has taken their place.`
                  : `${update.displayName ?? "The inactive player"} was removed and the game can continue.`,
                at: message.serverTimestamp ?? Date.now(),
              },
            ]);
          }
        }
        if (
          message.type === "game.state" ||
          message.type === "game.started" ||
          message.type === "game.switched" ||
          message.type === "game.resumed"
        ) {
          setGameActionPending(false);
          setGameError(null);
          const turnPayload = message.payload as {
            gameId?: string;
            state?: ActiveRoomSnapshot["gameState"];
          };
          const turnGameId = turnPayload.gameId ?? latestSnapshot.current?.currentGameId ?? null;
          const nextTurnParticipantId = roomTurnParticipantId(turnGameId, turnPayload.state);
          const viewerId = storedParticipantId || storedHostId || "";
          if (nextTurnParticipantId === viewerId && lastTurnParticipantId.current !== viewerId) {
            playRoomNotification("turn");
            showTabActivity("Your turn");
          }
          lastTurnParticipantId.current = nextTurnParticipantId;
          if (message.type === "game.state") {
            const resultPayload = message.payload as {
              result?: GameResult | null;
              roomResults?: RoomGameResults;
            };
            const result = resultPayload.result;
            if (result && !resultMessages.current.has(message.id)) {
              resultMessages.current.add(message.id);
              recordGameResult(storedRoomId, result, message.serverTimestamp ?? Date.now());
              const viewerId = storedParticipantId || storedHostId || "";
              const versusAi =
                latestSnapshot.current?.participants.some(
                  (participant) => participant.role === "ai",
                ) ?? false;
              trackEvent("game_result", {
                game_id: turnGameId ?? "unknown",
                result: result.winnerIds.includes(viewerId)
                  ? "win"
                  : result.winnerIds.length > 0
                    ? "loss"
                    : result.status === "abandoned"
                      ? "abandoned"
                      : "draw",
                versus_ai: versusAi,
                difficulty: versusAi
                  ? (latestSnapshot.current?.settings.aiDifficulty ?? "medium")
                  : "none",
                round_number: resultPayload.roomResults?.totalGames ?? 1,
              });
              const roomResults =
                resultPayload.roomResults ??
                addResultToRoomSummary(
                  latestSnapshot.current?.roomResults,
                  result,
                  latestSnapshot.current,
                );
              let resultAnnounced = false;
              const announceResult = () => {
                if (resultAnnounced) return;
                resultAnnounced = true;
                if (resultRevealTimer.current !== null)
                  window.clearTimeout(resultRevealTimer.current);
                setResultRevealPending(false);
                resultRevealTimer.current = null;
                setChat((items) => [
                  ...items,
                  {
                    id: `result:${message.id}`,
                    text: describeGameResult(result, latestSnapshot.current, viewerId),
                    roomResultSummary: describeRoomGameResults(
                      roomResults,
                      latestSnapshot.current,
                      viewerId,
                    ),
                    at: message.serverTimestamp ?? Date.now(),
                    kind: "result",
                    result,
                  },
                ]);
                if (result.winnerIds.includes(viewerId)) setCelebration(message.id);
                else if (
                  latestSnapshot.current?.participants.some(
                    (participant) =>
                      participant.id === viewerId &&
                      participant.role !== "spectator" &&
                      !participant.id.startsWith("ai:"),
                  )
                )
                  setRoundComplete({
                    id: message.id,
                    resultText: describeGameResult(result, latestSnapshot.current, viewerId),
                    roomSummary: describeRoomGameResults(
                      roomResults,
                      latestSnapshot.current,
                      viewerId,
                    ),
                    outcome:
                      result.status === "abandoned"
                        ? "abandoned"
                        : result.status === "draw" || result.winnerIds.length === 0
                          ? "draw"
                          : "lost",
                  });
              };
              const completedState = resultPayload as {
                gameId?: string;
                state?: {
                  lastMove?: { nonce?: unknown } | null;
                  actionAvailableAt?: number | null;
                  fastMode?: boolean;
                  history?: Array<{ dice?: number }>;
                };
              };
              const finalMoveNonce = completedState.state?.lastMove?.nonce;
              const hasVisualWin = result.winnerIds.length > 0 && result.status !== "abandoned";
              if (hasVisualWin) setResultRevealPending(true);
              if (
                completedState.gameId === "snakes-ladders" &&
                typeof finalMoveNonce === "number"
              ) {
                pendingSnakesResultAnnouncement.current = {
                  moveNonce: finalMoveNonce,
                  announce: announceResult,
                };
                if (hasVisualWin) {
                  if (resultRevealTimer.current !== null)
                    window.clearTimeout(resultRevealTimer.current);
                  resultRevealTimer.current = window.setTimeout(() => {
                    pendingSnakesResultAnnouncement.current = null;
                    announceResult();
                  }, 15_000);
                } else announceResult();
              } else if (hasVisualWin) {
                if (resultRevealTimer.current !== null)
                  window.clearTimeout(resultRevealTimer.current);
                const revealDelay =
                  completedState.gameId === "battleship" &&
                  typeof completedState.state?.actionAvailableAt === "number"
                    ? Math.max(1_800, completedState.state.actionAvailableAt - Date.now() + 1_200)
                    : completedState.gameId === "ludo"
                      ? (() => {
                          const lastDice = completedState.state?.history?.at(-1)?.dice ?? 1;
                          const fastMode = completedState.state?.fastMode === true;
                          return (
                            (fastMode ? 80 : 140) +
                            lastDice * (fastMode ? 95 : 185) +
                            (fastMode ? 120 : 240) +
                            1_200
                          );
                        })()
                      : 1_800;
                resultRevealTimer.current = window.setTimeout(announceResult, revealDelay);
              } else announceResult();
            }
          }
          if (message.type === "game.started" || message.type === "game.switched") {
            setRoundComplete(null);
            setResultRevealPending(false);
            if (resultRevealTimer.current !== null) {
              window.clearTimeout(resultRevealTimer.current);
              resultRevealTimer.current = null;
            }
            pendingSnakesResultAnnouncement.current = null;
            const requested = pendingStart.current;
            const startPayload = message.payload as {
              gameId?: string;
              removedInactiveParticipantIds?: string[];
            };
            const gameId = startPayload.gameId ?? requested?.gameId;
            if (gameId) {
              recordGameStarted({
                messageId: message.id,
                roomId: storedRoomId,
                playerId: storedParticipantId || storedHostId || "",
                gameId,
                versusAi: requested?.versusAi ?? false,
                startedAt: message.serverTimestamp ?? Date.now(),
              });
              if (!analyticsGameStartIds.current.has(message.id)) {
                analyticsGameStartIds.current.add(message.id);
                const versusAi =
                  requested?.versusAi ??
                  latestSnapshot.current?.participants.some(
                    (participant) => participant.role === "ai",
                  ) ??
                  false;
                trackEvent("game_start", {
                  game_id: gameId,
                  versus_ai: versusAi,
                  difficulty: versusAi
                    ? (latestSnapshot.current?.settings.aiDifficulty ?? "medium")
                    : "none",
                  participant_count:
                    latestSnapshot.current?.participants.filter(
                      (participant) => participant.connected && participant.role !== "spectator",
                    ).length ?? 0,
                });
              }
            }
            if (startPayload.removedInactiveParticipantIds?.length) {
              const removedCount = startPayload.removedInactiveParticipantIds.length;
              setChat((items) =>
                items.some((item) => item.id === `cleanup:${message.id}`)
                  ? items
                  : [
                      ...items,
                      {
                        id: `cleanup:${message.id}`,
                        text: `${removedCount} inactive ${removedCount === 1 ? "player was" : "players were"} removed. The room restarted with Topzle AI.`,
                        at: message.serverTimestamp ?? Date.now(),
                      },
                    ],
              );
            }
            pendingStart.current = null;
            if (startTimeout.current) clearTimeout(startTimeout.current);
            startTimeout.current = null;
            setGameStartPending(null);
            setPickerOpen(false);
          }
          setConnectionState((value) => ({
            ...value,
            snapshot: value.snapshot
              ? {
                  ...value.snapshot,
                  settings: ["easy", "medium", "hard"].includes(
                    String((message.payload as { aiDifficulty?: string }).aiDifficulty),
                  )
                    ? {
                        ...value.snapshot.settings,
                        aiDifficulty: (
                          message.payload as {
                            aiDifficulty: "easy" | "medium" | "hard";
                          }
                        ).aiDifficulty,
                        ...(typeof (message.payload as { aiPolicy?: unknown }).aiPolicy === "string"
                          ? {
                              aiPolicy: (message.payload as { aiPolicy: string }).aiPolicy,
                            }
                          : {}),
                      }
                    : typeof (message.payload as { aiPolicy?: unknown }).aiPolicy === "string"
                      ? {
                          ...value.snapshot.settings,
                          aiPolicy: (message.payload as { aiPolicy: string }).aiPolicy,
                        }
                      : value.snapshot.settings,
                  currentGameId:
                    (message.payload as { gameId?: string }).gameId ?? value.snapshot.currentGameId,
                  gameState:
                    (message.payload as { state?: ActiveRoomSnapshot["gameState"] }).state ??
                    value.snapshot.gameState,
                  roomResults:
                    (message.payload as { roomResults?: RoomGameResults }).roomResults ??
                    value.snapshot.roomResults,
                }
              : null,
          }));
        }
        if (message.type === "room.layout_changed" || message.type === "media.layout") {
          const layout = (message.payload as { layout?: ActiveRoomSnapshot["layout"] }).layout;
          if (layout)
            setConnectionState((value) => ({
              ...value,
              snapshot: value.snapshot ? { ...value.snapshot, layout } : null,
            }));
        }
        if (message.type === "error") {
          const payload = message.payload as { message?: string };
          const messageText = payload.message ?? "The room could not complete that action.";
          setGameActionPending(false);
          setGameError(messageText);
          setGameStartPending(null);
          setInactiveRemovalPending(null);
          pendingStart.current = null;
          if (startTimeout.current) clearTimeout(startTimeout.current);
          startTimeout.current = null;
          setConnectionState((value) => ({
            ...value,
            error: messageText,
          }));
        }
        if (message.type === "game.ended") {
          lastTurnParticipantId.current = null;
          setConnectionState((value) => ({
            ...value,
            snapshot: value.snapshot
              ? { ...value.snapshot, currentGameId: null, gameState: null }
              : null,
          }));
        }
      },
    });
    connection.current = roomConnection;
    roomConnection.connect();
    return () => {
      roomConnection.close();
      for (const pending of pendingPeerSignalAcks.current.values()) {
        window.clearTimeout(pending.timer);
        pending.resolve(false);
      }
      pendingPeerSignalAcks.current.clear();
      if (startTimeout.current) clearTimeout(startTimeout.current);
    };
  }, [
    recordGameStarted,
    recordGameResult,
    roomId,
    storedHostId,
    storedParticipantId,
    storedReconnectToken,
    storedRoomId,
    storedWebsocketUrl,
    deliverPeerSignal,
    floatChatMessage,
    playRoomNotification,
    showTabActivity,
  ]);
  useEffect(() => {
    if (
      !peerSignalFallbackNeeded ||
      !storedRealtimeSignalToken ||
      !storedParticipantId ||
      storedRoomId !== roomId
    )
      return;
    const bus = new SupabasePeerSignalBus(
      storedRealtimeSignalToken,
      storedParticipantId,
      deliverPeerSignal,
    );
    peerSignalBus.current = bus;
    bus.start(connectionState.status !== "connected");
    return () => {
      bus.stop();
      if (peerSignalBus.current === bus) peerSignalBus.current = null;
    };
  }, [
    deliverPeerSignal,
    connectionState.status,
    peerSignalFallbackNeeded,
    roomId,
    storedParticipantId,
    storedRealtimeSignalToken,
    storedRoomId,
  ]);
  useEffect(() => {
    const publishActivity = () => {
      connection.current?.send("room.presence", {
        activeInRoom: document.visibilityState === "visible" && document.hasFocus(),
      });
    };
    document.addEventListener("visibilitychange", publishActivity);
    window.addEventListener("focus", publishActivity);
    window.addEventListener("blur", publishActivity);
    return () => {
      connection.current?.send("room.presence", { activeInRoom: false });
      document.removeEventListener("visibilitychange", publishActivity);
      window.removeEventListener("focus", publishActivity);
      window.removeEventListener("blur", publishActivity);
    };
  }, []);
  useEffect(() => {
    if (!storedRoomId || !storedParticipantId || storedRoomId !== roomId || !localMediaAllowed)
      return;
    const sharedOptions = {
      participantId: storedParticipantId,
      onPublished: announcePublishedMedia,
      onRemoteStreams: setRemoteMediaStreams,
      onError: (message: string) => {
        const callFallback = "You can keep playing and chatting without the call.";
        const error = message.toLowerCase().includes("keep playing and chatting")
          ? message
          : `${message.replace(/[.\s]+$/, "")}. ${callFallback}`;
        setConnectionState((value) => ({ ...value, error }));
      },
    };
    const call =
      mediaAccessDecision?.provider === "cloudflare_realtime"
        ? new CloudflareRoomCall({
            ...sharedOptions,
            createSession: () => api.createMediaSession(storedRoomId),
            addTracks: (sessionId, input) => api.addMediaTracks(storedRoomId, sessionId, input),
            updateTracks: (sessionId, input) =>
              api.updateMediaTracks(storedRoomId, sessionId, input),
            closeTracks: (sessionId, mids) => api.closeMediaTracks(storedRoomId, sessionId, mids),
            renegotiate: (sessionId, description) =>
              api.renegotiateMedia(storedRoomId, sessionId, description),
            lease: (input) => api.leaseMedia(storedRoomId, input),
          })
        : new PeerToPeerRoomCall({
            ...sharedOptions,
            signal: sendPeerSignal,
          });
    roomCall.current = call;
    call.syncParticipants(latestSnapshot.current?.participants ?? []);
    if (call instanceof PeerToPeerRoomCall) {
      for (const signal of pendingPeerSignals.current.splice(0)) void call.handleSignal(signal);
    } else pendingPeerSignals.current = [];
    void call.publish(
      localAudioStream.current?.getAudioTracks()[0],
      localVideoStreamRef.current?.getVideoTracks()[0],
    );
    return () => {
      call.stop();
      if (roomCall.current === call) roomCall.current = null;
    };
  }, [
    announcePublishedMedia,
    localMediaAllowed,
    mediaAccessDecision?.provider,
    roomId,
    storedParticipantId,
    storedRoomId,
    sendPeerSignal,
  ]);
  useEffect(() => {
    if (connectionState.status === "connected") announcePublishedMedia(publishedMedia.current);
  }, [announcePublishedMedia, connectionState.status]);
  useEffect(() => {
    if (roomId && connectionState.snapshot) {
      latestSnapshot.current = connectionState.snapshot;
      updateRoomSnapshot(roomId, connectionState.snapshot);
      roomCall.current?.syncParticipants(connectionState.snapshot.participants);
    }
  }, [connectionState.snapshot, roomId, updateRoomSnapshot]);
  const activeGameId = (connectionState.snapshot ?? stored?.snapshot)?.currentGameId ?? null;
  const [styledGameId, setStyledGameId] = useState<string | null>(
    activeGameId && loadedGameStyles.has(activeGameId) ? activeGameId : null,
  );
  useEffect(() => {
    let active = true;
    if (!activeGameId || !gameStyleLoaders[activeGameId as PlayableRoomGameId]) {
      setStyledGameId(activeGameId);
      return;
    }
    void loadGameStyles(activeGameId)
      .then(() => {
        if (active) setStyledGameId(activeGameId);
      })
      .catch(() => {
        if (active)
          setConnectionState((value) => ({
            ...value,
            error: "The game visuals could not load. Refresh the room to try again.",
          }));
      });
    return () => {
      active = false;
    };
  }, [activeGameId]);
  if (!stored || stored.roomId !== roomId)
    return (
      <section className="empty-state">
        <WifiOff />
        <h1>This room session isn't on this device.</h1>
        <p>Open the invite again or enter its six-letter code to reconnect securely.</p>
        <Link className="button button-primary" to="/join">
          Join with code
        </Link>
      </section>
    );
  const snapshot = connectionState.snapshot ?? stored.snapshot;
  const ticState =
    snapshot.currentGameId === "tic-tac-toe"
      ? (snapshot.gameState as unknown as TicTacToeRoomState | null)
      : null;
  const chessState =
    snapshot.currentGameId === "chess"
      ? (snapshot.gameState as unknown as ChessRoomState | null)
      : null;
  const ludoState =
    snapshot.currentGameId === "ludo"
      ? (snapshot.gameState as unknown as LudoRoomState | null)
      : null;
  const connectFourState =
    snapshot.currentGameId === "connect-four"
      ? (snapshot.gameState as unknown as ConnectFourState | null)
      : null;
  const battleshipState =
    snapshot.currentGameId === "battleship"
      ? (snapshot.gameState as unknown as BattleshipPublicState | null)
      : null;
  const drawGuessState =
    snapshot.currentGameId === "draw-guess"
      ? (snapshot.gameState as unknown as DrawGuessPublicState | null)
      : null;
  const rockPaperScissorsState =
    snapshot.currentGameId === "rock-paper-scissors"
      ? (snapshot.gameState as unknown as RockPaperScissorsPublicState | null)
      : null;
  const snakesLaddersState =
    snapshot.currentGameId === "snakes-ladders"
      ? (snapshot.gameState as unknown as SnakesLaddersState | null)
      : null;
  const mafiaState =
    snapshot.currentGameId === "mafia"
      ? (snapshot.gameState as unknown as MafiaPublicState | null)
      : null;
  const mafiaChatLockedReason =
    mafiaState?.phase === "night"
      ? "The town is asleep. Chat reopens at dawn."
      : mafiaState && !mafiaState.aliveIds.includes(stored.participantId || stored.snapshot.hostId)
        ? "You can observe the case, but eliminated players cannot influence it."
        : null;
  const publishTyping = (isTyping: boolean) => {
    if (typingStopTimer.current) {
      window.clearTimeout(typingStopTimer.current);
      typingStopTimer.current = null;
    }
    if (typingPublished.current !== isTyping) {
      if (connection.current?.send("chat.typing", { isTyping })) typingPublished.current = isTyping;
    }
    if (isTyping)
      typingStopTimer.current = window.setTimeout(() => {
        if (typingPublished.current && connection.current?.send("chat.typing", { isTyping: false }))
          typingPublished.current = false;
      }, 1_200);
  };
  const sendChatText = (value: string) => {
    const text = value.trim();
    if (!text) return;
    const id = connection.current?.sendTracked("chat.message", { text });
    if (!id) return;
    const viewerId = stored.participantId || stored.snapshot.hostId;
    setChat((items) => [
      ...items,
      {
        id,
        senderId: viewerId,
        text,
        at: Date.now(),
        delivery: "sending",
        receipts: {},
      },
    ]);
    trackEvent("chat_message_sent", { game_id: stored.snapshot.currentGameId ?? "lobby" });
    publishTyping(false);
    setDraft("");
  };
  const sendChat = () => sendChatText(draft);
  const removeInactiveParticipant = (targetId: string) => {
    if (inactiveRemovalPending) return;
    setInactiveRemovalPending(targetId);
    const sent = connection.current?.send("moderation.action", {
      action: "remove_inactive",
      targetId,
    });
    if (!sent) {
      setInactiveRemovalPending(null);
      setConnectionState((value) => ({
        ...value,
        error: "Reconnect to the room before removing an inactive player.",
      }));
    }
  };
  const watchLocalMediaStream = (kind: "microphone" | "camera", stream: MediaStream) => {
    const track = kind === "microphone" ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
    if (!track) return;
    try {
      track.contentHint = kind === "microphone" ? "speech" : "motion";
    } catch {
      // Older Safari versions expose contentHint as read-only. Capture remains usable.
    }
    const pauseMessage =
      kind === "camera"
        ? "The browser paused your camera. On iPhone, close other tabs using the camera and return to Topzle."
        : "The browser paused your microphone. Close other tabs using it and return to Topzle.";
    let pauseTimer: ReturnType<typeof setTimeout> | null = null;
    track.addEventListener("mute", () => {
      const current =
        kind === "microphone" ? localAudioStream.current : localVideoStreamRef.current;
      if (current !== stream) return;
      if (pauseTimer) return;
      pauseTimer = setTimeout(() => {
        pauseTimer = null;
        if (track.muted) setConnectionState((value) => ({ ...value, error: pauseMessage }));
      }, 1_500);
    });
    track.addEventListener("unmute", () => {
      if (pauseTimer) clearTimeout(pauseTimer);
      pauseTimer = null;
      const current =
        kind === "microphone" ? localAudioStream.current : localVideoStreamRef.current;
      if (current !== stream) return;
      setConnectionState((value) => ({
        ...value,
        error: value.error === pauseMessage ? null : value.error,
      }));
      void roomCall.current
        ?.publish(
          localAudioStream.current?.getAudioTracks()[0],
          localVideoStreamRef.current?.getVideoTracks()[0],
        )
        .catch(() => {
          // The call manager keeps retrying the desired tracks after Safari resumes them.
        });
    });
    track.addEventListener(
      "ended",
      () => {
        if (pauseTimer) clearTimeout(pauseTimer);
        pauseTimer = null;
        const current =
          kind === "microphone" ? localAudioStream.current : localVideoStreamRef.current;
        if (current !== stream) return;
        if (kind === "microphone") {
          localAudioStream.current = null;
          setLocalAudioMediaStream(null);
          const state = useAppStore.getState();
          if (state.microphone) state.toggleMicrophone();
        } else {
          localVideoStreamRef.current = null;
          setLocalVideoStream(null);
          const state = useAppStore.getState();
          if (state.camera) state.toggleCamera();
        }
        void roomCall.current
          ?.publish(
            localAudioStream.current?.getAudioTracks()[0],
            localVideoStreamRef.current?.getVideoTracks()[0],
          )
          .catch(() => {
            // CloudflareRoomCall keeps the remaining live track and retries it.
          });
      },
      { once: true },
    );
  };
  const requestMedia = async (kind: "microphone" | "camera") => {
    if (!localMediaAllowed) {
      if (!mediaAccessDecision) await refreshMediaAccess();
      setCallAccessOpen(true);
      return;
    }
    if (mediaPending) return;
    const enabled =
      kind === "microphone"
        ? microphone && Boolean(localAudioStream.current)
        : camera && Boolean(localVideoStreamRef.current);
    const startMicrophoneWithFirstVideo =
      kind === "camera" &&
      !hasStartedVideo.current &&
      !localAudioStream.current?.getAudioTracks().some((track) => track.readyState === "live");
    let mediaChanged = false;
    setMediaPending(kind);
    try {
      if (enabled) {
        const stream =
          kind === "microphone" ? localAudioStream.current : localVideoStreamRef.current;
        if (kind === "microphone") {
          localAudioStream.current = null;
          setLocalAudioMediaStream(null);
          toggleMicrophone();
        } else {
          localVideoStreamRef.current = null;
          setLocalVideoStream(null);
          toggleCamera();
        }
        mediaChanged = true;
        stream?.getTracks().forEach((track) => {
          track.stop();
        });
        await roomCall.current?.publish(
          localAudioStream.current?.getAudioTracks()[0],
          localVideoStreamRef.current?.getVideoTracks()[0],
        );
        return;
      }
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: { ideal: 1 },
      };
      if (kind === "microphone") {
        const captureStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });
        const stream = new MediaStream(captureStream.getAudioTracks());
        const previous = localAudioStream.current;
        localAudioStream.current = null;
        previous?.getTracks().forEach((track) => {
          track.stop();
        });
        localAudioStream.current = stream;
        setLocalAudioMediaStream(stream);
        watchLocalMediaStream(kind, stream);
        if (!microphone) toggleMicrophone();
      } else {
        // Capture video on its own first. On iPhone a microphone failure in a combined
        // request can otherwise prevent an allowed camera from opening at all.
        const captureStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { ideal: 240 },
            height: { ideal: 240 },
            frameRate: { ideal: 12 },
            facingMode: { ideal: "user" },
            aspectRatio: { ideal: 1 },
          },
        });
        const stream = new MediaStream(captureStream.getVideoTracks());
        const previous = localVideoStreamRef.current;
        localVideoStreamRef.current = null;
        previous?.getTracks().forEach((track) => {
          track.stop();
        });
        localVideoStreamRef.current = stream;
        setLocalVideoStream(stream);
        watchLocalMediaStream(kind, stream);
        if (!camera) toggleCamera();
        hasStartedVideo.current = true;
        mediaChanged = true;
        void roomCall.current
          ?.publish(
            localAudioStream.current?.getAudioTracks()[0],
            localVideoStreamRef.current?.getVideoTracks()[0],
          )
          .catch(() => {
            // Keep the local preview responsive while the publisher retries.
          });
        if (startMicrophoneWithFirstVideo) {
          try {
            const audioCapture = await navigator.mediaDevices.getUserMedia({
              audio: audioConstraints,
              video: false,
            });
            const audioStream = new MediaStream(audioCapture.getAudioTracks());
            const previousAudio = localAudioStream.current;
            localAudioStream.current = null;
            previousAudio?.getTracks().forEach((track) => {
              track.stop();
            });
            localAudioStream.current = audioStream;
            setLocalAudioMediaStream(audioStream);
            watchLocalMediaStream("microphone", audioStream);
            if (!microphone) toggleMicrophone();
            trackEvent("call_media_changed", {
              media_type: "microphone",
              enabled: true,
              game_id: latestSnapshot.current?.currentGameId ?? "lobby",
            });
          } catch (error) {
            setConnectionState((value) => ({
              ...value,
              error:
                error instanceof DOMException && error.name === "NotAllowedError"
                  ? "Your camera is on. Microphone permission was declined; tap Unmute whenever you want to try again."
                  : "Your camera is on, but the microphone could not start. On iPhone, close other tabs using it or tap Unmute to retry.",
            }));
          }
        }
        await roomCall.current?.publish(
          localAudioStream.current?.getAudioTracks()[0],
          localVideoStreamRef.current?.getVideoTracks()[0],
        );
        return;
      }
      mediaChanged = true;
      await roomCall.current?.publish(
        localAudioStream.current?.getAudioTracks()[0],
        localVideoStreamRef.current?.getVideoTracks()[0],
      );
    } catch (error) {
      trackEvent("operation_failed", {
        operation: "call_media_change",
        game_id: latestSnapshot.current?.currentGameId ?? "lobby",
        route: "/room/:roomId",
      });
      setConnectionState((value) => ({
        ...value,
        error:
          error instanceof DOMException && error.name === "NotAllowedError"
            ? `${kind === "microphone" ? "Microphone" : "Camera"} permission was denied. You can keep playing without it.`
            : error instanceof DOMException && error.name === "NotReadableError"
              ? `${
                  kind === "microphone" ? "The microphone" : "The camera"
                } is already in use or could not start. On iPhone, close other tabs using it and try again.`
              : mediaChanged
                ? "The call connection was interrupted and is reconnecting. Your camera and microphone will stay on."
                : error instanceof Error
                  ? error.message
                  : "The room call could not start.",
      }));
    } finally {
      if (mediaChanged)
        trackEvent("call_media_changed", {
          media_type: kind,
          enabled: !enabled,
          game_id: latestSnapshot.current?.currentGameId ?? "lobby",
        });
      setMediaPending(null);
    }
  };
  const startGame = async (
    gameId: string,
    versusAi: boolean,
    restartClockSeconds?: number | null,
    challengerId?: string,
  ): Promise<boolean> => {
    if (gameStartPending) return false;
    const selectedPolicy =
      botPolicies[gameId]?.find((policy) => policy.id === pickerBotPolicy) ??
      defaultBotPolicy(gameId);
    const clockSeconds =
      restartClockSeconds !== undefined
        ? restartClockSeconds
        : gameId === "tic-tac-toe" || gameId === "chess"
          ? gameClockSeconds[gameId]
          : gameId === "draw-guess"
            ? drawGuessRoundSeconds
            : null;
    const existingLudoFastMode =
      latestSnapshot.current?.currentGameId === "ludo"
        ? Boolean((latestSnapshot.current.gameState as { fastMode?: boolean } | null)?.fastMode)
        : false;
    const selectedLudoFastMode = restartClockSeconds !== undefined ? existingLudoFastMode : false;
    pendingStart.current = { gameId, versusAi };
    setGameStartPending(`${gameId}:${versusAi ? "ai" : "group"}`);
    setGameError(null);
    setConnectionState((value) => ({ ...value, error: null }));
    try {
      const started = await api.startRoomGame(stored.roomId, {
        gameId,
        settings:
          gameId === "tic-tac-toe"
            ? { clockSeconds }
            : gameId === "chess"
              ? { clockSeconds }
              : gameId === "ludo"
                ? {
                    requireSixToLeave: true,
                    extraTurnOnSix: true,
                    fastMode: selectedLudoFastMode,
                    rollTimeSeconds: 30,
                    moveTimeSeconds: 30,
                  }
                : gameId === "draw-guess"
                  ? {
                      roundSeconds: clockSeconds ?? 60,
                      rounds:
                        snapshot.currentGameId === "draw-guess"
                          ? (drawGuessState?.totalRounds ?? drawGuessRounds)
                          : drawGuessRounds,
                    }
                  : gameId === "rock-paper-scissors"
                    ? { targetScore: 3, countdownSeconds: 3 }
                    : gameId === "battleship"
                      ? { botPolicy: selectedPolicy.id }
                      : {},
        versusAi,
        aiDifficulty: selectedPolicy.difficulty,
        aiPolicy: selectedPolicy.id,
        ...(challengerId ? { challengerId } : {}),
      });
      latestSnapshot.current = started.room;
      setConnectionState((value) => ({
        ...value,
        snapshot: started.room,
        error: null,
      }));
      updateRoomSnapshot(stored.roomId, started.room);
      recordGameStarted({
        messageId: started.event.id,
        roomId: stored.roomId,
        playerId: participantId,
        gameId,
        versusAi,
        startedAt: started.event.serverTimestamp ?? Date.now(),
      });
      if (!analyticsGameStartIds.current.has(started.event.id)) {
        analyticsGameStartIds.current.add(started.event.id);
        trackEvent(restartClockSeconds !== undefined ? "rematch_started" : "game_start", {
          game_id: gameId,
          versus_ai: versusAi,
          difficulty: versusAi ? selectedPolicy.difficulty : "none",
          bot_policy: versusAi ? selectedPolicy.id : "none",
          time_control_seconds: clockSeconds ?? 0,
          participant_count: started.room.participants.filter(
            (participant) => participant.connected && participant.role !== "spectator",
          ).length,
        });
      }
      const defaults = readGameSetupDefaults();
      defaults[gameId] = {
        botPolicy: selectedPolicy.id,
        clockSeconds,
        ...(gameId === "draw-guess" ? { rounds: drawGuessRounds } : {}),
      };
      window.localStorage.setItem(roomSetupDefaultsStorageKey, JSON.stringify(defaults));
      setPickerOpen(false);
      return true;
    } catch (error) {
      trackEvent("operation_failed", {
        operation: "game_start",
        game_id: gameId,
        route: "/room/:roomId",
      });
      const message =
        error instanceof Error ? error.message : "The game could not be started. Please try again.";
      setGameError(message);
      setConnectionState((value) => ({ ...value, error: message }));
      return false;
    } finally {
      pendingStart.current = null;
      if (startTimeout.current) clearTimeout(startTimeout.current);
      startTimeout.current = null;
      setGameStartPending(null);
    }
  };
  const participantId = stored.participantId || stored.snapshot.hostId;
  const aiPlaying =
    snapshot.participants.some((participant) => participant.role === "ai") ||
    ticState?.players.X.startsWith("ai:") === true ||
    ticState?.players.O.startsWith("ai:") === true ||
    chessState?.players.white.startsWith("ai:") === true ||
    chessState?.players.black.startsWith("ai:") === true ||
    ludoState?.players.some((player) => player.id.startsWith("ai:")) === true ||
    connectFourState?.players.red.startsWith("ai:") === true ||
    connectFourState?.players.yellow.startsWith("ai:") === true ||
    battleshipState?.players.some((playerId) => playerId.startsWith("ai:")) === true ||
    drawGuessState?.aiEnabled === true ||
    rockPaperScissorsState?.players.some((playerId) => playerId.startsWith("ai:")) === true ||
    snakesLaddersState?.players.some((player) => player.id.startsWith("ai:")) === true;
  const currentParticipant = snapshot.participants.find(
    (participant) => participant.id === participantId,
  );
  const localSafetyMode = currentParticipant?.safetyMode ?? "adult";
  const chatMode =
    localSafetyMode === "child" ? "read_only" : localSafetyMode === "teen" ? "quick" : "full";
  const typingNames = snapshot.participants
    .filter(
      (participant) => typingParticipantIds.has(participant.id) && participant.id !== participantId,
    )
    .map((participant) => participant.displayName);
  // Keep host controls available if roster metadata briefly lags behind the
  // authoritative game state after a reconnect.
  const gameStarterId =
    ticState?.players.X ??
    chessState?.players.white ??
    ludoState?.players[0]?.id ??
    connectFourState?.players.red ??
    battleshipState?.players[0] ??
    drawGuessState?.players[0] ??
    rockPaperScissorsState?.players[0] ??
    snakesLaddersState?.players[0]?.id ??
    null;
  const isHost =
    snapshot.hostId === participantId ||
    currentParticipant?.role === "host" ||
    currentParticipant?.role === "co_host" ||
    currentParticipant?.role === "moderator" ||
    gameStarterId === participantId;
  const connectedHumanParticipants = snapshot.participants.filter(
    (candidate) =>
      candidate.connected && candidate.role !== "ai" && !candidate.id.startsWith("ai:"),
  );
  const realParticipantCount = snapshot.participants.filter(
    (candidate) => candidate.role !== "ai" && !candidate.id.startsWith("ai:"),
  ).length;
  const botParticipantCount = snapshot.participants.filter(
    (candidate) => candidate.role === "ai" || candidate.id.startsWith("ai:"),
  ).length;
  const joinedFriends = connectedHumanParticipants.filter(
    (candidate) => candidate.id !== participantId,
  );
  const inactiveHumanParticipants = snapshot.participants.filter(
    (candidate) =>
      !candidate.connected && candidate.role !== "ai" && !candidate.id.startsWith("ai:"),
  );
  const abandonedGameTakeover =
    Boolean(snapshot.currentGameId) &&
    aiOpponentGameIds.has(snapshot.currentGameId ?? "") &&
    inactiveHumanParticipants.length > 0 &&
    connectedHumanParticipants.length === 1 &&
    connectedHumanParticipants[0]?.id === participantId;
  const canRestartGame = isHost || abandonedGameTakeover;
  const restartVersusAi = aiPlaying || abandonedGameTakeover;
  const joinedSpectator = snapshot.participants.find(
    (candidate) =>
      candidate.connected &&
      candidate.role === "spectator" &&
      !candidate.id.startsWith("ai:") &&
      candidate.id !== participantId &&
      !dismissedChallengerIds.has(candidate.id),
  );
  const humanMark =
    ticState?.players.X === participantId
      ? "X"
      : ticState?.players.O === participantId
        ? "O"
        : null;
  const humanTurn = humanMark !== null && ticState?.turn === humanMark;
  const ticThinkingParticipantId =
    ticState?.status === "playing" ? ticState.players[ticState.turn] : null;
  const chessActiveColor = chessState?.fen.split(" ")[1] === "b" ? "black" : "white";
  const chessThinkingParticipantId =
    chessState?.status === "playing" ? chessState?.players[chessActiveColor] : null;
  const ludoThinkingParticipantId =
    ludoState?.phase !== "finished" ? ludoState?.players[ludoState.turnIndex]?.id : null;
  const connectFourThinkingParticipantId =
    connectFourState?.status === "playing" ? connectFourState.players[connectFourState.turn] : null;
  const battleshipThinkingParticipantId =
    battleshipState?.phase === "playing"
      ? battleshipState.players[battleshipState.turnIndex]
      : battleshipState?.phase === "placement"
        ? battleshipState.players.find((playerId) => !battleshipState.readyIds.includes(playerId))
        : null;
  const snakesLaddersThinkingParticipantId =
    snakesLaddersState?.phase === "playing"
      ? snakesLaddersState.players[snakesLaddersState.turnIndex]?.id
      : null;
  const thinkingParticipantId =
    [
      ticThinkingParticipantId,
      chessThinkingParticipantId,
      ludoThinkingParticipantId,
      connectFourThinkingParticipantId,
      battleshipThinkingParticipantId,
      snakesLaddersThinkingParticipantId,
    ].find((id) => id?.startsWith("ai:")) ?? null;
  const selectedGame = gameCatalog.find((game) => game.id === selectedGameId) ?? gameCatalog[0];
  const selectedMinimumPlayers = Math.max(
    2,
    Number.parseInt(selectedGame?.playerCount.split(/[–-]/)[0] ?? "2", 10) || 2,
  );
  const requiredJoinedFriends = selectedMinimumPlayers - 1;
  const remainingJoinedFriends = Math.max(0, requiredJoinedFriends - joinedFriends.length);
  const joinedFriendsReady = remainingJoinedFriends === 0;
  const remainingFriendsCopy =
    remainingJoinedFriends === 1
      ? "one more friend joins"
      : `${remainingJoinedFriends} more friends join`;
  const selectedGameReady =
    connectionState.status === "connected" &&
    (Boolean(selectedGame?.ai && playWithBot) || joinedFriendsReady);
  const inviteUrl = `${window.location.origin}/join/${snapshot.code}`;
  const inviteText = `Join my ${selectedGame?.name ?? "Topzle"} room on Topzle: ${inviteUrl}`;
  const queuedGame = gameCatalog.find((game) => game.id === snapshot.settings.selectedGameId);
  const selectedTimeControls = gameTimeControls[selectedGameId as PlayableRoomGameId];
  const selectedClockSeconds =
    selectedGameId === "tic-tac-toe" || selectedGameId === "chess"
      ? gameClockSeconds[selectedGameId]
      : selectedGameId === "draw-guess"
        ? drawGuessRoundSeconds
        : null;
  const currentClockSeconds =
    snapshot.currentGameId === "tic-tac-toe"
      ? (ticState?.initialClockSeconds ?? null)
      : snapshot.currentGameId === "chess"
        ? (chessState?.initialClockSeconds ?? null)
        : snapshot.currentGameId === "draw-guess"
          ? drawGuessState
            ? Math.round((drawGuessState.deadline - drawGuessState.roundStartedAt) / 1000)
            : 60
          : null;
  const copyInvite = async (method = "copy_link") => {
    if (copyPending) return;
    setCopyPending(true);
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/join/${snapshot.code}`);
      setInviteCopied(true);
      trackEvent("invite_shared", {
        method,
        game_id: snapshot.currentGameId ?? selectedGameId,
      });
      window.setTimeout(() => setInviteCopied(false), 2_000);
    } catch {
      setConnectionState((value) => ({
        ...value,
        error: "The invite could not be copied. Share the room code instead.",
      }));
    } finally {
      setCopyPending(false);
    }
  };
  const markInviteShared = (method: "whatsapp" | "sms" | "email") => {
    setInviteFriendsOpen(true);
    setInviteShareStarted(true);
    trackEvent("invite_shared", {
      method,
      game_id: snapshot.currentGameId ?? selectedGameId,
    });
  };
  const shareInvite = async () => {
    setInviteFriendsOpen(true);
    setInviteShareStarted(true);
    if (!navigator.share) {
      await copyInvite("native_share_fallback");
      return;
    }
    try {
      await navigator.share({
        title: `Join my ${selectedGame?.name ?? "Topzle"} game`,
        text: `Come play ${selectedGame?.name ?? "a game"} with me on Topzle.`,
        url: inviteUrl,
      });
      trackEvent("invite_shared", {
        method: "native_share",
        game_id: snapshot.currentGameId ?? selectedGameId,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setConnectionState((value) => ({
        ...value,
        error: "The share menu could not open. Copy the invite link instead.",
      }));
    }
  };
  const openGameCatalog = () => {
    setSelectedGameId(snapshot.currentGameId ?? selectedGameId);
    setPickerMode("catalog");
    setHeaderPanel(null);
    setPickerOpen(true);
  };
  const gameFinished =
    (ticState && ticState.status !== "playing") ||
    (chessState && chessState.status !== "playing") ||
    (ludoState && ludoState.phase === "finished") ||
    (connectFourState && connectFourState.status !== "playing") ||
    (battleshipState && battleshipState.phase === "finished") ||
    (drawGuessState && drawGuessState.phase === "finished") ||
    (rockPaperScissorsState && rockPaperScissorsState.phase === "finished") ||
    (snakesLaddersState && snakesLaddersState.phase === "finished");
  const currentGamePlayerIds = new Set(
    ticState
      ? Object.values(ticState.players)
      : chessState
        ? Object.values(chessState.players)
        : ludoState
          ? ludoState.players.map((player) => player.id)
          : connectFourState
            ? Object.values(connectFourState.players)
            : battleshipState
              ? battleshipState.players
              : drawGuessState
                ? drawGuessState.players
                : rockPaperScissorsState
                  ? rockPaperScissorsState.players
                  : snakesLaddersState
                    ? snakesLaddersState.players.map((player) => player.id)
                    : [],
  );
  const canStartNextRound =
    Boolean(gameFinished) &&
    currentGamePlayerIds.has(participantId) &&
    !participantId.startsWith("ai:");
  const startNextRound = () => {
    if (!snapshot.currentGameId || gameStartPending !== null) return;
    setRestartConfirmOpen(false);
    setRoundComplete(null);
    setCelebration(null);
    setSelectedGameId(snapshot.currentGameId);
    const saved = readGameSetupDefaults()[snapshot.currentGameId];
    const policy =
      botPolicies[snapshot.currentGameId]?.find(
        (candidate) => candidate.id === (saved?.botPolicy ?? snapshot.settings.aiPolicy),
      ) ?? defaultBotPolicy(snapshot.currentGameId);
    setPickerBotPolicy(policy.id);
    setPlayWithBot(false);
    if (
      (snapshot.currentGameId === "tic-tac-toe" || snapshot.currentGameId === "chess") &&
      currentClockSeconds !== undefined
    )
      setGameClockSeconds((value) => ({
        ...value,
        [snapshot.currentGameId as "tic-tac-toe" | "chess"]: currentClockSeconds,
      }));
    setPickerMode("setup");
    setPickerOpen(true);
  };
  return (
    <section className="room-shell">
      <header className="room-header">
        <div className="room-header-branding">
          <button
            type="button"
            className="icon-button room-back"
            aria-label="Back to dashboard"
            title="Back to dashboard"
            onClick={() => navigate("/app")}
          >
            <ArrowLeft />
          </button>
          <Link className="room-brand" to="/app" aria-label="Topzle dashboard">
            <BrandMark />
            <b>Topzle</b>
          </Link>
          <button
            type="button"
            className="room-code"
            onClick={() => void copyInvite()}
            disabled={copyPending}
            title="Copy room invite"
          >
            {copyPending ? <LoaderCircle className="spin" /> : inviteCopied ? <Check /> : null}
            {inviteCopied ? "COPIED" : snapshot.code}
          </button>
          <div className="room-title">
            <strong>{snapshot.settings.title}</strong>
            <small>
              <span className={`connection-dot ${connectionState.status}`} />{" "}
              {connectionState.status} · {connectedHumanParticipants.length} online
            </small>
          </div>
        </div>
        <div className="room-header-actions">
          <button
            type="button"
            className={[
              "icon-button",
              "participants-button",
              headerPanel === "participants" ? "active" : "",
              participantsPulse ? "participant-joined" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={`${realParticipantCount} real ${realParticipantCount === 1 ? "participant" : "participants"} · ${botParticipantCount} ${botParticipantCount === 1 ? "bot" : "bots"}`}
            aria-label={`${realParticipantCount} real ${realParticipantCount === 1 ? "participant" : "participants"} and ${botParticipantCount} ${botParticipantCount === 1 ? "bot" : "bots"}`}
            aria-expanded={headerPanel === "participants"}
            onClick={() =>
              setHeaderPanel((panel) => (panel === "participants" ? null : "participants"))
            }
          >
            <Users />
            <span className="participant-counts" aria-hidden="true">
              <b>{realParticipantCount}</b>
              <small>{botParticipantCount} AI</small>
            </span>
          </button>
          <button
            type="button"
            className={headerPanel === "settings" ? "icon-button active" : "icon-button"}
            title="Room settings"
            aria-expanded={headerPanel === "settings"}
            onClick={() => setHeaderPanel((panel) => (panel === "settings" ? null : "settings"))}
          >
            <Settings2 />
          </button>
          <button
            type="button"
            className={headerPanel === "more" ? "icon-button active" : "icon-button"}
            title="More room options"
            aria-label="More room options"
            aria-expanded={headerPanel === "more"}
            onClick={() => setHeaderPanel((panel) => (panel === "more" ? null : "more"))}
          >
            <MoreHorizontal />
          </button>
        </div>
      </header>
      {headerPanel && (
        <>
          <button
            type="button"
            className="room-panel-dismiss"
            aria-label="Close room menu"
            onClick={() => setHeaderPanel(null)}
          />
          <section className="room-header-panel" aria-label={`${headerPanel} menu`}>
            <div className="room-header-panel-title">
              <strong>
                {headerPanel === "participants"
                  ? "People in this room"
                  : headerPanel === "settings"
                    ? "Room controls"
                    : "More options"}
              </strong>
              <button
                type="button"
                className="icon-button"
                aria-label="Close room menu"
                onClick={() => setHeaderPanel(null)}
              >
                <X />
              </button>
            </div>
            {headerPanel === "participants" &&
              snapshot.participants.map((participant) => {
                const avatarHidden = hiddenAvatarIds.has(participant.id);
                return (
                  <div className="room-person" key={participant.id}>
                    <span>{participant.displayName.slice(0, 1)}</span>
                    <div>
                      <strong>
                        {participant.displayName} {flagForCountry(participant.countryCode)}
                      </strong>
                      <small>
                        {participant.role.replace("_", " ")} ·{" "}
                        {participant.connected
                          ? participant.activeInRoom === false
                            ? "online · away from room"
                            : "active in room"
                          : "offline"}
                        {avatarHidden ? " · avatar hidden" : ""}
                      </small>
                    </div>
                    <div className="room-person-actions">
                      {participant.microphone ? <Mic /> : <MicOff />}
                      <button
                        type="button"
                        aria-label={`${avatarHidden ? "Show" : "Hide"} ${participant.id === participantId ? "your" : `${participant.displayName}'s`} avatar`}
                        title={avatarHidden ? "Show avatar" : "Hide avatar"}
                        onClick={() =>
                          setHiddenAvatarIds((current) => {
                            const next = new Set(current);
                            if (avatarHidden) next.delete(participant.id);
                            else next.add(participant.id);
                            return next;
                          })
                        }
                      >
                        {avatarHidden ? <Eye /> : <EyeOff />}
                      </button>
                    </div>
                  </div>
                );
              })}
            {headerPanel === "settings" && (
              <div className="room-menu-actions">
                <button
                  type="button"
                  onClick={() => {
                    setChatOpen((open) => !open);
                    setHeaderPanel(null);
                  }}
                >
                  <MessageCircle />
                  <span>
                    <strong>{chatOpen ? "Hide chat" : "Open chat"}</strong>
                    <small>Chat floats without covering the whole game</small>
                  </span>
                </button>
                <button type="button" onClick={openGameCatalog}>
                  <Gamepad2 />
                  <span>
                    <strong>Switch game</strong>
                    <small>Choose another room game</small>
                  </span>
                </button>
                <button
                  type="button"
                  aria-pressed={audioNotificationsEnabled}
                  onClick={() => setAudioNotificationsEnabled((enabled) => !enabled)}
                >
                  {audioNotificationsEnabled ? <Volume2 /> : <VolumeX />}
                  <span>
                    <strong>
                      {audioNotificationsEnabled
                        ? "Audio notifications on"
                        : "Audio notifications off"}
                    </strong>
                    <small>Sounds for your turn and when a player joins</small>
                  </span>
                </button>
              </div>
            )}
            {headerPanel === "more" && (
              <div className="room-menu-actions">
                <button
                  type="button"
                  onClick={() => {
                    const gameId = snapshot.currentGameId ?? selectedGameId;
                    window.open(`/how-to-play/${gameId}`, "_blank", "noopener,noreferrer");
                    setHeaderPanel(null);
                  }}
                >
                  <BookOpen />
                  <span>
                    <strong>How to play</strong>
                    <small>Open this game’s rules and tips</small>
                  </span>
                </button>
                <button type="button" onClick={() => void copyInvite()} disabled={copyPending}>
                  {copyPending ? (
                    <LoaderCircle className="spin" />
                  ) : inviteCopied ? (
                    <Check />
                  ) : (
                    <Copy />
                  )}
                  <span>
                    <strong>{inviteCopied ? "Invite copied" : "Copy invite"}</strong>
                    <small>Room code {snapshot.code}</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHeaderPanel(null);
                    setSupportKind("feedback");
                  }}
                >
                  <MessageSquareText />
                  <span>
                    <strong>Send feedback</strong>
                    <small>Share an idea about this room or game</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHeaderPanel(null);
                    setSupportKind("issue");
                  }}
                >
                  <Bug />
                  <span>
                    <strong>Report issue</strong>
                    <small>Attach this room and device context</small>
                  </span>
                </button>
                <button type="button" onClick={() => navigate("/app")}>
                  <PhoneOff />
                  <span>
                    <strong>Leave room</strong>
                    <small>Return to your dashboard</small>
                  </span>
                </button>
              </div>
            )}
          </section>
        </>
      )}
      {connectionState.error && (
        <div className="room-error">
          {connectionState.error}
          <button
            type="button"
            onClick={() => setConnectionState((value) => ({ ...value, error: null }))}
          >
            <X />
          </button>
        </div>
      )}
      {supportKind && (
        <SupportDialog
          kind={supportKind}
          context={{
            source: "room",
            roomId: snapshot.roomId,
            roomCode: snapshot.code,
            gameId: snapshot.currentGameId ?? selectedGameId,
          }}
          onClose={() => setSupportKind(null)}
        />
      )}
      {presenceToasts.length > 0 && (
        <div className="room-presence-toasts" aria-live="polite">
          {presenceToasts.map((toast) => (
            <div className={`presence-toast ${toast.state}`} key={toast.id}>
              <span className="presence-toast-avatar" aria-hidden="true">
                {toast.displayName.slice(0, 1).toUpperCase()}
              </span>
              <span>
                <strong>
                  {toast.state === "inactive"
                    ? `${toast.displayName} is inactive`
                    : `${toast.displayName} was removed`}
                </strong>
                <small>
                  {toast.state === "inactive"
                    ? "Their avatar is dimmed. Remove them if the game needs to continue."
                    : "The room and active game were updated."}
                </small>
              </span>
              {toast.state === "inactive" && (
                <button
                  type="button"
                  className="presence-toast-remove"
                  disabled={inactiveRemovalPending !== null}
                  onClick={() => removeInactiveParticipant(toast.participantId)}
                >
                  {inactiveRemovalPending === toast.participantId ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <UserMinus />
                  )}
                  {inactiveRemovalPending === toast.participantId ? "Removing…" : "Remove"}
                </button>
              )}
              <button
                type="button"
                className="presence-toast-close"
                aria-label="Dismiss notification"
                onClick={() =>
                  setPresenceToasts((items) => items.filter((item) => item.id !== toast.id))
                }
              >
                <X />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={chatOpen ? "room-content chat-visible" : "room-content"}>
        <div className="game-area">
          <ParticipantBubbles
            roomId={snapshot.roomId}
            participants={snapshot.participants}
            participantId={participantId}
            localCamera={camera}
            localMicrophone={microphone}
            localAudioStream={localAudioMediaStream}
            localVideoStream={localVideoStream}
            remoteMediaStreams={remoteMediaStreams}
            mediaPending={mediaPending}
            localMediaAllowed={localMediaAllowed}
            thinkingParticipantId={thinkingParticipantId}
            hiddenAvatarIds={hiddenAvatarIds}
            onToggleLocalMedia={(kind) => void requestMedia(kind)}
            onHideAvatar={(hiddenParticipantId) =>
              setHiddenAvatarIds((current) => new Set(current).add(hiddenParticipantId))
            }
            onMediaPreferencesChange={applyMediaPreferences}
            inactiveRemovalPending={inactiveRemovalPending}
            onRemoveInactive={removeInactiveParticipant}
          />
          {abandonedGameTakeover && snapshot.currentGameId && (
            <div className="inactive-takeover-card" role="status">
              <div>
                <span className="eyebrow">YOU’RE THE ONLY ACTIVE PLAYER</span>
                <strong>
                  {inactiveHumanParticipants.length} inactive{" "}
                  {inactiveHumanParticipants.length === 1 ? "player" : "players"}
                </strong>
                <small>Remove them and restart this game against Topzle AI.</small>
              </div>
              <button
                type="button"
                className="button button-primary"
                disabled={gameStartPending !== null}
                onClick={() => setRestartConfirmOpen(true)}
              >
                {gameStartPending ? <LoaderCircle className="spin" /> : <UserMinus />}
                {gameStartPending ? "Restarting…" : "Remove & play AI"}
              </button>
            </div>
          )}
          {snapshot.currentGameId && aiPlaying && isHost && joinedSpectator && (
            <div className="ai-arrival-card" role="status">
              <div>
                <span className="eyebrow">A PLAYER JOINED</span>
                <strong>{joinedSpectator.displayName} is watching</strong>
                <small>
                  Keep playing Topzle AI, or start a fresh{" "}
                  {gameCatalog.find((game) => game.id === snapshot.currentGameId)?.name ?? "game"}{" "}
                  with them.
                </small>
              </div>
              <button
                type="button"
                className="button button-primary"
                disabled={gameStartPending !== null}
                onClick={() =>
                  void startGame(
                    snapshot.currentGameId ?? "",
                    false,
                    currentClockSeconds,
                    joinedSpectator.id,
                  )
                }
              >
                {gameStartPending ? <LoaderCircle className="spin" /> : <Users />}
                {gameStartPending ? "Starting…" : `Play with ${joinedSpectator.displayName}`}
              </button>
              <button
                type="button"
                className="ai-arrival-dismiss"
                aria-label="Keep playing with Topzle AI"
                title="Keep playing with Topzle AI"
                onClick={() =>
                  setDismissedChallengerIds((dismissed) => {
                    const next = new Set(dismissed);
                    next.add(joinedSpectator.id);
                    return next;
                  })
                }
              >
                <X />
              </button>
            </div>
          )}
          {snapshot.currentGameId && aiPlaying && currentParticipant?.role === "spectator" && (
            <div className="spectator-notice" role="status">
              <Eye />
              <span>
                <strong>Spectating live</strong>
                <small>
                  The host is playing Topzle AI. You can watch now, and they can start a fresh match
                  with you.
                </small>
              </span>
            </div>
          )}
          {snapshot.currentGameId &&
          gameStyleLoaders[snapshot.currentGameId as PlayableRoomGameId] &&
          styledGameId !== snapshot.currentGameId ? (
            <div className="game-empty" role="status">
              <LoaderCircle className="spin" />
              <p>
                Preparing {gameCatalog.find((game) => game.id === snapshot.currentGameId)?.name}…
              </p>
            </div>
          ) : snapshot.currentGameId === "tic-tac-toe" && ticState?.board ? (
            <div className="active-game tic-tac-toe-game">
              <div className="game-status">
                <span>{aiPlaying ? "YOU VS TOPZLE AI" : "ROUND 01"}</span>
                <strong>
                  {ticState.status === "playing"
                    ? humanTurn
                      ? "Your turn"
                      : aiPlaying
                        ? "Topzle AI's turn"
                        : `${ticState.turn}'s turn`
                    : ticState.finishReason === "timeout"
                      ? `${ticState.winner} wins on time`
                      : "Round complete"}
                </strong>
              </div>
              {ticState.clocks && ticState.lastMoveAt && (
                <GameClocks
                  entries={[
                    { label: "X", seconds: ticState.clocks.X, active: ticState.turn === "X" },
                    { label: "O", seconds: ticState.clocks.O, active: ticState.turn === "O" },
                  ]}
                  lastMoveAt={ticState.lastMoveAt}
                  running={ticState.status === "playing"}
                />
              )}
              <div className="ttt-board">
                {ticState.board.map((mark, index) => (
                  <button
                    type="button"
                    className={[
                      mark ? `mark ${mark.toLowerCase()}` : "",
                      ticState.winningLine?.includes(index) ? "winning-cell" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={
                      Boolean(mark) ||
                      ticState.status !== "playing" ||
                      !humanTurn ||
                      gameActionPending
                    }
                    onClick={() => {
                      if (connection.current?.send("game.action", { type: "place", index }))
                        setGameActionPending(true);
                    }}
                    key={
                      [
                        "top-left",
                        "top-middle",
                        "top-right",
                        "middle-left",
                        "center",
                        "middle-right",
                        "bottom-left",
                        "bottom-middle",
                        "bottom-right",
                      ][index]
                    }
                  >
                    {mark ? <TicMark mark={mark} /> : null}
                  </button>
                ))}
              </div>
              {ticState.status !== "playing" && !resultRevealPending && (
                <PlayAgainAction
                  canStart={canStartNextRound}
                  pending={gameStartPending !== null}
                  onClick={startNextRound}
                />
              )}
              {aiPlaying && isHost && (
                <button
                  type="button"
                  className="text-link end-ai-game"
                  onClick={() => connection.current?.send("game.ended", { reason: "host_ended" })}
                >
                  End AI game
                </button>
              )}
            </div>
          ) : snapshot.currentGameId === "chess" && chessState ? (
            <ChessBoard
              key={chessState.fen}
              state={chessState}
              participantId={participantId}
              aiPlaying={aiPlaying}
              pending={gameActionPending}
              serverError={gameError}
              playAgain={
                chessState.status !== "playing" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onMove={(from, to, promotion) => {
                setGameError(null);
                if (
                  connection.current?.send("game.action", {
                    type: "move",
                    from,
                    to,
                    ...(promotion ? { promotion } : {}),
                  })
                )
                  setGameActionPending(true);
              }}
            />
          ) : snapshot.currentGameId === "ludo" && ludoState ? (
            <LudoBoard
              state={ludoState}
              participantId={participantId}
              participants={snapshot.participants}
              aiPlaying={aiPlaying}
              aiDifficulty={snapshot.settings.aiDifficulty ?? "medium"}
              isHost={isHost}
              settingsPending={gameActionPending}
              serverError={gameError}
              playAgain={
                ludoState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onAction={(action) => {
                const sent = Boolean(connection.current?.send("game.action", action));
                if (sent) setGameActionPending(true);
                return sent;
              }}
            />
          ) : snapshot.currentGameId === "connect-four" && connectFourState ? (
            <ConnectFourBoard
              state={connectFourState}
              participantId={participantId}
              pending={gameActionPending}
              serverError={gameError}
              playAgain={
                connectFourState.status !== "playing" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onDrop={(column) => {
                setGameError(null);
                if (connection.current?.send("game.action", { type: "drop", column }))
                  setGameActionPending(true);
              }}
            />
          ) : snapshot.currentGameId === "battleship" && battleshipState ? (
            <BattleshipBoard
              state={battleshipState}
              participantId={participantId}
              pending={gameActionPending}
              serverError={gameError}
              playAgain={
                battleshipState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onAction={(action) => {
                setGameError(null);
                if (connection.current?.send("game.action", action)) setGameActionPending(true);
              }}
            />
          ) : snapshot.currentGameId === "draw-guess" && drawGuessState ? (
            <DrawGuessBoard
              state={drawGuessState}
              participantId={participantId}
              participants={snapshot.participants}
              aiDifficulty={snapshot.settings.aiDifficulty ?? "medium"}
              serverError={gameError}
              playAgain={
                drawGuessState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onAction={(action) => {
                const sent = Boolean(connection.current?.send("game.action", action));
                if (sent) setGameActionPending(true);
                return sent;
              }}
            />
          ) : snapshot.currentGameId === "rock-paper-scissors" && rockPaperScissorsState ? (
            <RockPaperScissorsBoard
              state={rockPaperScissorsState}
              participantId={participantId}
              participants={snapshot.participants}
              localVideoStream={localVideoStream}
              remoteMediaStreams={remoteMediaStreams}
              cameraPending={mediaPending === "camera"}
              pending={gameActionPending}
              serverError={gameError}
              playAgain={
                rockPaperScissorsState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onEnableCamera={() => void requestMedia("camera")}
              onAction={(action) => {
                setGameError(null);
                const sent = Boolean(connection.current?.send("game.action", action));
                if (sent) setGameActionPending(true);
                return sent;
              }}
            />
          ) : snapshot.currentGameId === "snakes-ladders" && snakesLaddersState ? (
            <SnakesLaddersBoard
              state={snakesLaddersState}
              participantId={participantId}
              participants={snapshot.participants}
              pending={gameActionPending}
              isHost={isHost}
              settingsPending={gameActionPending}
              serverError={gameError}
              playAgain={
                snakesLaddersState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onRoll={() => {
                setGameError(null);
                const sent = Boolean(connection.current?.send("game.action", { type: "roll" }));
                if (sent) setGameActionPending(true);
                return sent;
              }}
              onFastModeChange={(enabled) => {
                setGameError(null);
                const sent = Boolean(
                  connection.current?.send("game.action", { type: "set_fast_mode", enabled }),
                );
                if (sent) setGameActionPending(true);
                return sent;
              }}
              onMovePresentationComplete={(moveNonce) => {
                const pendingAnnouncement = pendingSnakesResultAnnouncement.current;
                if (!pendingAnnouncement || pendingAnnouncement.moveNonce !== moveNonce) return;
                pendingSnakesResultAnnouncement.current = null;
                if (resultRevealTimer.current !== null)
                  window.clearTimeout(resultRevealTimer.current);
                resultRevealTimer.current = window.setTimeout(pendingAnnouncement.announce, 1_200);
              }}
            />
          ) : snapshot.currentGameId === "mafia" && mafiaState ? (
            <MafiaBoard
              state={mafiaState}
              participantId={participantId}
              participants={snapshot.participants}
              pending={gameActionPending}
              serverError={gameError}
              playAgain={
                mafiaState.phase === "finished" && !resultRevealPending ? (
                  <PlayAgainAction
                    canStart={canStartNextRound}
                    pending={gameStartPending !== null}
                    onClick={startNextRound}
                  />
                ) : null
              }
              onAction={(action) => {
                setGameError(null);
                const sent = Boolean(connection.current?.send("game.action", action));
                if (sent) setGameActionPending(true);
                return sent;
              }}
            />
          ) : (
            <div className="game-empty">
              <span className="game-spark">
                <Sparkles />
              </span>
              <span className="eyebrow">{queuedGame ? "GAME SELECTED" : "THE ROOM IS READY"}</span>
              <h1>{queuedGame ? `${queuedGame.name} is ready` : "What are we playing?"}</h1>
              <p>
                {queuedGame
                  ? isHost
                    ? "Choose the match settings when you’re ready to begin."
                    : "The host is choosing the match settings. You’ll join automatically when it starts."
                  : isHost
                    ? "Pick a game to get everyone started."
                    : "The host will choose a game shortly."}
              </p>
              {isHost && (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => {
                    if (queuedGame) {
                      setSelectedGameId(queuedGame.id);
                      setPickerMode("setup");
                    }
                    setPickerOpen(true);
                  }}
                >
                  <Gamepad2 /> {queuedGame ? `Set up ${queuedGame.name}` : "Pick a game"}
                </button>
              )}
            </div>
          )}
        </div>
        {!chatOpen && showNewMessagesOnScreen && floatingChatMessages.length > 0 && (
          <div className="floating-chat-stack" aria-live="polite">
            {floatingChatMessages.map((item) => {
              const sender = snapshot.participants.find(
                (participant) => participant.id === item.senderId,
              );
              return (
                <div className="floating-chat-message" key={item.id}>
                  <span>{sender?.displayName.slice(0, 1) ?? "Z"}</span>
                  <div>
                    <strong>{sender?.displayName ?? "Topzle"}</strong>
                    <p>{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {chatOpen && (
          <aside className="room-chat" id="room-chat" aria-label="Room chat">
            <div className="chat-head">
              <div>
                <strong>Room chat</strong>
                <small>{snapshot.participants.length} people</small>
              </div>
              <label className="chat-overlay-setting">
                <input
                  type="checkbox"
                  checked={showNewMessagesOnScreen}
                  onChange={(event) => setShowNewMessagesOnScreen(event.target.checked)}
                />
                <Settings2 />
                <span>Show new message on screen</span>
              </label>
              <button
                type="button"
                className="icon-button"
                aria-label="Close chat and show the full game"
                title="Close chat"
                onClick={() => setChatOpen(false)}
              >
                <X />
              </button>
            </div>
            <div className="messages" ref={chatScrollRef} aria-live="polite">
              {chat.map((item) => {
                const sender = snapshot.participants.find(
                  (participant) => participant.id === item.senderId,
                );
                const receiptValues = Object.values(item.receipts ?? {});
                const receiptStatus = receiptValues.includes("read")
                  ? "read"
                  : receiptValues.includes("delivered")
                    ? "delivered"
                    : item.delivery;
                return (
                  <div
                    className={
                      item.kind === "result"
                        ? "message result-message"
                        : item.senderId === (stored.participantId || stored.snapshot.hostId)
                          ? "message own"
                          : "message"
                    }
                    key={item.id}
                  >
                    <span>
                      {item.kind === "result" ? (
                        <Trophy />
                      ) : (
                        (sender?.displayName.slice(0, 1) ?? "Z")
                      )}
                    </span>
                    <div>
                      <small>
                        {item.kind === "result" ? "Game result" : (sender?.displayName ?? "Topzle")}{" "}
                        · {formatLocalTime(item.at)}
                      </small>
                      <p>{item.text}</p>
                      {item.senderId === participantId &&
                        item.kind !== "result" &&
                        receiptStatus && (
                          <small className={`message-receipt ${receiptStatus}`}>
                            {receiptStatus === "sending" || receiptStatus === "sent" ? (
                              <Check />
                            ) : (
                              <CheckCheck />
                            )}
                            {receiptStatus === "read"
                              ? "Read"
                              : receiptStatus === "delivered"
                                ? "Delivered"
                                : receiptStatus === "sending"
                                  ? "Sending"
                                  : "Sent"}
                          </small>
                        )}
                      {item.roomResultSummary && (
                        <p className="room-result-summary">
                          <strong>Room total</strong>
                          <span>{item.roomResultSummary}</span>
                        </p>
                      )}
                      {item.result && Object.keys(item.result.scores).length > 0 && (
                        <div className="result-scores">
                          {Object.entries(item.result.scores).map(([playerId, score]) => (
                            <span key={playerId}>
                              {participantName(playerId, snapshot, participantId)}
                              <b>{score}</b>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {typingNames.length > 0 && (
                <div className="chat-typing" role="status">
                  <span aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  {typingNames.length === 1
                    ? `${typingNames[0]} is typing`
                    : `${typingNames.slice(0, 2).join(" and ")} are typing`}
                </div>
              )}
            </div>
            {mafiaChatLockedReason ? (
              <div className="chat-safety-lock mafia-chat-lock">
                <MoonStar />
                <span>{mafiaChatLockedReason}</span>
              </div>
            ) : chatMode === "full" ? (
              <form
                className="chat-compose"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendChat();
                }}
              >
                <input
                  value={draft}
                  onChange={(event) => {
                    const value = event.target.value.slice(0, 1000);
                    setDraft(value);
                    publishTyping(Boolean(value.trim()));
                  }}
                  onBlur={() => publishTyping(false)}
                  placeholder="Message the room"
                />
                <button type="submit" aria-label="Send" disabled={!draft.trim()}>
                  <Send />
                </button>
              </form>
            ) : chatMode === "quick" ? (
              <fieldset className="teen-quick-chat">
                <legend className="sr-only">Safe quick chat</legend>
                <small>Choose a quick message</small>
                <div>
                  {teenQuickChat.map((message) => (
                    <button type="button" onClick={() => sendChatText(message)} key={message}>
                      {message}
                    </button>
                  ))}
                </div>
              </fieldset>
            ) : (
              <div className="chat-safety-lock">
                <ShieldCheck />
                <span>Chat is off for this account. Game results still appear here.</span>
              </div>
            )}
          </aside>
        )}
      </div>
      <div className="room-controls">
        <button
          type="button"
          className={
            microphone
              ? "control active"
              : localMediaAllowed
                ? "control"
                : "control access-required"
          }
          onClick={() => void requestMedia("microphone")}
          disabled={mediaPending !== null || mediaAccessDecision === null}
          aria-busy={mediaPending === "microphone" || undefined}
        >
          {mediaPending === "microphone" ? (
            <LoaderCircle className="spin" />
          ) : microphone ? (
            <Mic />
          ) : (
            <MicOff />
          )}
          <span>{mediaPending === "microphone" ? "Opening…" : microphone ? "Mute" : "Unmute"}</span>
        </button>
        <button
          type="button"
          className={
            camera ? "control active" : localMediaAllowed ? "control" : "control access-required"
          }
          onClick={() => void requestMedia("camera")}
          disabled={mediaPending !== null || mediaAccessDecision === null}
          aria-busy={mediaPending === "camera" || undefined}
        >
          {mediaPending === "camera" ? (
            <LoaderCircle className="spin" />
          ) : camera ? (
            <Camera />
          ) : (
            <CameraOff />
          )}
          <span>{mediaPending === "camera" ? "Opening…" : camera ? "Stop video" : "Video"}</span>
        </button>
        <button
          type="button"
          className={chatOpen ? "control chat-control active" : "control chat-control"}
          aria-controls="room-chat"
          aria-expanded={chatOpen}
          aria-label={
            unreadChatCount
              ? `Chat, ${unreadChatCount} unread ${unreadChatCount === 1 ? "message" : "messages"}`
              : chatOpen
                ? "Hide chat"
                : "Open chat"
          }
          onClick={() => setChatOpen(!chatOpen)}
        >
          <MessageCircle />
          {unreadChatCount > 0 && (
            <b className="chat-unread-badge" aria-hidden="true">
              {unreadChatCount}
            </b>
          )}
          <span>{chatOpen ? "Hide chat" : "Chat"}</span>
        </button>
        <button type="button" className="control" onClick={openGameCatalog}>
          <Gamepad2 />
          <span>Games</span>
        </button>
        {(gameFinished ? canStartNextRound : canRestartGame) && snapshot.currentGameId && (
          <button
            type="button"
            className="control"
            onClick={gameFinished ? startNextRound : () => setRestartConfirmOpen(true)}
            disabled={gameStartPending !== null}
          >
            {gameStartPending?.startsWith(`${snapshot.currentGameId}:`) ? (
              <LoaderCircle className="spin" />
            ) : (
              <RotateCcw />
            )}
            <span>{gameStartPending ? "Starting…" : gameFinished ? "Next round" : "Restart"}</span>
          </button>
        )}
        <Link className="control leave" to="/app">
          <PhoneOff />
          <span>Leave</span>
        </Link>
      </div>
      {callAccessOpen && mediaAccessDecision && !mediaAccessDecision.allowed && (
        <CallAccessDialog
          decision={mediaAccessDecision}
          roomCode={snapshot.code}
          onClose={() => setCallAccessOpen(false)}
          onResolved={refreshMediaAccess}
        />
      )}
      {pickerOpen && (
        <div className="modal-backdrop">
          <button
            type="button"
            className="modal-dismiss"
            aria-label="Close game picker"
            onClick={() => setPickerOpen(false)}
          />
          <div
            className="game-picker"
            role="dialog"
            aria-modal="true"
            aria-label={pickerMode === "catalog" ? "Choose a game" : "Game setup"}
          >
            <div className="game-picker-head">
              <div>
                <span className="eyebrow">
                  {pickerMode === "catalog" ? "CHOOSE A GAME" : "READY TO PLAY"}
                </span>
                <h2>
                  {pickerMode === "catalog"
                    ? "Pick a game"
                    : `Set up ${selectedGame?.name ?? "game"}`}
                </h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setPickerOpen(false)}>
                <X />
              </button>
            </div>
            <div className="game-picker-body">
              {pickerMode === "catalog" ? (
                <div className="picker-grid">
                  {roomGameCatalog.map((game) => (
                    <button
                      type="button"
                      className={
                        selectedGameId === game.id ? "picker-game selected" : "picker-game"
                      }
                      aria-pressed={selectedGameId === game.id}
                      onClick={() => {
                        void loadGameStyles(game.id);
                        setSelectedGameId(game.id);
                        setPlayWithBot(false);
                        setInviteFriendsOpen(false);
                        setInviteShareStarted(false);
                        setPickerMode("setup");
                      }}
                      disabled={gameStartPending !== null}
                      key={game.id}
                    >
                      <span className="icon-tile">
                        <Gamepad2 />
                      </span>
                      <strong>{game.name}</strong>
                      <small>{game.playerCount} players · ready to play</small>
                    </button>
                  ))}
                </div>
              ) : (
                selectedGame &&
                playableRoomGameIdSet.has(selectedGame.id) && (
                  <div className="game-setup-bar">
                    <div className="game-setup-heading">
                      <span className="icon-tile">
                        <Gamepad2 />
                      </span>
                      <div>
                        <strong>{selectedGame.name}</strong>
                        <small>Choose how this game starts.</small>
                      </div>
                    </div>
                    {selectedGameId === "ludo" ? (
                      <div className="no-time-control">
                        <Clock3 />
                        <span>
                          <strong>30 seconds per action</strong>
                          <small>
                            30 seconds per action; Topzle completes an expired roll or move.
                          </small>
                        </span>
                      </div>
                    ) : selectedGameId === "snakes-ladders" ? (
                      <div className="no-time-control">
                        <Clock3 />
                        <span>
                          <strong>20 seconds to roll</strong>
                          <small>
                            Topzle rolls and moves the goti when the turn timer expires.
                          </small>
                        </span>
                      </div>
                    ) : selectedTimeControls ? (
                      <label>
                        <Clock3 /> Time control
                        <select
                          value={selectedClockSeconds ?? "none"}
                          onChange={(event) => {
                            const clock =
                              event.target.value === "none" ? null : Number(event.target.value);
                            if (selectedGameId === "tic-tac-toe" || selectedGameId === "chess")
                              setGameClockSeconds((value) => ({
                                ...value,
                                [selectedGameId]: clock,
                              }));
                            if (selectedGameId === "draw-guess" && clock !== null)
                              setDrawGuessRoundSeconds(clock);
                          }}
                          disabled={gameStartPending !== null}
                        >
                          {selectedTimeControls.map((control) => (
                            <option
                              value={control.seconds === null ? "none" : control.seconds}
                              key={control.label}
                            >
                              {control.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <div className="no-time-control">
                        <Clock3 />
                        <span>
                          <strong>No time control</strong>
                          <small>{selectedGame.name} plays at the room's natural pace.</small>
                        </span>
                      </div>
                    )}
                    {selectedGameId === "draw-guess" && (
                      <label>
                        <RotateCcw /> Rounds
                        <select
                          value={drawGuessRounds}
                          onChange={(event) => setDrawGuessRounds(Number(event.target.value))}
                          disabled={gameStartPending !== null}
                        >
                          <option value={1}>1 round · quick match</option>
                          <option value={3}>3 rounds · classic</option>
                          <option value={5}>5 rounds · party</option>
                          <option value={8}>8 rounds · marathon</option>
                        </select>
                      </label>
                    )}
                    {selectedGame.ai && (
                      <>
                        <label className="bot-toggle">
                          <input
                            type="checkbox"
                            checked={playWithBot}
                            onChange={(event) => setPlayWithBot(event.target.checked)}
                            disabled={gameStartPending !== null}
                          />
                          <Bot />
                          <span>
                            <strong>
                              {selectedGameId === "draw-guess"
                                ? "Let local AI guess"
                                : "Play with bot"}
                            </strong>
                            <small>
                              {selectedGameId === "draw-guess"
                                ? "The classifier runs in this browser without an external AI API"
                                : "Ready immediately without waiting for another player"}
                            </small>
                          </span>
                        </label>
                        {playWithBot && (
                          <fieldset className="bot-policy-picker">
                            <legend>
                              <Bot /> Bot type
                            </legend>
                            <div className="bot-policy-options">
                              {(
                                botPolicies[selectedGameId] ?? [defaultBotPolicy(selectedGameId)]
                              ).map((policy) => {
                                const PolicyIcon = policy.icon;
                                const selected = pickerBotPolicy === policy.id;
                                return (
                                  <button
                                    type="button"
                                    className={selected ? "bot-policy selected" : "bot-policy"}
                                    aria-pressed={selected}
                                    onClick={() => {
                                      setPickerBotPolicy(policy.id);
                                    }}
                                    disabled={gameStartPending !== null}
                                    key={policy.id}
                                  >
                                    <PolicyIcon />
                                    <span>
                                      <strong>{policy.label}</strong>
                                      <small>{policy.description}</small>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </fieldset>
                        )}
                      </>
                    )}
                    <div className="invite-friends-card">
                      <button
                        type="button"
                        className={
                          inviteFriendsOpen
                            ? "invite-friends-toggle active"
                            : "invite-friends-toggle"
                        }
                        aria-expanded={inviteFriendsOpen}
                        onClick={() => setInviteFriendsOpen((open) => !open)}
                      >
                        <UserPlus />
                        <span>
                          <strong>Invite friends</strong>
                          <small>Share this room and start when they arrive</small>
                        </span>
                        {joinedFriends.length > 0 ? <b>{joinedFriends.length} here</b> : <Share2 />}
                      </button>
                      {inviteFriendsOpen && (
                        <div className="invite-share-panel">
                          <p>
                            <strong>{selectedGame.name} is ready to join</strong>
                            <span>{inviteUrl}</span>
                          </p>
                          <fieldset className="invite-share-options" aria-label="Share room invite">
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(inviteText)}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => markInviteShared("whatsapp")}
                            >
                              <MessageCircle />
                              <span>WhatsApp</span>
                            </a>
                            <a
                              href={`sms:?&body=${encodeURIComponent(inviteText)}`}
                              onClick={() => markInviteShared("sms")}
                            >
                              <Smartphone />
                              <span>SMS</span>
                            </a>
                            <a
                              href={`mailto:?bcc=${encodeURIComponent("contact@topzle.com")}&subject=${encodeURIComponent(`Join my ${selectedGame.name} game`)}&body=${encodeURIComponent(inviteText)}`}
                              onClick={() => markInviteShared("email")}
                            >
                              <Mail />
                              <span>Email</span>
                            </a>
                            <button
                              type="button"
                              onClick={() => {
                                setInviteFriendsOpen(true);
                                setInviteShareStarted(true);
                                void copyInvite();
                              }}
                              disabled={copyPending}
                            >
                              {copyPending ? (
                                <LoaderCircle className="spin" />
                              ) : inviteCopied ? (
                                <Check />
                              ) : (
                                <Copy />
                              )}
                              <span>{inviteCopied ? "Copied" : "Copy link"}</span>
                            </button>
                            <button type="button" onClick={() => void shareInvite()}>
                              <Share2 />
                              <span>More</span>
                            </button>
                          </fieldset>
                          <div
                            className={
                              joinedFriendsReady ? "invite-waiting ready" : "invite-waiting"
                            }
                            role="status"
                          >
                            {joinedFriendsReady ? (
                              <Check />
                            ) : inviteShareStarted ? (
                              <LoaderCircle className="spin" />
                            ) : (
                              <Users />
                            )}
                            <span>
                              <strong>
                                {joinedFriends.length
                                  ? `${joinedFriends.map((friend) => friend.displayName).join(", ")} joined`
                                  : inviteShareStarted
                                    ? "Waiting for friends to join…"
                                    : "Choose how you want to invite them"}
                              </strong>
                              <small>
                                {joinedFriendsReady
                                  ? "Enough players are here. You can start the game now."
                                  : `Play Now unlocks when ${remainingFriendsCopy}${selectedGame.ai ? " or you enable the bot" : ""}.`}
                              </small>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="game-start-actions">
                      <button
                        type="button"
                        className="play-now-button"
                        onClick={() =>
                          void startGame(selectedGame.id, selectedGame.ai && playWithBot)
                        }
                        disabled={
                          !(isHost || canStartNextRound) ||
                          gameStartPending !== null ||
                          !selectedGameReady
                        }
                      >
                        {gameStartPending ? <LoaderCircle className="spin" /> : <Gamepad2 />}
                        {gameStartPending ? "Starting…" : "Play now"}
                      </button>
                    </div>
                    {(isHost || canStartNextRound) && !selectedGameReady && (
                      <small className="game-start-requirement">
                        {connectionState.status !== "connected"
                          ? "Connecting to the room before play can start."
                          : `Invite ${remainingJoinedFriends === 1 ? "one more friend" : `${remainingJoinedFriends} more friends`}${selectedGame.ai ? " or enable Play with bot" : ""} to start.`}
                      </small>
                    )}
                    {!isHost && !canStartNextRound && (
                      <small>Only the room host can start or switch the game.</small>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {restartConfirmOpen && snapshot.currentGameId && !gameFinished && (
        <div className="modal-backdrop">
          <button
            type="button"
            className="modal-dismiss"
            aria-label="Cancel game restart"
            onClick={() => !gameStartPending && setRestartConfirmOpen(false)}
          />
          <div
            className="restart-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="restart-title"
          >
            <span className="restart-icon">
              <RotateCcw />
            </span>
            <span className="eyebrow">
              {abandonedGameTakeover ? "RECLAIM GAME" : "RESTART GAME"}
            </span>
            <h2 id="restart-title">
              {abandonedGameTakeover ? "Restart with Topzle AI?" : "Start this game over?"}
            </h2>
            <p>
              {abandonedGameTakeover
                ? `${inactiveHumanParticipants.length} inactive ${
                    inactiveHumanParticipants.length === 1 ? "player" : "players"
                  } will be removed. You’ll become the room host and start a fresh game against Topzle AI.`
                : "Everyone's current progress will be cleared. This cannot be undone after the new game starts."}
            </p>
            <div>
              <button
                type="button"
                className="button button-ghost"
                onClick={() => setRestartConfirmOpen(false)}
                disabled={gameStartPending !== null}
              >
                Keep playing
              </button>
              <button
                type="button"
                className="button restart-confirm"
                onClick={async () => {
                  if (
                    await startGame(
                      snapshot.currentGameId ?? "",
                      restartVersusAi,
                      currentClockSeconds,
                    )
                  )
                    setRestartConfirmOpen(false);
                }}
                disabled={gameStartPending !== null}
              >
                {gameStartPending ? <LoaderCircle className="spin" /> : <RotateCcw />}
                {gameStartPending
                  ? "Restarting…"
                  : abandonedGameTakeover
                    ? `Remove ${inactiveHumanParticipants.length} & play AI`
                    : "Restart game"}
              </button>
            </div>
          </div>
        </div>
      )}
      {roundComplete && gameFinished && (
        <div className="modal-backdrop round-complete-backdrop">
          <button
            type="button"
            className="modal-dismiss"
            aria-label="Close round result"
            onClick={() => !gameStartPending && setRoundComplete(null)}
          />
          <div
            className="round-complete-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="round-complete-title"
          >
            <span className="round-complete-icon">
              <Trophy />
            </span>
            <span className="eyebrow">ROUND COMPLETE</span>
            <h2 id="round-complete-title">
              {roundComplete.outcome === "draw"
                ? "That round was a draw."
                : roundComplete.outcome === "abandoned"
                  ? "That round has ended."
                  : "Good game. Next round?"}
            </h2>
            <p>{roundComplete.resultText}</p>
            <small>{roundComplete.roomSummary}</small>
            <div className="round-complete-actions">
              <button
                type="button"
                className="button button-ghost"
                onClick={() => setRoundComplete(null)}
                disabled={gameStartPending !== null}
              >
                Keep viewing
              </button>
              {canStartNextRound ? (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={startNextRound}
                  disabled={gameStartPending !== null}
                >
                  {gameStartPending ? <LoaderCircle className="spin" /> : <RotateCcw />}
                  {gameStartPending ? "Starting…" : "Next round"}
                </button>
              ) : (
                <span className="waiting-rematch">Waiting for a player to start.</span>
              )}
            </div>
          </div>
        </div>
      )}
      {celebration && (
        <div className="winner-celebration" role="status" aria-live="polite">
          <div className="celebration-copy">
            <Trophy />
            <strong>You won!</strong>
            <span>Great game — ready for another round?</span>
          </div>
          <div className="fireworks" aria-hidden="true">
            {celebrationParticles.map((particle) => (
              <i
                key={`${celebration}:${particle.id}`}
                style={
                  {
                    "--angle": `${(particle.position * 137.5) % 360}deg`,
                    "--distance": `${130 + (particle.position % 7) * 42}px`,
                    "--delay": `${(particle.position % 9) * 45}ms`,
                    "--color": ["#2F8CFF", "#2CCAF5", "#2ED69B", "#ffd166", "#FF647C"][
                      particle.position % 5
                    ],
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

interface ParticipantBubblePlacement {
  x: number;
  y: number;
  size: number;
}

function LocalVideoPreview({ stream }: { stream: MediaStream }) {
  const video = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!video.current) return;
    video.current.srcObject = stream;
    void video.current.play().catch(() => undefined);
    return () => {
      if (video.current) video.current.srcObject = null;
    };
  }, [stream]);
  return (
    <video
      ref={video}
      className="local-video"
      autoPlay
      muted
      playsInline
      aria-label="Your camera preview"
    />
  );
}

function RemoteVideoPreview({ name, stream }: { name: string; stream: MediaStream }) {
  const video = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!video.current) return;
    video.current.srcObject = stream;
    void video.current.play().catch(() => undefined);
    return () => {
      if (video.current) video.current.srcObject = null;
    };
  }, [stream]);
  return (
    <video
      ref={video}
      className="remote-video"
      autoPlay
      muted
      playsInline
      aria-label={`${name}'s camera`}
    />
  );
}

function RemoteAudio({
  name,
  stream,
  muted,
}: {
  name: string;
  stream: MediaStream;
  muted: boolean;
}) {
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!audio.current) return;
    const element = audio.current;
    element.srcObject = stream;
    element.muted = muted;
    const play = () => {
      void element.play().then(
        () => {
          document.removeEventListener("pointerdown", play);
          document.removeEventListener("keydown", play);
        },
        () => undefined,
      );
    };
    play();
    document.addEventListener("pointerdown", play, { passive: true });
    document.addEventListener("keydown", play);
    return () => {
      document.removeEventListener("pointerdown", play);
      document.removeEventListener("keydown", play);
      element.srcObject = null;
    };
  }, [stream, muted]);
  return <audio ref={audio} autoPlay muted={muted} aria-label={`${name}'s audio`} />;
}

const compactBubbleSize = 72;
const expandedBubbleSize = 108;
const bubbleAccessorySpace = 64;

function useSpeakingParticipantIds(
  participantId: string,
  localAudioStream: MediaStream | null,
  remoteMediaStreams: Map<string, MediaStream>,
) {
  const [speakingIds, setSpeakingIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    const streams = new Map(remoteMediaStreams);
    if (localAudioStream?.getAudioTracks().some((track) => track.readyState === "live"))
      streams.set(participantId, localAudioStream);
    for (const [id, stream] of streams)
      if (!stream.getAudioTracks().some((track) => track.readyState === "live")) streams.delete(id);
    if (streams.size === 0) {
      setSpeakingIds(new Set());
      return;
    }

    let active = true;
    let animationFrame = 0;
    let context: AudioContext | null = null;
    const sources: MediaStreamAudioSourceNode[] = [];
    const resumeContext = () => {
      if (context?.state === "suspended") void context.resume().catch(() => undefined);
    };
    document.addEventListener("pointerdown", resumeContext, { passive: true });
    document.addEventListener("keydown", resumeContext);
    try {
      context = new AudioContext();
      void context.resume().catch(() => undefined);
      const meters = [...streams.entries()].map(([id, stream]) => {
        const analyser = context?.createAnalyser();
        if (!analyser || !context) return null;
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.62;
        const source = context.createMediaStreamSource(stream);
        source.connect(analyser);
        sources.push(source);
        return {
          id,
          analyser,
          samples: new Uint8Array(analyser.fftSize),
          lastVoiceAt: 0,
        };
      });
      let priorSignature = "";
      const sample = (time: number) => {
        if (!active) return;
        const next = new Set<string>();
        for (const meter of meters) {
          if (!meter) continue;
          meter.analyser.getByteTimeDomainData(meter.samples);
          let energy = 0;
          for (const value of meter.samples) {
            const amplitude = (value - 128) / 128;
            energy += amplitude * amplitude;
          }
          const rms = Math.sqrt(energy / meter.samples.length);
          if (rms >= 0.045) meter.lastVoiceAt = time;
          if (time - meter.lastVoiceAt < 260) next.add(meter.id);
        }
        const signature = [...next].sort().join("|");
        if (signature !== priorSignature) {
          priorSignature = signature;
          setSpeakingIds(next);
        }
        animationFrame = window.requestAnimationFrame(sample);
      };
      animationFrame = window.requestAnimationFrame(sample);
    } catch {
      setSpeakingIds(new Set());
    }
    return () => {
      active = false;
      window.cancelAnimationFrame(animationFrame);
      document.removeEventListener("pointerdown", resumeContext);
      document.removeEventListener("keydown", resumeContext);
      for (const source of sources) source.disconnect();
      void context?.close();
    };
  }, [localAudioStream, participantId, remoteMediaStreams]);
  return speakingIds;
}

function ParticipantBubbles({
  roomId,
  participants,
  participantId,
  localCamera,
  localMicrophone,
  localAudioStream,
  localVideoStream,
  remoteMediaStreams,
  mediaPending,
  localMediaAllowed,
  thinkingParticipantId,
  hiddenAvatarIds,
  onToggleLocalMedia,
  onHideAvatar,
  onMediaPreferencesChange,
  inactiveRemovalPending,
  onRemoveInactive,
}: {
  roomId: string;
  participants: ActiveRoomSnapshot["participants"];
  participantId: string;
  localCamera: boolean;
  localMicrophone: boolean;
  localAudioStream: MediaStream | null;
  localVideoStream: MediaStream | null;
  remoteMediaStreams: Map<string, MediaStream>;
  mediaPending: "camera" | "microphone" | null;
  localMediaAllowed: boolean;
  thinkingParticipantId: string | null;
  hiddenAvatarIds: ReadonlySet<string>;
  onToggleLocalMedia: (kind: "camera" | "microphone") => void;
  onHideAvatar: (participantId: string) => void;
  onMediaPreferencesChange: (input: {
    activeSpeakerIds: string[];
    hiddenVideoIds: string[];
  }) => void;
  inactiveRemovalPending: string | null;
  onRemoveInactive: (participantId: string) => void;
}) {
  const layer = useRef<HTMLElement | null>(null);
  const drag = useRef<{
    participantId: string;
    pointerId: number;
    startX: number;
    startY: number;
    placement: ParticipantBubblePlacement;
    moved: boolean;
  } | null>(null);
  const [placements, setPlacements] = useState<Record<string, ParticipantBubblePlacement>>({});
  const [actionsParticipantId, setActionsParticipantId] = useState<string | null>(null);
  const [mutedParticipants, setMutedParticipants] = useState<Set<string>>(() => new Set());
  const [hiddenVideos, setHiddenVideos] = useState<Set<string>>(() => new Set());
  const speakingParticipantIds = useSpeakingParticipantIds(
    participantId,
    localAudioStream,
    remoteMediaStreams,
  );
  useEffect(() => {
    onMediaPreferencesChange({
      activeSpeakerIds: [...speakingParticipantIds].filter((id) => id !== participantId),
      hiddenVideoIds: [
        ...new Set([...hiddenVideos, ...[...hiddenAvatarIds].filter((id) => id !== participantId)]),
      ],
    });
  }, [
    hiddenAvatarIds,
    hiddenVideos,
    onMediaPreferencesChange,
    participantId,
    speakingParticipantIds,
  ]);
  const storageKey = `topzle.room-bubbles:${roomId}`;

  const fitPlacement = useCallback((next: ParticipantBubblePlacement) => {
    const width = layer.current?.clientWidth ?? window.innerWidth;
    const height = layer.current?.clientHeight ?? window.innerHeight;
    const requestedSize = Number.isFinite(next.size) ? next.size : compactBubbleSize;
    const requestedX = Number.isFinite(next.x) ? next.x : 8;
    const requestedY = Number.isFinite(next.y) ? next.y : 8;
    const size = Math.min(Math.max(requestedSize, compactBubbleSize), expandedBubbleSize);
    return {
      x: Math.min(Math.max(requestedX, 8), Math.max(8, width - size - 8)),
      y: Math.min(Math.max(requestedY, 8), Math.max(8, height - size - bubbleAccessorySpace - 8)),
      size,
    };
  }, []);

  useEffect(() => {
    const saved = window.localStorage.getItem(storageKey);
    if (!saved) return;
    try {
      setPlacements(JSON.parse(saved) as Record<string, ParticipantBubblePlacement>);
    } catch {
      window.localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  useEffect(() => {
    setPlacements((current) => {
      const next: Record<string, ParticipantBubblePlacement> = {};
      const layerWidth = layer.current?.clientWidth ?? window.innerWidth;
      const layerHeight = layer.current?.clientHeight ?? window.innerHeight;
      const verticalStep = compactBubbleSize + bubbleAccessorySpace + 8;
      const rows = Math.max(1, Math.floor((layerHeight - 16) / verticalStep));
      participants.forEach((participant, index) => {
        const existing = current[participant.id];
        const column = Math.floor(index / rows);
        const fallback = {
          x: layerWidth - compactBubbleSize - 12 - column * (compactBubbleSize + 12),
          y: 12 + (index % rows) * verticalStep,
          size: compactBubbleSize,
        };
        next[participant.id] = fitPlacement(existing ?? fallback);
      });
      return next;
    });
  }, [fitPlacement, participants]);

  useEffect(() => {
    if (!Object.keys(placements).length) return;
    window.localStorage.setItem(storageKey, JSON.stringify(placements));
  }, [placements, storageKey]);

  useEffect(() => {
    const keepInBounds = () =>
      setPlacements((current) =>
        Object.fromEntries(
          Object.entries(current).map(([id, placement]) => [id, fitPlacement(placement)]),
        ),
      );
    window.addEventListener("resize", keepInBounds);
    return () => window.removeEventListener("resize", keepInBounds);
  }, [fitPlacement]);

  const startDrag = (
    participantBubbleId: string,
    placement: ParticipantBubblePlacement,
    event: React.PointerEvent<HTMLElement>,
  ) => {
    if ((event.target as HTMLElement).closest("button")) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      participantId: participantBubbleId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      placement,
      moved: false,
    };
  };

  const moveDrag = (event: React.PointerEvent<HTMLElement>) => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault();
    const deltaX = event.clientX - active.startX;
    const deltaY = event.clientY - active.startY;
    if (Math.hypot(deltaX, deltaY) >= 6) active.moved = true;
    if (!active.moved) return;
    setPlacements((current) => ({
      ...current,
      [active.participantId]: fitPlacement({
        ...active.placement,
        x: active.placement.x + deltaX,
        y: active.placement.y + deltaY,
      }),
    }));
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (!active.moved)
      setActionsParticipantId((current) =>
        current === active.participantId ? null : active.participantId,
      );
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const cancelDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const toggleSetValue = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setter((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section ref={layer} className="participant-bubbles-layer" aria-label="Room call participants">
      {participants
        .filter((participant) => {
          if (hiddenAvatarIds.has(participant.id)) return false;
          if (participant.id === participantId) return localCamera || localMicrophone;
          return participant.camera || participant.microphone;
        })
        .map((participant, index) => {
          const local = participant.id === participantId;
          const microphone = local ? localMicrophone : participant.microphone;
          const camera = local ? localCamera : participant.camera;
          const remoteStream = remoteMediaStreams.get(participant.id);
          const videoReady = Boolean(remoteStream?.getVideoTracks().length);
          const videoHidden = hiddenVideos.has(participant.id);
          const speaking =
            microphone &&
            (speakingParticipantIds.has(participant.id) || Boolean(participant.speaking));
          const avatarIsImage = /^(?:https?:|data:|blob:|\/)/.test(participant.avatar);
          const placement = placements[participant.id] ?? {
            x: 12,
            y: 12 + index * (compactBubbleSize + bubbleAccessorySpace + 8),
            size: compactBubbleSize,
          };
          const expanded = placement.size > compactBubbleSize;
          return (
            <article
              data-participant-id={participant.id}
              className={[
                "player-bubble",
                speaking ? "speaking" : "",
                participant.connected ? "connected" : "offline",
                expanded ? "expanded" : "",
                actionsParticipantId === participant.id ? "actions-open" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                width: `${placement.size}px`,
                height: `${placement.size}px`,
                transform: `translate3d(${placement.x}px, ${placement.y}px, 0)`,
              }}
              onPointerDown={(event) => startDrag(participant.id, placement, event)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={cancelDrag}
              aria-label={`${local ? "Your" : participant.displayName} avatar. Drag to move; tap for controls.`}
              title={`Drag to move ${local ? "your" : `${participant.displayName}'s`} avatar; tap for controls`}
              key={participant.id}
            >
              <div className="player-bubble-media">
                {local && camera && localVideoStream ? (
                  <LocalVideoPreview stream={localVideoStream} />
                ) : !local && camera && remoteStream && videoReady && !videoHidden ? (
                  <RemoteVideoPreview name={participant.displayName} stream={remoteStream} />
                ) : avatarIsImage ? (
                  <img src={participant.avatar} alt="" draggable={false} />
                ) : participant.id.startsWith("ai:") ? (
                  <Bot aria-hidden="true" />
                ) : (
                  <span>{participant.displayName.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <i
                className={`connection-dot ${participant.connected ? participant.connectionQuality : "offline"}`}
                title={
                  participant.connected
                    ? `${participant.connectionQuality} connection`
                    : "Participant is offline"
                }
              />
              {thinkingParticipantId === participant.id && (
                <span
                  className="ai-thinking-dots player-bubble-thinking"
                  role="status"
                  aria-label="Topzle AI is choosing a move"
                >
                  <i />
                  <i />
                  <i />
                </span>
              )}
              <span className="player-bubble-name">
                {local ? "You" : participant.displayName.split(" ")[0]}
                {flagForCountry(participant.countryCode) && (
                  <span
                    className="player-country-flag"
                    title={participant.countryCode}
                    role="img"
                    aria-label={`Country ${participant.countryCode}`}
                  >
                    {flagForCountry(participant.countryCode)}
                  </span>
                )}
              </span>
              <span
                className={speaking ? "player-bubble-wave visible" : "player-bubble-wave"}
                aria-hidden="true"
              >
                {["wave-1", "wave-2", "wave-3", "wave-4", "wave-5"].map((bar) => (
                  <i key={bar} />
                ))}
              </span>
              <div className="player-bubble-actions">
                {local && localMediaAllowed ? (
                  <>
                    <button
                      type="button"
                      className={microphone ? "active" : ""}
                      aria-label={microphone ? "Mute your microphone" : "Unmute your microphone"}
                      title={microphone ? "Mute microphone" : "Unmute microphone"}
                      disabled={mediaPending !== null}
                      onClick={() => onToggleLocalMedia("microphone")}
                    >
                      {mediaPending === "microphone" ? (
                        <LoaderCircle className="spin" />
                      ) : microphone ? (
                        <Mic />
                      ) : (
                        <MicOff />
                      )}
                    </button>
                    <button
                      type="button"
                      className={camera ? "active" : ""}
                      aria-label={camera ? "Hide your camera" : "Show your camera"}
                      title={camera ? "Stop video" : "Start video"}
                      disabled={mediaPending !== null}
                      onClick={() => onToggleLocalMedia("camera")}
                    >
                      {mediaPending === "camera" ? (
                        <LoaderCircle className="spin" />
                      ) : camera ? (
                        <Camera />
                      ) : (
                        <CameraOff />
                      )}
                    </button>
                  </>
                ) : participant.connected ? (
                  <>
                    <button
                      type="button"
                      className={!mutedParticipants.has(participant.id) ? "active" : ""}
                      aria-label={
                        mutedParticipants.has(participant.id)
                          ? `Unmute ${participant.displayName}`
                          : `Mute ${participant.displayName}`
                      }
                      title={
                        mutedParticipants.has(participant.id)
                          ? "Listen to participant"
                          : "Mute participant for you"
                      }
                      disabled={!remoteStream?.getAudioTracks().length}
                      onClick={() => toggleSetValue(setMutedParticipants, participant.id)}
                    >
                      {mutedParticipants.has(participant.id) ? <VolumeX /> : <Volume2 />}
                    </button>
                    <button
                      type="button"
                      className={!videoHidden && camera ? "active" : ""}
                      aria-label={
                        videoHidden
                          ? `Show ${participant.displayName}'s video`
                          : `Hide ${participant.displayName}'s video`
                      }
                      title={videoHidden ? "Show participant video" : "Hide participant video"}
                      disabled={!camera || !videoReady}
                      onClick={() => toggleSetValue(setHiddenVideos, participant.id)}
                    >
                      {videoHidden ? <EyeOff /> : <Eye />}
                    </button>
                  </>
                ) : null}
                {!local && !participant.connected && !participant.id.startsWith("ai:") && (
                  <button
                    type="button"
                    className="remove-inactive"
                    aria-label={`Remove inactive player ${participant.displayName}`}
                    title="Remove inactive player and continue"
                    disabled={inactiveRemovalPending !== null}
                    onClick={() => onRemoveInactive(participant.id)}
                  >
                    {inactiveRemovalPending === participant.id ? (
                      <LoaderCircle className="spin" />
                    ) : (
                      <UserMinus />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={
                    expanded
                      ? `Make ${local ? "your" : participant.displayName}'s avatar compact`
                      : `Expand ${local ? "your" : participant.displayName}'s avatar`
                  }
                  title={expanded ? "Make compact" : "Expand avatar"}
                  onClick={() =>
                    setPlacements((current) => ({
                      ...current,
                      [participant.id]: fitPlacement({
                        ...placement,
                        size: expanded ? compactBubbleSize : expandedBubbleSize,
                      }),
                    }))
                  }
                >
                  {expanded ? <Minimize2 /> : <Maximize2 />}
                </button>
                <button
                  type="button"
                  aria-label={`Hide ${local ? "your" : `${participant.displayName}'s`} avatar`}
                  title="Hide avatar"
                  onClick={() => onHideAvatar(participant.id)}
                >
                  <EyeOff />
                </button>
              </div>
            </article>
          );
        })}
      {[...remoteMediaStreams.entries()].map(([remoteParticipantId, stream]) => {
        const participant = participants.find((item) => item.id === remoteParticipantId);
        return participant && stream.getAudioTracks().length ? (
          <RemoteAudio
            key={remoteParticipantId}
            name={participant.displayName}
            stream={stream}
            muted={mutedParticipants.has(remoteParticipantId)}
          />
        ) : null;
      })}
    </section>
  );
}

function PlayAgainAction({
  canStart,
  pending,
  onClick,
}: {
  canStart: boolean;
  pending: boolean;
  onClick: () => void;
}) {
  return canStart ? (
    <button
      type="button"
      className="button button-primary play-again"
      onClick={onClick}
      disabled={pending}
    >
      {pending ? <LoaderCircle className="spin" /> : <RotateCcw />}
      {pending ? "Starting…" : "Next round"}
    </button>
  ) : (
    <p className="waiting-rematch">Waiting for a player to start the next round.</p>
  );
}

function GameClocks({
  entries,
  lastMoveAt,
  running,
}: {
  entries: Array<{ label: string; seconds: number; active: boolean }>;
  lastMoveAt: number;
  running: boolean;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [running]);
  const elapsed = running ? Math.max(0, Math.floor((now - lastMoveAt) / 1000)) : 0;
  return (
    <div className="game-clocks">
      {entries.map((entry) => {
        const seconds = Math.max(0, entry.seconds - (entry.active ? elapsed : 0));
        return (
          <div className={entry.active && running ? "active" : ""} key={entry.label}>
            <span>{entry.label}</span>
            <strong>
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
            </strong>
          </div>
        );
      })}
    </div>
  );
}

function TicMark({ mark }: { mark: "X" | "O" }) {
  return mark === "X" ? (
    <svg className="tic-mark tic-mark-x" viewBox="0 0 100 100" aria-hidden="true">
      <path pathLength="1" d="M24 24 76 76" />
      <path pathLength="1" d="M76 24 24 76" />
    </svg>
  ) : (
    <svg className="tic-mark tic-mark-o" viewBox="0 0 100 100" aria-hidden="true">
      <circle pathLength="1" cx="50" cy="50" r="29" />
    </svg>
  );
}

const chessPieces: Record<string, string> = {
  K: "♔",
  Q: "♕",
  R: "♖",
  B: "♗",
  N: "♘",
  P: "♙",
  k: "♚",
  q: "♛",
  r: "♜",
  b: "♝",
  n: "♞",
  p: "♟",
};

const chessPieceNames: Record<string, string> = {
  K: "white king",
  Q: "white queen",
  R: "white rook",
  B: "white bishop",
  N: "white knight",
  P: "white pawn",
  k: "black king",
  q: "black queen",
  r: "black rook",
  b: "black bishop",
  n: "black knight",
  p: "black pawn",
};

function parseChessPosition(fen: string) {
  const board = fen.split(" ")[0]?.split("/") ?? [];
  return board.flatMap((rank, rankIndex) => {
    const cells: Array<{ square: string; piece: string | null }> = [];
    let file = 0;
    for (const character of rank) {
      if (/\d/.test(character)) {
        for (let empty = 0; empty < Number(character); empty += 1) {
          cells.push({ square: `${String.fromCharCode(97 + file)}${8 - rankIndex}`, piece: null });
          file += 1;
        }
      } else {
        cells.push({
          square: `${String.fromCharCode(97 + file)}${8 - rankIndex}`,
          piece: character,
        });
        file += 1;
      }
    }
    return cells;
  });
}

function ChessPiece({ piece }: { piece: string }) {
  const white = piece === piece.toUpperCase();
  return (
    <svg
      className={white ? "chess-piece white" : "chess-piece black"}
      viewBox="0 0 100 100"
      aria-hidden="true"
    >
      <text x="50" y="73" textAnchor="middle">
        {chessPieces[piece]}
      </text>
    </svg>
  );
}

function formatChessClock(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function ChessBoard({
  state,
  participantId,
  aiPlaying,
  pending,
  serverError,
  playAgain,
  onMove,
}: {
  state: ChessRoomState;
  participantId: string;
  aiPlaying: boolean;
  pending: boolean;
  serverError: string | null;
  playAgain: React.ReactNode;
  onMove: (from: string, to: string, promotion?: "q" | "r" | "b" | "n") => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [promotionMove, setPromotionMove] = useState<{ from: string; to: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  const humanColor =
    state.players.white === participantId
      ? "white"
      : state.players.black === participantId
        ? "black"
        : null;
  const activeColor = state.fen.split(" ")[1] === "b" ? "black" : "white";
  const board = useMemo(() => new Chess(state.fen), [state.fen]);
  const cells = parseChessPosition(state.fen);
  if (humanColor === "black") cells.reverse();
  const canMove =
    humanColor !== null && state.status === "playing" && activeColor === humanColor && !pending;
  const legalMoves = useMemo(
    () => (selected ? board.moves({ square: selected as Square, verbose: true }) : []),
    [board, selected],
  );
  const legalDestinations = new Map<string, boolean>(
    legalMoves.map((move) => [move.to, Boolean(move.captured) || move.flags.includes("e")]),
  );
  const lastMove = state.history.at(-1);
  const checkedKing = board.isCheck()
    ? cells.find((cell) => cell.piece === (activeColor === "white" ? "K" : "k"))?.square
    : null;
  const boardBottomColor = humanColor ?? "white";
  const boardTopColor = boardBottomColor === "white" ? "black" : "white";
  const playerLabel = (color: "white" | "black") => {
    const id = state.players[color];
    if (id === participantId) return "You";
    if (id.startsWith("ai:")) return "Topzle AI";
    return "Opponent";
  };
  const remainingClock = (color: "white" | "black") => {
    if (!state.clocks) return null;
    const elapsed =
      state.status === "playing" && activeColor === color
        ? Math.max(0, Math.floor((now - state.lastMoveAt) / 1000))
        : 0;
    return Math.max(0, state.clocks[color] - elapsed);
  };
  useEffect(() => {
    if (state.status !== "playing" || !state.clocks) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [state.clocks, state.status]);
  const selectPiece = (square: string, piece: string) => {
    const pieceColor = piece === piece.toUpperCase() ? "white" : "black";
    if (pieceColor !== humanColor) {
      setFeedback("Choose one of your own pieces, then try again.");
      return;
    }
    const moves = board.moves({ square: square as Square, verbose: true });
    if (moves.length === 0) {
      setFeedback(
        board.isCheck()
          ? "That piece cannot answer the check. Choose another piece and retry."
          : "That piece has no legal moves. Choose another piece and retry.",
      );
      return;
    }
    setSelected(square);
    setFeedback(null);
  };
  const handleSquare = (square: string, piece: string | null) => {
    if (!canMove) return;
    if (!selected) {
      if (!piece) {
        setFeedback("Select one of your pieces first.");
        return;
      }
      selectPiece(square, piece);
      return;
    }
    if (square === selected) {
      setSelected(null);
      setFeedback(null);
      return;
    }
    if (piece && (piece === piece.toUpperCase() ? "white" : "black") === humanColor) {
      selectPiece(square, piece);
      return;
    }
    const matchingMoves = legalMoves.filter((move) => move.to === square);
    if (matchingMoves.length === 0) {
      setFeedback(
        board.isCheck()
          ? "That move leaves your king in check. Choose a highlighted square and retry."
          : "That move is not legal. Choose a highlighted square and retry.",
      );
      return;
    }
    if (matchingMoves.some((move) => Boolean(move.promotion))) {
      setPromotionMove({ from: selected, to: square });
      setFeedback("Choose the piece for your pawn promotion.");
      return;
    }
    onMove(selected, square);
    setSelected(null);
    setFeedback(null);
  };
  const renderPlayerBar = (color: "white" | "black") => {
    const clock = remainingClock(color);
    const playerId = state.players[color];
    const aiPlayer = playerId.startsWith("ai:");
    const aiThinking = aiPlayer && state.status === "playing" && activeColor === color;
    return (
      <div
        className={
          activeColor === color && state.status === "playing"
            ? "chess-player active"
            : "chess-player"
        }
      >
        <span className={`chess-avatar ${color}`}>
          {color === "white" ? "♙" : "♟"}
          {aiThinking && (
            <span
              className="ai-thinking-dots chess-avatar-thinking"
              role="status"
              aria-label="Topzle AI is choosing a move"
            >
              <i />
              <i />
              <i />
            </span>
          )}
        </span>
        <div>
          <strong>{playerLabel(color)}</strong>
          <small>{color} pieces</small>
        </div>
        {clock !== null && <time>{formatChessClock(clock)}</time>}
      </div>
    );
  };
  return (
    <div className="active-game chess-game">
      <div className="game-status">
        <span>{aiPlaying ? "CHESS · TOPZLE AI" : "CHESS MATCH"}</span>
        <strong>
          {state.status === "playing"
            ? canMove
              ? "Your move"
              : aiPlaying
                ? "Topzle AI's move"
                : `${activeColor}'s move`
            : state.reason === "timeout"
              ? `${state.winner} wins on time`
              : `${state.winner ?? "Nobody"} · ${state.reason ?? state.status}`}
        </strong>
      </div>
      <div className="chess-stage">
        {renderPlayerBar(boardTopColor)}
        <section className="chess-board" aria-label="Chess board">
          {cells.map((cell) => {
            const file = cell.square.charCodeAt(0) - 97;
            const rank = Number(cell.square[1]);
            const light = (file + rank) % 2 === 0;
            const legalCapture = legalDestinations.get(cell.square);
            const classes = [
              light ? "light-square" : "dark-square",
              selected === cell.square ? "selected" : "",
              legalDestinations.has(cell.square)
                ? legalCapture
                  ? "legal-capture"
                  : "legal-move"
                : "",
              lastMove && (lastMove.from === cell.square || lastMove.to === cell.square)
                ? "last-move"
                : "",
              checkedKing === cell.square ? "king-in-check" : "",
              checkedKing === cell.square && state.status !== "playing" ? "checkmate-king" : "",
            ]
              .filter(Boolean)
              .join(" ");
            const showFile = rank === (humanColor === "black" ? 8 : 1);
            const showRank = cell.square[0] === (humanColor === "black" ? "h" : "a");
            return (
              <button
                type="button"
                className={classes}
                onClick={() => handleSquare(cell.square, cell.piece)}
                disabled={!canMove}
                aria-label={`${cell.square}${cell.piece ? ` ${chessPieceNames[cell.piece]}` : " empty"}`}
                key={cell.square}
              >
                {cell.piece ? <ChessPiece piece={cell.piece} /> : null}
                {legalDestinations.has(cell.square) && <i aria-hidden="true" />}
                {showRank && <small className="rank-label">{rank}</small>}
                {showFile && <small className="file-label">{cell.square[0]}</small>}
              </button>
            );
          })}
          {promotionMove && (
            <div className="promotion-picker" role="dialog" aria-label="Choose promotion piece">
              <strong>Promote pawn to</strong>
              <div>
                {(["q", "r", "b", "n"] as const).map((piece) => {
                  const character = humanColor === "white" ? piece.toUpperCase() : piece;
                  return (
                    <button
                      type="button"
                      key={piece}
                      onClick={() => {
                        onMove(promotionMove.from, promotionMove.to, piece);
                        setPromotionMove(null);
                        setSelected(null);
                        setFeedback(null);
                      }}
                      aria-label={`Promote to ${chessPieceNames[character]}`}
                    >
                      <ChessPiece piece={character} />
                    </button>
                  );
                })}
              </div>
              <button type="button" className="text-link" onClick={() => setPromotionMove(null)}>
                Cancel
              </button>
            </div>
          )}
        </section>
        {renderPlayerBar(boardBottomColor)}
      </div>
      <div
        className={
          serverError || feedback || board.isCheck() ? "chess-feedback visible" : "chess-feedback"
        }
        role="status"
      >
        {serverError ??
          feedback ??
          (board.isCheck()
            ? humanColor === activeColor
              ? "Your king is in check — choose a legal response."
              : `${activeColor} is in check.`
            : lastMove
              ? `Last move: ${lastMove.san}`
              : "Select a piece to see every legal move.")}
      </div>
      <div className="chess-actions">{playAgain}</div>
    </div>
  );
}

function LudoDie({
  value,
  rolling,
  color,
  rollNonce,
  throwForce,
}: {
  value: number | null;
  rolling: boolean;
  color: LudoColor;
  rollNonce: number;
  throwForce: number;
}) {
  const faceValue = value ?? 1;
  const tumbleSeed = (rollNonce * 37 + faceValue * 19) % 7;
  const dropDirection = tumbleSeed % 2 === 0 ? 1 : -1;
  const driftX = dropDirection * (18 + tumbleSeed * 6);
  const restingOrientation: Record<number, { x: number; y: number; z: number }> = {
    1: { x: -8, y: 10, z: 0 },
    2: { x: -98, y: 7, z: 0 },
    3: { x: -7, y: -80, z: 0 },
    4: { x: -7, y: 100, z: 0 },
    5: { x: 82, y: -7, z: 0 },
    6: { x: 8, y: 190, z: 0 },
  };
  const resting = restingOrientation[faceValue] ?? { x: -8, y: 10, z: 0 };
  const forceTurns = Math.round(clampLudoPhysics(throwForce, 0, 1) * 2);
  const rollX = resting.x + (2 + (tumbleSeed % 2) + forceTurns) * 360;
  const rollY = resting.y + (2 + ((tumbleSeed + 1) % 2) + forceTurns) * 360;
  const rollZ =
    resting.z + dropDirection * (1 + (tumbleSeed % 2) + Math.ceil(forceTurns / 2)) * 360;
  const dieStyle = {
    "--ludo-player-color": ludoColorHex[color],
    "--dice-impact-x": `${driftX * -0.08}%`,
    "--dice-bounce-x": `${driftX * -0.18}%`,
    "--dice-second-x": `${driftX * 0.07}%`,
    "--dice-return-x": `${driftX * -0.025}%`,
    "--dice-roll-x-flight": `${rollX * 0.24}deg`,
    "--dice-roll-y-flight": `${rollY * 0.22}deg`,
    "--dice-roll-z-flight": `${rollZ * 0.28}deg`,
    "--dice-roll-x-impact": `${rollX * 0.63}deg`,
    "--dice-roll-y-impact": `${rollY * 0.61}deg`,
    "--dice-roll-z-impact": `${rollZ * 0.66}deg`,
    "--dice-roll-x-bounce": `${rollX * 0.78}deg`,
    "--dice-roll-y-bounce": `${rollY * 0.76}deg`,
    "--dice-roll-z-bounce": `${rollZ * 0.8}deg`,
    "--dice-roll-x-late": `${rollX * 0.94}deg`,
    "--dice-roll-y-late": `${rollY * 0.93}deg`,
    "--dice-roll-z-late": `${rollZ * 0.95}deg`,
    "--dice-roll-x": `${rollX}deg`,
    "--dice-roll-y": `${rollY}deg`,
    "--dice-roll-z": `${rollZ}deg`,
  } as React.CSSProperties;
  const faces = [
    { value: 1, side: "front" },
    { value: 6, side: "back" },
    { value: 3, side: "right" },
    { value: 4, side: "left" },
    { value: 2, side: "top" },
    { value: 5, side: "bottom" },
  ];
  return (
    <>
      <span
        className={rolling ? "ludo-die-shell rolling" : "ludo-die-shell"}
        style={dieStyle}
        aria-hidden="true"
      >
        <span className={`ludo-die-model value-${faceValue} ${rolling ? "rolling" : ""}`}>
          {faces.map((face) => (
            <span className={`ludo-die-face ${face.side}`} key={face.side}>
              {ludoDicePips[face.value]?.map((pip) => (
                <i
                  style={{ left: `${pip.left}%`, top: `${pip.top}%` }}
                  key={`${face.value}:${pip.left}:${pip.top}`}
                />
              ))}
            </span>
          ))}
        </span>
      </span>
      <span className="ludo-dice-shadow" style={dieStyle} aria-hidden="true" />
    </>
  );
}

function LudoBoard({
  state,
  participantId,
  participants,
  aiPlaying,
  aiDifficulty,
  isHost,
  settingsPending,
  serverError,
  playAgain,
  onAction,
}: {
  state: LudoRoomState;
  participantId: string;
  participants: ActiveRoomSnapshot["participants"];
  aiPlaying: boolean;
  aiDifficulty: "easy" | "medium" | "hard";
  isHost: boolean;
  settingsPending: boolean;
  serverError: string | null;
  playAgain: React.ReactNode;
  onAction: (action: LudoEvent) => boolean;
}) {
  const latestRollEntry = state.history.at(-1);
  const latestMoveAnimationKey =
    latestRollEntry?.tokenId !== null && latestRollEntry?.tokenId !== undefined
      ? `${state.history.length}:${latestRollEntry.playerId}:${latestRollEntry.tokenId}:${latestRollEntry.at}`
      : null;
  const [diceRolling, setDiceRolling] = useState(false);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [minimumRollComplete, setMinimumRollComplete] = useState(true);
  const [diceDrag, setDiceDrag] = useState({ x: 0, y: 0, active: false });
  const [throwPhysics, setThrowPhysics] = useState<LudoThrowPhysics>(defaultLudoThrowPhysics);
  const [moveFeedback, setMoveFeedback] = useState<{
    message: string;
    rollNonce: number;
  } | null>(null);
  const [submittedMoveKey, setSubmittedMoveKey] = useState<string | null>(null);
  const [animatedStepMove, setAnimatedStepMove] = useState<{
    key: string;
    playerId: string;
    tokenId: number;
    color: LudoColor;
    progress: number;
  } | null>(null);
  const rollStartNonce = useRef(state.rollNonce);
  const observedRollNonce = useRef(state.rollNonce);
  const rollTimer = useRef<number | null>(null);
  const stepMoveTimer = useRef<number | null>(null);
  const observedMoveAnimationKey = useRef(latestMoveAnimationKey);
  const rollOwnerId = useRef(participantId);
  const boardRef = useRef<HTMLElement | null>(null);
  const diceButtonRef = useRef<HTMLButtonElement | null>(null);
  const activeRollDuration = useRef(
    state.fastMode ? Math.round(ludoDiceRollDurationMs * 0.45) : ludoDiceRollDurationMs,
  );
  const suppressDiceClick = useRef(false);
  const diceDragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    lastAt: number;
    velocityX: number;
    velocityY: number;
    maxDrag: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null>(null);
  const current = state.players[state.turnIndex];
  const humanTurn = current?.id === participantId;
  const currentLegalTokens =
    current && state.phase === "move" && state.dice !== null
      ? current.tokens.filter((token) => analyzeLudoMove(state, current, token).legal)
      : [];
  const soleEligibleTokenId =
    currentLegalTokens.length === 1 ? (currentLegalTokens[0]?.id ?? null) : null;
  const captureMoveAvailable =
    humanTurn &&
    state.phase === "move" &&
    current?.tokens.some((token) => analyzeLudoMove(state, current, token).captureCount > 0);
  const latestRoll = state.dice ?? latestRollEntry?.dice ?? null;
  const latestMove = latestRollEntry;
  const interactionStateKey = [
    state.rollNonce,
    state.history.length,
    state.turnIndex,
    state.phase,
    state.dice ?? "none",
  ].join(":");
  const movePending = submittedMoveKey === interactionStateKey && !serverError;
  const rollResultOwner =
    state.phase === "roll_result" && latestRollEntry
      ? state.players.find((player) => player.id === latestRollEntry.playerId)
      : undefined;
  const visualRoller = diceRolling
    ? (state.players.find((player) => player.id === rollOwnerId.current) ?? current)
    : (rollResultOwner ?? current);
  let botSelectedTokenId: number | null = null;
  if (current?.id.startsWith("ai:") && state.phase === "move" && state.dice !== null) {
    try {
      const action = chooseLudoBotAction(state, aiDifficulty);
      if (action.type === "move") botSelectedTokenId = action.tokenId;
    } catch {
      botSelectedTokenId = null;
    }
  }

  useEffect(
    () => () => {
      if (rollTimer.current !== null) window.clearTimeout(rollTimer.current);
      if (stepMoveTimer.current !== null) window.clearTimeout(stepMoveTimer.current);
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: the immutable history key is the animation boundary.
  useEffect(() => {
    if (!latestMoveAnimationKey || observedMoveAnimationKey.current === latestMoveAnimationKey)
      return;
    observedMoveAnimationKey.current = latestMoveAnimationKey;
    const entry = state.history.at(-1);
    if (!entry || entry.tokenId === null) return;
    const player = state.players.find((candidate) => candidate.id === entry.playerId);
    const token = player?.tokens.find((candidate) => candidate.id === entry.tokenId);
    if (!player || !token) return;
    const startProgress = token.progress === 0 ? -1 : Math.max(-1, token.progress - entry.dice);
    const steps =
      startProgress === -1
        ? [0]
        : Array.from(
            { length: Math.max(0, token.progress - startProgress) },
            (_, index) => startProgress + index + 1,
          );
    if (steps.length === 0) return;
    if (stepMoveTimer.current !== null) window.clearTimeout(stepMoveTimer.current);
    setAnimatedStepMove({
      key: latestMoveAnimationKey,
      playerId: player.id,
      tokenId: token.id,
      color: player.color,
      progress: startProgress,
    });
    let stepIndex = 0;
    const stepDuration = state.fastMode ? 95 : 185;
    const advance = () => {
      const progress = steps[stepIndex];
      if (progress === undefined) {
        stepMoveTimer.current = window.setTimeout(
          () => {
            setAnimatedStepMove(null);
            stepMoveTimer.current = null;
          },
          state.fastMode ? 120 : 240,
        );
        return;
      }
      setAnimatedStepMove((value) => (value ? { ...value, progress } : value));
      stepIndex += 1;
      stepMoveTimer.current = window.setTimeout(advance, stepDuration);
    };
    stepMoveTimer.current = window.setTimeout(advance, state.fastMode ? 80 : 140);
  }, [latestMoveAnimationKey, state.fastMode]);

  useEffect(() => {
    if (state.phase === "finished" || state.actionDeadlineAt === null) return;
    setTimerNow(Date.now());
    const timer = window.setInterval(() => setTimerNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [state.actionDeadlineAt, state.phase]);

  useEffect(() => {
    if (
      diceRolling &&
      minimumRollComplete &&
      (state.rollNonce !== rollStartNonce.current || Boolean(serverError))
    ) {
      setDiceRolling(false);
    }
  }, [diceRolling, minimumRollComplete, serverError, state.rollNonce]);

  useEffect(() => {
    if (state.rollNonce === observedRollNonce.current) return;
    const previousNonce = observedRollNonce.current;
    observedRollNonce.current = state.rollNonce;
    const nextRollOwnerId = state.history.at(-1)?.playerId ?? current?.id ?? participantId;
    const sameRollAlreadyAnimating = diceRolling && rollOwnerId.current === nextRollOwnerId;
    rollOwnerId.current = nextRollOwnerId;
    if (sameRollAlreadyAnimating) return;
    const botThrowProfile = nextRollOwnerId.startsWith("ai:")
      ? getLudoBotThrowProfile(state, nextRollOwnerId, aiDifficulty)
      : null;
    const sharedThrow =
      state.lastThrow?.rollNonce === state.rollNonce ? state.lastThrow : undefined;
    const botForce =
      sharedThrow?.force ?? botThrowProfile?.force ?? 0.48 + ((state.rollNonce * 17) % 35) / 100;
    const momentumX =
      sharedThrow?.momentumX ?? botThrowProfile?.momentumX ?? ((state.rollNonce % 3) - 1) * 18;
    const momentumY =
      sharedThrow?.momentumY ??
      botThrowProfile?.momentumY ??
      (((state.rollNonce + 1) % 3) - 1) * 14;
    const naturalDuration = Math.round(1_950 - botForce * 700);
    const botPhysics = {
      ...defaultLudoThrowPhysics,
      durationMs: state.fastMode
        ? Math.max(520, Math.round(naturalDuration * 0.45))
        : naturalDuration,
      force: botForce,
      momentumX,
      momentumY,
      lateMomentumX: momentumX * 0.24,
      lateMomentumY: momentumY * 0.24,
      reboundX: momentumX * -0.2,
      reboundY: momentumY * -0.2,
      pathTilt:
        sharedThrow?.pathTilt ?? botThrowProfile?.pathTilt ?? ((state.rollNonce % 5) - 2) * 3,
    };
    const boardRect = boardRef.current?.getBoundingClientRect();
    const dieRect = diceButtonRef.current?.getBoundingClientRect();
    const rollOwnerColor =
      state.players.find((player) => player.id === nextRollOwnerId)?.color ?? "red";
    const homeOrigin = dieRect
      ? ludoDiceHomeOrigin(rollOwnerColor, dieRect.width, dieRect.height)
      : { x: 0, y: 0 };
    const boundedBotPhysics =
      boardRect && dieRect
        ? constrainLudoThrowToBoard(
            botPhysics,
            boardRect.width,
            boardRect.height,
            dieRect.width,
            dieRect.height,
            homeOrigin.x,
            homeOrigin.y,
          )
        : botPhysics;
    setThrowPhysics(boundedBotPhysics);
    activeRollDuration.current = boundedBotPhysics.durationMs;
    rollStartNonce.current = previousNonce;
    setMinimumRollComplete(false);
    setDiceRolling(true);
    if (rollTimer.current !== null) window.clearTimeout(rollTimer.current);
    rollTimer.current = window.setTimeout(() => {
      setMinimumRollComplete(true);
      rollTimer.current = null;
    }, activeRollDuration.current);
  }, [
    aiDifficulty,
    current?.id,
    diceRolling,
    participantId,
    state,
    state.history,
    state.rollNonce,
  ]);

  useEffect(() => {
    if (state.phase !== "roll" || !humanTurn) {
      diceDragState.current = null;
      setDiceDrag((value) =>
        value.active || value.x || value.y ? { x: 0, y: 0, active: false } : value,
      );
    }
  }, [humanTurn, state.phase]);

  const rollDice = (physics: LudoThrowPhysics = defaultLudoThrowPhysics) => {
    if (diceRolling) return;
    const boardRect = boardRef.current?.getBoundingClientRect();
    const dieRect = diceButtonRef.current?.getBoundingClientRect();
    const homeOrigin =
      dieRect && visualRoller
        ? ludoDiceHomeOrigin(visualRoller.color, dieRect.width, dieRect.height)
        : { x: 0, y: 0 };
    const visualPhysics = state.fastMode
      ? { ...physics, durationMs: Math.max(520, Math.round(physics.durationMs * 0.45)) }
      : physics;
    const boundedPhysics =
      boardRect && dieRect
        ? constrainLudoThrowToBoard(
            visualPhysics,
            boardRect.width,
            boardRect.height,
            dieRect.width,
            dieRect.height,
            homeOrigin.x,
            homeOrigin.y,
          )
        : visualPhysics;
    rollOwnerId.current = participantId;
    rollStartNonce.current = state.rollNonce;
    activeRollDuration.current = boundedPhysics.durationMs;
    setThrowPhysics(boundedPhysics);
    setDiceDrag({ x: 0, y: 0, active: false });
    setMinimumRollComplete(false);
    setDiceRolling(true);
    if (
      !onAction({
        type: "roll",
        throwStyle: {
          force: boundedPhysics.force,
          momentumX: boundedPhysics.momentumX,
          momentumY: boundedPhysics.momentumY,
          pathTilt: boundedPhysics.pathTilt,
        },
      })
    ) {
      setDiceRolling(false);
      setMinimumRollComplete(true);
      return;
    }
    if (rollTimer.current !== null) window.clearTimeout(rollTimer.current);
    rollTimer.current = window.setTimeout(() => {
      setMinimumRollComplete(true);
      rollTimer.current = null;
    }, boundedPhysics.durationMs);
  };

  const beginDiceDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (diceRolling || !humanTurn || state.phase !== "roll") return;
    event.preventDefault();
    const boardRect = boardRef.current?.getBoundingClientRect();
    const dieRect = event.currentTarget.getBoundingClientRect();
    const boardWidth = boardRect?.width ?? 420;
    const edgeInset = Math.max(4, boardWidth * 0.012);
    const tumbleEnvelopeX = dieRect.width * 0.22;
    const tumbleEnvelopeY = dieRect.height * 0.22;
    const minX = boardRect
      ? boardRect.left + edgeInset + tumbleEnvelopeX - dieRect.left
      : -boardWidth * 0.4;
    const maxX = boardRect
      ? boardRect.right - edgeInset - tumbleEnvelopeX - dieRect.right
      : boardWidth * 0.4;
    const minY = boardRect
      ? boardRect.top + edgeInset + tumbleEnvelopeY - dieRect.top
      : -boardWidth * 0.4;
    const maxY = boardRect
      ? boardRect.bottom - edgeInset - tumbleEnvelopeY - dieRect.bottom
      : boardWidth * 0.4;
    const maxDrag = Math.max(90, Math.abs(minX), Math.abs(maxX), Math.abs(minY), Math.abs(maxY));
    event.currentTarget.setPointerCapture(event.pointerId);
    diceDragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      lastAt: event.timeStamp,
      velocityX: 0,
      velocityY: 0,
      maxDrag,
      minX,
      maxX,
      minY,
      maxY,
    };
    setDiceDrag({ x: 0, y: 0, active: true });
  };

  const completeDiceDrag = (
    event: React.PointerEvent<HTMLButtonElement>,
    edgeRelease?: { x: number; y: number; velocityX: number; velocityY: number },
  ) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    diceDragState.current = null;
    const x = edgeRelease?.x ?? clampLudoPhysics(event.clientX - drag.startX, drag.minX, drag.maxX);
    const y = edgeRelease?.y ?? clampLudoPhysics(event.clientY - drag.startY, drag.minY, drag.maxY);
    const distance = Math.hypot(x, y);
    suppressDiceClick.current = true;
    window.setTimeout(() => {
      suppressDiceClick.current = false;
    }, 400);
    if (distance < 7) {
      rollDice(defaultLudoThrowPhysics);
      return;
    }
    const releaseElapsed = Math.max(8, event.timeStamp - drag.lastAt);
    const releaseVelocityX = (event.clientX - drag.lastX) / releaseElapsed;
    const releaseVelocityY = (event.clientY - drag.lastY) / releaseElapsed;
    const velocityX = edgeRelease?.velocityX ?? drag.velocityX * 0.55 + releaseVelocityX * 0.45;
    const velocityY = edgeRelease?.velocityY ?? drag.velocityY * 0.55 + releaseVelocityY * 0.45;
    rollDice(
      calculateLudoThrowPhysics({
        distanceX: x,
        distanceY: y,
        maxDrag: drag.maxDrag,
        velocityX,
        velocityY,
      }),
    );
  };

  const moveDiceDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const elapsed = Math.max(8, event.timeStamp - drag.lastAt);
    const instantVelocityX = (event.clientX - drag.lastX) / elapsed;
    const instantVelocityY = (event.clientY - drag.lastY) / elapsed;
    const velocityX = drag.velocityX * 0.35 + instantVelocityX * 0.65;
    const velocityY = drag.velocityY * 0.35 + instantVelocityY * 0.65;
    const distanceX = event.clientX - drag.startX;
    const distanceY = event.clientY - drag.startY;
    const x = clampLudoPhysics(distanceX, drag.minX, drag.maxX);
    const y = clampLudoPhysics(distanceY, drag.minY, drag.maxY);
    setDiceDrag({ x, y, active: true });
    if (hasLudoDiceTouchedEdge(distanceX, distanceY, drag)) {
      completeDiceDrag(event, { x, y, velocityX, velocityY });
      return;
    }
    drag.velocityX = velocityX;
    drag.velocityY = velocityY;
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    drag.lastAt = event.timeStamp;
  };

  const finishDiceDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    completeDiceDrag(event);
  };

  const cancelDiceDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = diceDragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const x = clampLudoPhysics(drag.lastX - drag.startX, drag.minX, drag.maxX);
    const y = clampLudoPhysics(drag.lastY - drag.startY, drag.minY, drag.maxY);
    if (Math.hypot(x, y) >= 7) {
      completeDiceDrag(event, {
        x,
        y,
        velocityX: drag.velocityX,
        velocityY: drag.velocityY,
      });
      return;
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    diceDragState.current = null;
    setDiceDrag({ x: 0, y: 0, active: false });
  };

  const displayName = (playerId: string) => {
    if (playerId === participantId) return "You";
    if (playerId.startsWith("ai:")) return "Topzle AI";
    return participants.find((participant) => participant.id === playerId)?.displayName ?? "Player";
  };
  const cells = Array.from({ length: 225 }, (_, index) => {
    const row = Math.floor(index / 15);
    const column = index % 15;
    const coordinateKey = `${row}:${column}`;
    const pathIndex = ludoPathIndexByCoordinate.get(coordinateKey);
    const homeColor = ludoHomeColorByCoordinate.get(coordinateKey);
    const yardColor = ludoYardColor(row, column);
    const yardInterior = Boolean(yardColor && isLudoYardInterior(row, column));
    const startColor = pathIndex === undefined ? undefined : ludoStartColorBySquare.get(pathIndex);
    const center =
      row >= 6 && row <= 8 && column >= 6 && column <= 8 && pathIndex === undefined && !homeColor;
    const classes = [
      "ludo-cell",
      pathIndex !== undefined ? "track" : "",
      yardColor ? `yard ${yardColor}` : "",
      yardInterior ? "yard-interior" : "",
      ludoYardSlots.has(coordinateKey) ? "yard-slot" : "",
      homeColor ? `home-lane ${homeColor}` : "",
      startColor ? `safe start ${startColor}` : "",
      pathIndex !== undefined && ludoSafeSquares.has(pathIndex) ? "safe" : "",
      center ? "home-center" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <span
        className={classes}
        style={{ gridRow: row + 1, gridColumn: column + 1 }}
        aria-hidden="true"
        key={coordinateKey}
      >
        {pathIndex !== undefined &&
          ludoSafeSquares.has(pathIndex) &&
          !ludoStartColorBySquare.has(pathIndex) && <b className="ludo-safe-star">★</b>}
        {startColor && <b className={`ludo-start-arrow ${startColor}`}>➤</b>}
      </span>
    );
  });

  const tokenGroups = new Map<
    string,
    Array<{
      player: LudoRoomState["players"][number];
      token: LudoRoomState["players"][number]["tokens"][number];
      row: number;
      column: number;
    }>
  >();
  for (const player of state.players) {
    for (const token of player.tokens) {
      const coordinate =
        token.progress === -1
          ? ludoYardTokenCoordinates[player.color]?.[token.id]
          : ludoTokenCoordinate(player.color, token.progress);
      if (!coordinate) continue;
      const key = `${coordinate.row}:${coordinate.column}`;
      const group = tokenGroups.get(key) ?? [];
      group.push({ player, token, ...coordinate });
      tokenGroups.set(key, group);
    }
  }
  const latestCapturePlayer =
    (latestMove?.captureCount ?? 0) > 0
      ? state.players.find((player) => player.id === latestMove?.playerId)
      : undefined;
  const latestCaptureToken = latestCapturePlayer?.tokens.find(
    (token) => token.id === latestMove?.tokenId,
  );
  const latestCaptureCoordinate =
    latestCapturePlayer && latestCaptureToken
      ? ludoTokenCoordinate(latestCapturePlayer.color, latestCaptureToken.progress)
      : null;
  const animatedStepCoordinate = animatedStepMove
    ? animatedStepMove.progress === -1
      ? ludoYardTokenCoordinates[animatedStepMove.color]?.[animatedStepMove.tokenId]
      : ludoTokenCoordinate(animatedStepMove.color, animatedStepMove.progress)
    : null;

  const animatedRollOwnerId = rollOwnerId.current;
  const animatedRollOwnerName = displayName(animatedRollOwnerId);
  const animatedRollHasResult =
    diceRolling &&
    latestRollEntry !== undefined &&
    latestRollEntry.playerId === animatedRollOwnerId &&
    state.rollNonce !== rollStartNonce.current;
  const animatedRollHasMove =
    current?.id === animatedRollOwnerId && state.phase === "move" && state.dice !== null;
  const animatedRollAutomatic = Boolean(latestRollEntry?.automatic);
  const automaticRollOwner = animatedRollOwnerId === participantId ? "you" : animatedRollOwnerName;
  const turnIndicatorPlayerId = diceRolling ? animatedRollOwnerId : current?.id;
  const botThrowProfile =
    turnIndicatorPlayerId?.startsWith("ai:") && (diceRolling || state.phase === "roll")
      ? getLudoBotThrowProfile(state, turnIndicatorPlayerId, aiDifficulty)
      : null;
  const currentTurnText =
    current?.id === participantId ? "Your turn" : `${displayName(current?.id ?? "")}'s turn`;
  const diceTurnEyebrow = botThrowProfile
    ? botThrowProfile.label
    : diceRolling
      ? animatedRollAutomatic
        ? "TIME EXPIRED · AUTO ROLL"
        : "ROLLING NOW"
      : state.phase === "roll_result" && latestRollEntry
        ? `${displayName(latestRollEntry.playerId)} rolled ${latestRollEntry.dice} · no move`
        : humanTurn
          ? "DRAG OR TAP TO THROW"
          : "CURRENT TURN";
  const diceTurnText = botThrowProfile
    ? botThrowProfile.reason
    : diceRolling
      ? animatedRollAutomatic
        ? `Topzle is rolling for ${automaticRollOwner}`
        : animatedRollOwnerId === participantId
          ? "Your turn"
          : `${animatedRollOwnerName}'s turn`
      : state.phase === "roll_result"
        ? current?.id === participantId
          ? "You are next"
          : `${displayName(current?.id ?? "")} is next`
        : currentTurnText;
  const diceAtHome = !diceRolling && state.phase === "roll";
  const diceButtonStyle = {
    "--dice-drag-x": `${diceDrag.x}px`,
    "--dice-drag-y": `${diceDrag.y}px`,
    "--dice-throw-start-x": `${throwPhysics.startX}px`,
    "--dice-throw-start-y": `${throwPhysics.startY}px`,
    "--dice-momentum-x": `${throwPhysics.momentumX}px`,
    "--dice-momentum-y": `${throwPhysics.momentumY}px`,
    "--dice-late-momentum-x": `${throwPhysics.lateMomentumX}px`,
    "--dice-late-momentum-y": `${throwPhysics.lateMomentumY}px`,
    "--dice-rebound-x": `${throwPhysics.reboundX}px`,
    "--dice-rebound-y": `${throwPhysics.reboundY}px`,
    "--dice-safe-min-x": `${throwPhysics.safeMinX}px`,
    "--dice-safe-max-x": `${throwPhysics.safeMaxX}px`,
    "--dice-safe-min-y": `${throwPhysics.safeMinY}px`,
    "--dice-safe-max-y": `${throwPhysics.safeMaxY}px`,
    "--dice-wall-1-x": `${throwPhysics.wall1X}px`,
    "--dice-wall-1-y": `${throwPhysics.wall1Y}px`,
    "--dice-wall-2-x": `${throwPhysics.wall2X}px`,
    "--dice-wall-2-y": `${throwPhysics.wall2Y}px`,
    "--dice-wall-3-x": `${throwPhysics.wall3X}px`,
    "--dice-wall-3-y": `${throwPhysics.wall3Y}px`,
    "--dice-path-tilt": `${throwPhysics.pathTilt}deg`,
    "--dice-late-path-tilt": `${throwPhysics.pathTilt * 0.28}deg`,
    "--dice-rebound-tilt": `${throwPhysics.pathTilt * -0.45}deg`,
    "--dice-roll-duration": `${throwPhysics.durationMs}ms`,
    "--dice-lift-z": `${72 + throwPhysics.force * 88}px`,
    "--dice-lift-late-z": `${(72 + throwPhysics.force * 88) * 0.28}px`,
    "--dice-first-bounce": `${-(52 + throwPhysics.force * 42)}%`,
    "--dice-second-bounce": `${-(16 + throwPhysics.force * 22)}%`,
  } as React.CSSProperties;
  const animatedMoveLead = animatedStepMove
    ? latestMove?.automatic
      ? animatedStepMove.playerId === participantId
        ? "Topzle is moving your"
        : `Topzle is moving ${displayName(animatedStepMove.playerId)}'s`
      : animatedStepMove.playerId === participantId
        ? "You are moving"
        : `${displayName(animatedStepMove.playerId)} is moving`
    : "";
  const status =
    state.phase === "finished"
      ? state.winnerIds[0] === participantId
        ? "You brought every goti home!"
        : `${displayName(state.winnerIds[0] ?? "")} won the match`
      : state.winnerIds.length > 0
        ? `${displayName(state.winnerIds.at(-1) ?? "")} finished ${state.winnerIds.length === 1 ? "first" : `place ${state.winnerIds.length}`} · the race continues`
        : animatedStepMove
          ? `${animatedMoveLead} goti ${animatedStepMove.tokenId + 1} · ${animatedStepMove.progress < 0 ? "leaving the yard" : `step ${animatedStepMove.progress + 1}`}`
          : diceRolling
            ? animatedRollHasResult
              ? animatedRollAutomatic
                ? `Topzle rolled ${latestRollEntry.dice} for ${automaticRollOwner} · ${
                    animatedRollHasMove ? "checking the legal gotis" : "no goti can move"
                  }`
                : animatedRollHasMove
                  ? animatedRollOwnerId === participantId
                    ? `You rolled ${latestRollEntry.dice} · choose a glowing goti`
                    : `${animatedRollOwnerName} rolled ${latestRollEntry.dice} · choosing a goti`
                  : animatedRollOwnerId === participantId
                    ? `You rolled ${latestRollEntry.dice} · no goti can move`
                    : `${animatedRollOwnerName} rolled ${latestRollEntry.dice} · no goti can move`
              : animatedRollOwnerId === participantId
                ? "Rolling your dice…"
                : `${animatedRollOwnerName} is rolling…`
            : state.phase === "roll_result" && latestRollEntry
              ? `${displayName(latestRollEntry.playerId)} rolled ${latestRollEntry.dice} · no goti can move`
              : humanTurn
                ? state.phase === "roll"
                  ? "Your turn · drag and throw, or tap the dice"
                  : soleEligibleTokenId !== null
                    ? `You rolled ${state.dice} · your only eligible goti is moving automatically`
                    : captureMoveAvailable
                      ? `You rolled ${state.dice} · a capture is available`
                      : `You rolled ${state.dice} · choose a glowing goti`
                : soleEligibleTokenId !== null
                  ? `${displayName(current?.id ?? "")}'s only eligible goti is moving automatically`
                  : current?.id.startsWith("ai:") && state.phase === "move"
                    ? `Topzle AI rolled ${state.dice} · choosing a goti`
                    : `${displayName(current?.id ?? "")}'s turn`;
  const actionTimeLimit =
    state.phase === "move"
      ? soleEligibleTokenId !== null
        ? (state.fastMode ? LUDO_FAST_DICE_PRESENTATION_MS : LUDO_DICE_PRESENTATION_MS) / 1_000
        : state.moveTimeSeconds
      : state.phase === "roll"
        ? state.rollTimeSeconds
        : 0;
  const actionTimeRemainingMs =
    state.actionDeadlineAt === null
      ? 0
      : Math.min(actionTimeLimit * 1_000, Math.max(0, state.actionDeadlineAt - timerNow));
  const actionTimeRemaining = Math.ceil(actionTimeRemainingMs / 1_000);
  const actionTimeProgress =
    actionTimeLimit > 0 ? (actionTimeRemainingMs / (actionTimeLimit * 1_000)) * 100 : 0;
  const actionTimerUrgency =
    actionTimeRemaining <= 5 ? "ending" : actionTimeRemaining <= 10 ? "warning" : "";

  return (
    <div className="active-game ludo-game">
      <div className="ludo-game-heading">
        <div className="game-status">
          <span>{aiPlaying ? "CLASSIC INDIAN LUDO · TOPZLE AI" : "CLASSIC INDIAN LUDO"}</span>
          <strong>{status}</strong>
        </div>
        <div className="ludo-heading-controls">
          <span
            className={`ludo-dice-mode ${state.smartDiceEnabled ? "smart" : "fair"}`}
            title={
              state.smartDiceEnabled
                ? "Competitive mode is on. Every face remains possible, while disclosed tactical and catch-up weights can change the odds."
                : "Fair mode is on. Every face has an equal 1 in 6 chance."
            }
          >
            {state.smartDiceEnabled ? "Smart dice" : "Fair dice"}
          </span>
          <button
            type="button"
            className={state.fastMode ? "ludo-fast-toggle active" : "ludo-fast-toggle"}
            aria-pressed={state.fastMode}
            aria-label={`${state.fastMode ? "Disable" : "Enable"} fast Ludo animations${isHost ? "" : ". Only the host can change this setting"}`}
            disabled={!isHost || settingsPending}
            onClick={() => onAction({ type: "set_fast_mode", enabled: !state.fastMode })}
          >
            {settingsPending && isHost ? <LoaderCircle className="spin" /> : <Sparkles />}
            <span>Fast</span>
            <i aria-hidden="true" />
          </button>
        </div>
      </div>
      {actionTimeLimit > 0 && state.actionDeadlineAt !== null && (
        <div
          className={`ludo-action-timer ${current?.color ?? "red"} ${actionTimerUrgency}`}
          role="timer"
          aria-label={`${actionTimeRemaining} seconds remaining to ${
            state.phase === "roll"
              ? "throw the dice"
              : soleEligibleTokenId !== null
                ? "the automatic goti move"
                : "choose a goti"
          }`}
        >
          <i style={{ width: `${actionTimeProgress}%` }} aria-hidden="true" />
          <span>
            {diceRolling && state.phase === "move"
              ? soleEligibleTokenId !== null
                ? "Only eligible goti moves after the dice lands"
                : "Goti timer starts after the dice lands"
              : soleEligibleTokenId !== null
                ? `${actionTimeRemaining}s · moving the only eligible goti`
                : `${actionTimeRemaining}s to ${state.phase === "roll" ? "throw" : "choose a goti"}`}
          </span>
        </div>
      )}
      <section ref={boardRef} className="ludo-board" aria-label="Classic Indian Ludo board">
        {cells}
        <span className="ludo-home-center" aria-hidden="true">
          <i className="red" />
          <i className="green" />
          <i className="yellow" />
          <i className="blue" />
          <svg className="ludo-center-captures" viewBox="0 0 100 100">
            <title>Golden boundaries earned by capturing opponent gotis</title>
            {state.players.flatMap((player) =>
              ludoCaptureMarks(player.id, player.captures).map((mark) => {
                const scale = 0.92 - (mark.number / Math.max(7, player.captures + 1)) * 0.72;
                const latest =
                  latestCapturePlayer?.id === player.id && mark.number === player.captures;
                return (
                  <polygon
                    className={`${player.color}${latest ? " latest" : ""}`}
                    points={ludoCenterCapturePolygons[player.color]}
                    style={{ "--capture-scale": scale } as React.CSSProperties}
                    key={mark.id}
                  />
                );
              }),
            )}
          </svg>
          <b />
        </span>
        {state.players.map((player) => (
          <span
            className={`ludo-yard-label ${player.color} ${
              player.id === turnIndicatorPlayerId ? "active" : ""
            }`}
            key={player.id}
          >
            <b>{displayName(player.id)}</b>
            {state.winnerIds.includes(player.id) && (
              <span className="ludo-placement-trophy">
                <Trophy />
                {state.winnerIds.indexOf(player.id) === 0
                  ? "First"
                  : `#${state.winnerIds.indexOf(player.id) + 1}`}
              </span>
            )}
            <small>
              {player.finished}/4 home · {player.captures} cut
            </small>
            {state.phase !== "finished" && player.id === turnIndicatorPlayerId && (
              <span
                className={`ludo-home-turn ${player.color}`}
                key={`${state.turnIndex}:${state.rollNonce}:${state.phase}:${diceRolling}`}
                aria-hidden="true"
              >
                <small>{diceTurnEyebrow}</small>
                <b>{diceTurnText}</b>
              </span>
            )}
          </span>
        ))}
        {[...tokenGroups.entries()].map(([coordinate, tokens]) => {
          const position = tokens[0];
          if (!position) return null;
          return (
            <span
              className={`ludo-token-stack count-${Math.min(tokens.length, 4)}`}
              style={{
                gridRow: position.row + 1,
                gridColumn: position.column + 1,
              }}
              key={coordinate}
            >
              {tokens.map(({ player, token }) => {
                const selectable =
                  player.id === participantId &&
                  humanTurn &&
                  state.phase === "move" &&
                  !diceRolling &&
                  soleEligibleTokenId === null;
                const moveAnalysis = analyzeLudoMove(state, player, token);
                const legal = selectable && moveAnalysis.legal;
                const captureMove = legal && moveAnalysis.captureCount > 0;
                const blockedMessage = ludoMoveBlockMessage(moveAnalysis.reason);
                const botSelected = player.id === current?.id && token.id === botSelectedTokenId;
                const autoSelected =
                  state.phase === "move" &&
                  player.id === current?.id &&
                  token.id === soleEligibleTokenId;
                const stepAnimating =
                  animatedStepMove?.playerId === player.id && animatedStepMove.tokenId === token.id;
                const lastMoved =
                  latestMove?.tokenId != null &&
                  latestMove?.playerId === player.id &&
                  latestMove?.tokenId === token.id;
                const lastMovedCapture = lastMoved && (latestMove.captureCount ?? 0) > 0;
                return (
                  <button
                    type="button"
                    className={[
                      "ludo-token",
                      player.color,
                      legal ? "legal" : "",
                      selectable && !legal ? "blocked" : "",
                      botSelected ? "bot-selected" : "",
                      autoSelected ? "auto-selected" : "",
                      stepAnimating ? "step-source-hidden" : "",
                      lastMoved ? "last-moved" : "",
                      lastMovedCapture ? "captured" : "",
                      state.phase === "finished" && state.winnerIds.includes(player.id)
                        ? "winning-goti"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (!legal) {
                        setMoveFeedback({ message: blockedMessage, rollNonce: state.rollNonce });
                        return;
                      }
                      setMoveFeedback(null);
                      if (onAction({ type: "move", tokenId: token.id }))
                        setSubmittedMoveKey(interactionStateKey);
                    }}
                    disabled={movePending || !selectable}
                    aria-disabled={!legal}
                    title={
                      autoSelected
                        ? `Goti ${token.id + 1} will move automatically`
                        : captureMove
                          ? `Capture ${moveAnalysis.captureCount} opponent goti`
                          : legal
                            ? `Move ${state.dice} places`
                            : selectable
                              ? blockedMessage
                              : undefined
                    }
                    aria-label={`${displayName(player.id)}'s ${player.color} goti ${token.id + 1}${
                      token.progress === -1
                        ? " in the yard"
                        : token.progress >= 56
                          ? " home"
                          : ` on step ${token.progress + 1}`
                    }${
                      autoSelected
                        ? " — only eligible goti; moving automatically"
                        : captureMove
                          ? ` — captures ${moveAnalysis.captureCount} opponent goti`
                          : legal
                            ? " — legal move"
                            : selectable
                              ? ` — ${blockedMessage}`
                              : ""
                    }`}
                    key={`${player.id}:${token.id}`}
                  >
                    <i />
                  </button>
                );
              })}
            </span>
          );
        })}
        {animatedStepMove && animatedStepCoordinate && (
          <span
            className={`ludo-token ${animatedStepMove.color} step-moving`}
            style={{
              gridRow: animatedStepCoordinate.row + 1,
              gridColumn: animatedStepCoordinate.column + 1,
            }}
            aria-hidden="true"
            key={`${animatedStepMove.key}:${animatedStepMove.progress}`}
          >
            <i />
          </span>
        )}
        {latestCaptureCoordinate && !animatedStepMove && (
          <span
            className="ludo-capture-burst"
            style={{
              gridRow: latestCaptureCoordinate.row + 1,
              gridColumn: latestCaptureCoordinate.column + 1,
            }}
            aria-hidden="true"
            key={`capture:${latestMove?.playerId}:${latestMove?.tokenId}:${state.history.length}`}
          >
            <b>CUT!</b>
            {ludoCaptureParticles.map((particle) => (
              <i
                style={
                  {
                    "--particle": particle,
                    "--particle-hue": `${345 + particle * 8}deg`,
                    "--particle-angle": `${particle * 36}deg`,
                    "--particle-delay": `${particle * 18}ms`,
                  } as React.CSSProperties
                }
                key={particle}
              />
            ))}
          </span>
        )}
        <button
          ref={diceButtonRef}
          type="button"
          className={[
            "ludo-dice",
            diceRolling ? "rolling" : "",
            diceAtHome ? "at-home" : "at-center",
            diceDrag.active ? "dragging" : "",
            visualRoller?.color ?? "red",
            botThrowProfile ? `emotion-${botThrowProfile.emotion}` : "",
            `bounces-${throwPhysics.bounceCount}`,
          ]
            .filter(Boolean)
            .join(" ")}
          style={diceButtonStyle}
          disabled={diceRolling || !humanTurn || state.phase !== "roll"}
          onPointerDown={beginDiceDrag}
          onPointerMove={moveDiceDrag}
          onPointerUp={finishDiceDrag}
          onPointerCancel={cancelDiceDrag}
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
          onClick={() => {
            if (suppressDiceClick.current) {
              suppressDiceClick.current = false;
              return;
            }
            rollDice(defaultLudoThrowPhysics);
          }}
          aria-label={
            diceRolling
              ? "Rolling the dice"
              : humanTurn && state.phase === "roll"
                ? "Drag and throw the dice, or tap to roll"
                : latestRoll
                  ? `Latest roll was ${latestRoll}`
                  : "Wait for your turn"
          }
          title={humanTurn && state.phase === "roll" ? "Drag to throw · tap to roll" : undefined}
        >
          <LudoDie
            value={latestRoll}
            rolling={diceRolling}
            color={visualRoller?.color ?? "red"}
            throwForce={throwPhysics.force}
            rollNonce={
              diceRolling && state.rollNonce === rollStartNonce.current
                ? state.rollNonce + 1
                : state.rollNonce
            }
          />
        </button>
        {state.phase === "finished" && (
          <div className="ludo-result">
            <Trophy />
            <strong>{state.winnerIds[0] === participantId ? "You won!" : "Match complete"}</strong>
            {playAgain}
          </div>
        )}
      </section>
      <small className="ludo-rule-hint">
        Capture once to unlock home · five home squares are safe · exact roll reaches the centre
      </small>
      <div
        className={
          serverError || moveFeedback?.rollNonce === state.rollNonce
            ? "ludo-feedback visible"
            : "ludo-feedback"
        }
        role="status"
      >
        {serverError ??
          (moveFeedback?.rollNonce === state.rollNonce ? moveFeedback.message : null) ??
          (captureMoveAvailable
            ? "A legal move can capture an opponent on this roll."
            : soleEligibleTokenId !== null
              ? "Only one goti can use this roll. It will move automatically."
              : "Glowing gotis can use this roll.")}
      </div>
    </div>
  );
}
